import React, { useEffect, useRef } from 'react'
import {
  AdditiveBlending,
  BoxGeometry,
  BufferGeometry,
  Color,
  DirectionalLight,
  DoubleSide,
  Euler,
  Float32BufferAttribute,
  Fog,
  Group,
  HemisphereLight,
  InstancedMesh,
  Line,
  LineBasicMaterial,
  RingGeometry,
  CircleGeometry,
  ConeGeometry,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  PerspectiveCamera,
  PlaneGeometry,
  PointLight,
  Points,
  PointsMaterial,
  Quaternion,
  Scene,
  Vector3,
  WebGLRenderer,
} from 'three'
import {
  ACORN,
  SQUIRREL_BODY,
  VOXEL,
  buildAcornGeometry,
  buildHeartGeometry,
  buildMushroom,
  buildSpiderBody,
  buildSquirrelGeometries,
  buildWolfBody,
  buildPepper,
  buildFeather,
  buildSleepZ,
  buildCrocBody,
  buildScorpionBody,
  buildScorpionTail,
  SCORPION_TAIL,
  buildIguanaBody,
  buildBatBody,
  buildBatWing,
  buildBearBody,
  buildBearLeg,
  BEAR_HIP,
  buildDragonfly,
  buildNutGeometry,
  NUT_KINDS,
  stampDecor,
  meshDecor,
  DECOR,
  buildWolfLeg,
  buildFrog,
  buildSnakeHead,
  buildSnakeSegment,
  SNAKE_SEGMENT,
  WOLF_HIP,
  buildTree,
  damageTree,
  meshTree,
  TREE_HITS,
  TREE_VOXEL,
} from './squirrel-models'
import { createSound } from './squirrel-sound'

// Pixel heart for the lives display
const HEART = 'M1 0h2v1h1V0h2v1h1v2H6v1H5v1H4v1H3V5H2V4H1V3H0V1h1z'
// and a pixel acorn (cap and nut) for the +1 when one is eaten
const ACORN_CAP = 'M3 0h1v1h2v1h1v1H0V2h1V1h2z'
const ACORN_NUT = 'M1 3h5v2H5v1H4v1H3V6H2V5H1z'
const SVG_NS = 'http://www.w3.org/2000/svg'
// A pixel trophy for the best score on touch screens
const TROPHY = 'M0 0h8v1H7v1H6v1H5v1H3V3H2V2H1V1H0zM3 4h2v1H3zM2 5h4v2H2z'

// World units are CSS px in the flat view: x matches the screen, y is up from
// the nav's top border, and z runs back from that border (0) into the woods.
const SQ_W = SQUIRREL_BODY[0].length * VOXEL
const SQ_H = (SQUIRREL_BODY.length + 2) * VOXEL
const SQ_D = 6 * VOXEL
const MOUTH = SQ_W / 2 - 4 // Mouth offset from the squirrel's centre
const AC_R = (ACORN.length * VOXEL) / 2 // Acorn collision radius
const DEPTH = 700 // How far back the woods go

const GRAVITY = 900
const RUN_SPEED = 150
const PLAYER_SPEED = 170
const SPRINT_SPEED = 260 // holding Shift
const JUMP_SPEED = 290
const STEP = 5 // highest ledge it walks up without jumping
// Things in the tree list that aren't trees: no climbing, acorns or hearts
const NOT_TREES = ['boulder', 'log', 'stone']
// Blocky hills: columns TERRAIN_CELL wide rising in TERRAIN_STEP blocks. One
// block it hops up by walking; anything taller takes a jump (or a few).
const TERRAIN_CELL = 10
const TERRAIN_STEP = 10
const TERRAIN_STEPS = 10 // the tallest hills, in blocks
const MOUNTAIN_STEPS = 34 // and mountains, in the frozen peaks
const STEP_UP = TERRAIN_STEP + 1
// Cave systems run under some stretches of country, each in its own patch
// UNDER_REGION columns square; UNDER_WALL is how high their walls and roofs are
const UNDER_REGION = 120
const UNDER_CHANCE = 0.45
const UNDER_WALL = 70
const UNDER_PITCH = 0.85 // the camera looks down more steeply underground
const CAVE_TRIP = 0.8 // seconds to fade down into the caves or back up
const CAVE_LIGHTS = 6 // lights lit at once, from the sources nearest the squirrel
// What caves are like: rock colours, what lights them (torches, crystals,
// glowing mushrooms, lava or glowing pools), what lives there besides bats,
// and whether there are pools, lava or ice underfoot
const CAVE_TYPES = {
  limestone: { name: 'Limestone caves', floor: '#8f8676', wall: '#a59c8a', band: '#958c7a', void: '#0a0806', light: 'torch', glowworms: '#b8ff7a', enemies: ['spider'] },
  granite: { name: 'Crystal caverns', floor: '#6e6a6c', wall: '#85797c', band: '#766c6f', void: '#07070a', light: 'crystal', glow: '#8fdcff', enemies: ['spider', 'scorpion'] },
  ice: { name: 'Ice caves', floor: '#cfe3ee', wall: '#8fbfd8', band: '#7fb0cc', void: '#06101a', light: 'crystal', glow: '#9ff0ff', slippery: true, enemies: ['wolf'] },
  glacier: { name: 'Glacier caves', floor: '#dcebf2', wall: '#a6d0e4', band: '#8cc0da', void: '#081422', light: 'crystal', glow: '#c4f6ff', slippery: true, enemies: ['bear'] },
  grotto: { name: 'Mossy grotto', floor: '#3f5a34', wall: '#56604c', band: '#4b5544', void: '#040805', light: 'mushroom', glow: '#6dffb0', glowworms: '#9dffd0', pools: true, enemies: ['frog'] },
  amethyst: { name: 'Amethyst caverns', floor: '#3a3842', wall: '#4a4554', band: '#403c4a', void: '#07050c', light: 'crystal', glow: '#c27aff', enemies: ['snake'] },
  ruins: { name: 'Sunken ruins', floor: '#6e6a58', wall: '#7d7862', band: '#6a6652', void: '#080806', light: 'torch', glow: '#7affd0', pools: true, enemies: ['croc'] },
  sandstone: { name: 'Sandstone caves', floor: '#b07a48', wall: '#c48a52', band: '#a8683a', void: '#0c0704', light: 'torch', enemies: ['scorpion'] },
  volcanic: { name: 'Lava tubes', floor: '#2e2a2a', wall: '#3a3332', band: '#2a2524', void: '#0c0403', light: 'lava', lava: true, enemies: ['iguana', 'scorpion'] },
  seacave: { name: 'Sea caves', floor: '#4a5a62', wall: '#5a6a72', band: '#4c5a62', void: '#030a0e', light: 'pool', glow: '#2fd8ff', pools: true, enemies: ['snake'] },
}
// Each biome's two kinds of cave; which one depends on the rock in that stretch
const BIOME_CAVES = {
  meadow: ['limestone', 'granite'],
  fog: ['ice', 'granite'],
  rain: ['grotto', 'limestone'],
  storm: ['amethyst', 'granite'],
  jungle: ['ruins', 'grotto'],
  desert: ['sandstone', 'volcanic'],
  tropical: ['seacave', 'volcanic'],
  snow: ['glacier', 'ice'],
}
const MAX_ACORNS = 6
const MAX_TREE_ACORNS = 26
const HANGING_STAY = 0.2 // chance a ripe acorn never falls, so it has to be climbed for
const GROUND_NUTS = 0.9 // of a tree's acorns already lying on the ground under it
const LIVES = 5
const BEST_KEY = 'nutsHighScore'

// Nut mode: the page drops off the bottom of the screen and the camera swings
// from side-on (flat pixel art) to behind the squirrel, like a third-person game
const STAGE_SCALE = 1.1 // page scale as it drops
const STAGE_OPACITY = 0.7
const stageShift = () => window.innerHeight + 40 // px the page drops by
const FLAT_FOV = 0.4 // degrees: far away and narrow is all but orthographic
const GAME_FOV = 45
const GAME_PITCH = (20 * Math.PI) / 180
const GOLDEN_REACH = 250 // a golden nut draws in the nuts round about the squirrel, all in view
const TOP_PITCH = 1.45 // as steep as the chase camera goes: all but straight down
const FP_FOV = 75 // first person: a wider view from the squirrel's eyes
const CAMERA_KEY = 'nutsCamera'
const FPS_KEY = 'nutsFps'
const SOUND_KEY = 'nutsSound' // 'off' once the player mutes it; on otherwise
const GAME_DISTANCE = 170
const INTRO_TIME = 2 // seconds the squirrel keeps itself busy on entering
const COUNTER_SIZE = 16 // px, Silkscreen is crispest in multiples of 8
const PROMPT_TIME = 10000
const TOAST_TIME = 2000
const DAY_LENGTH = 150 // seconds for a full day and night
const DAY_START = 0.08 // a run starts in the morning, in full daylight

// Each biome has its own creatures. All of them are slower than the squirrel
// walks, except wolves, which it can only outrun by sprinting.
//   wander/chase: speeds; sight: how close before it gives chase; reach: how
//   close before it crouches (windup seconds) and pounces; leap/lift: pounce
//   speed along the ground and up; day/night: how many roam about
const ENEMY_KINDS = {
  spider: { hp: 2, wander: 45, chase: 90, sight: 140, reach: 55, windup: 0.3, cooldown: 1.4, leap: 190, lift: 200, leapTime: 0.6, day: 3, night: 8, shadow: [14, 12] },
  wolf: { hp: 3, wander: 60, chase: 210, sight: 260, reach: 50, windup: 0.2, cooldown: 1.6, leap: 260, lift: 150, leapTime: 0.45, day: 2, night: 4, shadow: [26, 12] },
  frog: { hp: 1, wander: 30, chase: 80, sight: 160, reach: 85, windup: 0.45, cooldown: 1.2, leap: 240, lift: 280, leapTime: 0.75, day: 4, night: 6, shadow: [14, 14] },
  croc: { hp: 3, wander: 25, chase: 75, sight: 150, reach: 60, windup: 0.35, cooldown: 1.6, leap: 220, lift: 70, leapTime: 0.35, day: 3, night: 5, shadow: [34, 10] },
  // Scorpions don't pounce: they cock their tails and strike with the sting
  scorpion: { sting: true, hp: 2, wander: 35, chase: 100, sight: 150, reach: 40, windup: 0.3, cooldown: 1.3, leap: 220, lift: 90, leapTime: 0.4, day: 3, night: 6, shadow: [20, 14] },
  iguana: { hp: 2, wander: 40, chase: 120, sight: 160, reach: 50, windup: 0.25, cooldown: 1.4, leap: 230, lift: 140, leapTime: 0.5, day: 3, night: 2, shadow: [28, 10] },
  snake: { hp: 2, wander: 35, chase: 110, sight: 170, reach: 45, windup: 0.35, cooldown: 1.3, leap: 280, lift: 60, leapTime: 0.3, day: 3, night: 6, shadow: [24, 8] },
  // Bats come out at night in every biome, many from caves in the hills.
  // They flit about overhead and swoop down to bite; pounce on one as it
  // swoops, or jump to reach it.
  // Polar bears roam the frozen peaks: big, tough and slow, easily outrun.
  // big scales how close their bites and the squirrel's pounces reach.
  bear: { hp: 4, wander: 22, chase: 62, sight: 210, reach: 62, windup: 0.6, cooldown: 2.2, leap: 170, lift: 150, leapTime: 0.6, day: 2, night: 3, shadow: [46, 24], big: 1.8 },
  bat: { hp: 1, wander: 70, chase: 130, sight: 210, reach: 95, windup: 0.4, cooldown: 2.2, leap: 250, lift: 0, leapTime: 0.6, day: 0, night: 3, shadow: [18, 8], fly: true },
}

// Biomes: the first run always starts in the sunny meadow; a new run after
// dying or resetting starts somewhere else. Walking far enough crosses into
// neighbouring biomes, laid out in big cells of the world.
const BIOME_KINDS = ['meadow', 'fog', 'rain', 'storm', 'jungle', 'desert', 'tropical', 'snow']
const BIOME_CELL = 12000 // rough size of a biome region: about 30 seconds' walk from the middle to the edge
const BIOMES = {
  meadow: { name: 'Meadow', enemy: 'spider', sky: '#87c5f0', skyMix: 0, light: 1, fog: 1, rain: 0, storm: 0, ground: '#5f6e2c', grass: ['#6d7f2e', '#7f8d34', '#5c6e28', '#8a8438'] },
  fog: { name: 'Foggy hollow', enemy: 'wolf', sky: '#aab3b8', skyMix: 0.85, light: 0.8, fog: 0.35, rain: 0, storm: 0, ground: '#56603e', grass: ['#68715a', '#737c62', '#5d6650', '#7a7d5e'] },
  rain: { name: 'Rainy woods', enemy: 'frog', sky: '#66778a', skyMix: 0.7, light: 0.7, fog: 0.7, rain: 1, storm: 0, ground: '#48652a', grass: ['#4f7a2a', '#5c8a30', '#46702a', '#5f8a3a'] },
  jungle: { name: 'Rainforest', enemy: 'croc', sky: '#8fc9a8', skyMix: 0.45, light: 0.9, fog: 0.65, rain: 0, storm: 0, ground: '#355e22', grass: ['#2f7a2a', '#3a8f30', '#28682a', '#4aa03a'] },
  desert: { name: 'Desert', enemy: 'scorpion', sky: '#f0d7a0', skyMix: 0.35, light: 1.05, fog: 0.9, rain: 0, storm: 0, ground: '#cfae70', grass: ['#c9a866', '#b89a5a', '#a8a050', '#d9b97a'] },
  // enemy can be a list: both kinds roam, sharing the numbers
  tropical: { name: 'Tropical shore', enemy: ['snake', 'iguana'], sky: '#7fd4f0', skyMix: 0.3, light: 1, fog: 1, rain: 0, storm: 0, ground: '#d6bf86', grass: ['#5aa03a', '#6ab04a', '#4a9030', '#7ac050'] },
  // Extreme mountains under deep snow, with blizzards now and then
  snow: { name: 'Frozen peaks', enemy: 'bear', sky: '#c4d3e0', skyMix: 0.5, light: 0.95, fog: 0.6, rain: 0, storm: 0, ground: '#e4ebf0', grass: ['#dfe8ee', '#cfdce4', '#eef3f6', '#b9c9d2'] },
  storm: { name: 'Thunderstorm', enemy: 'snake', sky: '#323949', skyMix: 0.9, light: 0.6, fog: 0.6, rain: 1.6, storm: 1, ground: '#3c4d22', grass: ['#3f5226', '#4a5e2c', '#36481f', '#50612e'] },
}

// Endless woods, grown in square chunks around the squirrel
const CHUNK = 320
const LOAD_RADIUS = 900
const DROP_RADIUS = 1150
const SPIDER_LEASH = 900 // creatures further than this wander back near the squirrel

// Climbing trees, and hearts that grow in them while lives are down
const CLIMB_SPEED = 90
const MAX_HEARTS = 2

// Pouncing, and spiders that take two bites
const POUNCE_TIME = 0.42
const POISON_TIME = 2.5 // seconds it glows green after eating a mushroom
const SUPER_CHANCE = 0.02 // of a tree's acorns being a rainbow super nut
const SUPER_TIME = 10 // seconds of psychedelic invincibility
const SUPER_WARNING = 3 // final seconds it flashes, faster and faster
const POUNCE_COOLDOWN = 0.6
const COMBO_TIME = 3.5 // seconds to eat the next nut and keep a streak going
// Streaks of 3 nuts score double, 6 or more triple
const comboMultiplier = (streak) => (streak >= 6 ? 3 : streak >= 3 ? 2 : 1)
const GLIDE_FALL = 55 // fastest it sinks while gliding with Space held
const CHARGED_TIME = 15 // seconds a lightning-struck creature glows, quicker and sparking
const SHOT_SPEED = 170 // creatures' ranged attacks, in wet weather
const SPIDER_DEATH = 1 // seconds a beaten creature takes to flip and shrink away
const STING_TIME = 0.4 // a scorpion's strike, there and back

// Pixel icons for the touch buttons: a sword to pounce, an arrow to jump
const SWORD_ICON = ['........##', '.......###', '......###.', '.....###..', '.#..###...', '..####....', '...##.....', '..####....', '.##..#....', '##........']
// A cog: eight teeth round a ring with a hole in the middle
const GEAR_ICON = ['....##....', '.##.##.##.', '.########.', '..##..##..', '###....###', '###....###', '..##..##..', '.########.', '.##.##.##.', '....##....']
// An eye: first person view
const EYE_ICON = ['..........', '..........', '...####...', '.##....##.', '#...##...#', '#..####..#', '.##.##.##.', '...####...', '..........', '..........']
const JUMP_ICON = ['....##....', '...####...', '..######..', '.########.', '###.##.###', '....##....', '....##....', '....##....', '....##....', '....##....']
// A shopping basket for the nut store, and a lightning bolt for the
// power-ups bought there
const SHOP_ICON = ['...####...', '..#....#..', '.#......#.', '##########', '#.#.##.#.#', '##########', '.#.#..#.#.', '.########.', '..######..', '..........']
const BAG_ICON = ['.....####.', '....####..', '...####...', '..####....', '.#######..', '....####..', '...####...', '..###.....', '.##.......', '#.........']

// Skins from the nut store: fur, eye and tail colours (natural colours in nut
// mode; the flat view keeps the theme), a glow per material, and what's
// worn on the head (HATS)
const SKINS = [
  { id: 'classic', name: 'Classic', price: 0, fur: '#9c5326', eye: '#140d08', tail: '#c7753a' },
  { id: 'fox', name: 'Fox', price: 150, fur: '#d9541a', eye: '#140d08', tail: '#f5ede0' },
  { id: 'panda', name: 'Panda', price: 250, fur: '#f2f0ea', eye: '#111111', tail: '#1c1c1c', hat: 'pandaEars' },
  { id: 'ninja', name: 'Ninja', price: 350, fur: '#22232a', eye: '#f2f2f2', tail: '#30323a', hat: 'headband' },
  { id: 'wizard', name: 'Wizard', price: 500, fur: '#8a8f99', eye: '#140d08', tail: '#c4c8d0', hat: 'wizard' },
  { id: 'radioactive', name: 'Radioactive', price: 650, fur: '#5dff3a', eye: '#0b2a05', tail: '#c6ff4a', glow: [0.7, 0, 0.8] },
  { id: 'ghost', name: 'Ghost', price: 800, fur: '#e8f4ff', eye: '#3a4a6a', tail: '#ffffff', glow: [0.5, 0, 0.6], ghost: true },
  { id: 'unicorn', name: 'Unicorn', price: 1000, fur: '#fff4fb', eye: '#5a2a6a', tail: '#ff7ad9', glow: [0.15, 0, 0.5], hat: 'horn' },
  { id: 'molten', name: 'Molten', price: 1200, fur: '#3a2620', eye: '#ffd23f', tail: '#ff5a14', glow: [0, 1, 0.9] },
  { id: 'king', name: 'Nut King', price: 1600, fur: '#ffd23f', eye: '#3a2400', tail: '#ffb300', glow: [0.35, 0, 0.35], hat: 'crown' },
  { id: 'disco', name: 'Disco', price: 2000, fur: '#ff5fa2', eye: '#111111', tail: '#5fd4ff', glow: [0.3, 0, 0.3], rainbow: true, hat: 'shades' },
]
// Characters to play as: the woods' creatures, in colours of their own.
// colours follow each creature's materials: [body, second, eyes, ...]
const CHARACTERS = [
  { id: 'squirrel', name: 'Squirrel', price: 0 },
  { id: 'wolf', name: 'Midnight wolf', price: 500, colours: ['#4b4f8a', '#2a2d55', '#9ff4ff'] },
  { id: 'frog', name: 'Golden frog', price: 1000, colours: ['#e8b422', '#fff1a8', '#ff3b3b', '#a07a10'] },
  { id: 'spider', name: 'Candy spider', price: 2000, colours: ['#ff6fb5', '#7dff6a'] },
  { id: 'iguana', name: 'Fire iguana', price: 3500, colours: ['#d8341c', '#ffd23f', '#1a1a1a', '#ff9a3a'] },
  { id: 'scorpion', name: 'Ice scorpion', price: 5000, colours: ['#a8e6ff', '#3a7bd5', '#ffffff'] },
  { id: 'croc', name: 'Royal croc', price: 7500, colours: ['#7a3ab8', '#4a1f78', '#7dff6a', '#fff4e0'] },
  { id: 'bear', name: 'Golden bear', price: 10000, colours: ['#ffd23f', '#c99a1a', '#3a2400'], glow: 0.25 },
]
// Boxes in the squirrel's own space (it faces +x, feet at 0, the head about
// x 2 to 16 and y 6 to 14, ears up to 18): [x0, y0, z0, x1, y1, z1, colour, lit]
const HATS = {
  wizard: [
    [2, 14, -6, 13, 15.2, 6, '#4b2a8a'],
    [4, 15.2, -4.5, 11, 19, 4.5, '#5a34a8'],
    [5, 19, -3.5, 10, 22, 3.5, '#5a34a8'],
    [5.5, 22, -2.5, 9, 25, 2.5, '#5a34a8'],
    [4.5, 25, -1.5, 7.5, 27, 1.5, '#5a34a8'],
    [3, 26.5, -1, 5, 28, 1, '#5a34a8'],
    [7.5, 17, -4.8, 9.5, 19, 4.8, '#ffd23f', true],
    [5, 22.5, -2.8, 6.5, 24, 2.8, '#ffd23f', true],
    [11, 3, -3, 16, 8, 3, '#f4f4f4'],
    [12, 0.5, -2, 15, 3, 2, '#f4f4f4'],
  ],
  headband: [
    [3, 11.5, -4.5, 14.5, 13, 4.5, '#e02020'],
    [-1, 11, -0.6, 3, 12.5, 0.6, '#e02020'],
    [-4, 9.5, -0.6, -1, 11, 0.6, '#e02020'],
  ],
  pandaEars: [
    [5.6, 13.6, -4.6, 10.4, 18.4, -1.6, '#111111'],
    [5.6, 13.6, 1.6, 10.4, 18.4, 4.6, '#111111'],
    [5.4, 9.4, -4.4, 9, 12.6, -3.9, '#111111'],
    [5.4, 9.4, 3.9, 9, 12.6, 4.4, '#111111'],
  ],
  horn: [
    [10, 13, -1.2, 13, 16, 1.2, '#ffd23f', true],
    [11, 16, -0.9, 13.6, 19, 0.9, '#ffe98a', true],
    [12, 19, -0.6, 14.2, 22, 0.6, '#fff6c8', true],
    [2, 13, -1, 6, 15, 1, '#ff7ad9'],
    [0, 11, -1, 3, 13, 1, '#9a7aff'],
  ],
  crown: [
    [3, 14, -4.5, 13, 16.5, -3.5, '#ffd23f', true],
    [3, 14, 3.5, 13, 16.5, 4.5, '#ffd23f', true],
    [3, 14, -4.5, 4, 16.5, 4.5, '#ffd23f', true],
    [12, 14, -4.5, 13, 16.5, 4.5, '#ffd23f', true],
    [3, 16.5, -4.5, 4.5, 19, -3, '#ffd23f', true],
    [11.5, 16.5, -4.5, 13, 19, -3, '#ffd23f', true],
    [3, 16.5, 3, 4.5, 19, 4.5, '#ffd23f', true],
    [11.5, 16.5, 3, 13, 19, 4.5, '#ffd23f', true],
    [7.2, 16.5, -4.5, 8.8, 19.5, -3.5, '#ffd23f', true],
    [7.2, 16.5, 3.5, 8.8, 19.5, 4.5, '#ffd23f', true],
    [7, 14.6, -4.9, 9, 16, 4.9, '#e0306a', true],
  ],
  shades: [
    [5, 9.2, -4.8, 9.6, 12.6, -4.2, '#111111'],
    [5, 9.2, 4.2, 9.6, 12.6, 4.8, '#111111'],
    [9, 11.4, -4.6, 10, 12.4, 4.6, '#111111'],
    [3, 14, -1.5, 9, 15, 1.5, '#ff5fa2'],
    [4, 15, -1, 8, 18, 1, '#ffd23f', true],
    [5, 18, -0.6, 7, 20, 0.6, '#5fd4ff', true],
  ],
}
// What the store says about each skin and power-up
const SHOP_ABOUT = {
  classic: 'Each character in its own colours',
  squirrel: 'The one and only',
  wolf: 'Midnight blue, with eyes like ice',
  frog: 'Solid gold and very jumpy',
  spider: 'Sweet, pink and eight legs long',
  iguana: 'Hot-headed and red all over',
  scorpion: 'Cold-blooded, in crystal blue',
  croc: 'A purple crocodile fit for a king',
  bear: 'A great glowing golden bear',
  fox: 'Quick, sly and very orange',
  panda: 'Black and white and round all over',
  ninja: 'Silent, deadly, still loves nuts',
  wizard: 'A starry hat and a long white beard',
  radioactive: "Glows in the dark. Don't ask why",
  ghost: 'See-through, pale and spooky',
  unicorn: 'A golden horn and a glowing pink tail',
  molten: 'Fresh out of the volcano',
  king: 'A jewelled crown for the ruler of the woods',
  disco: 'Never stops changing colour',
  golden: 'Draws every nut nearby straight into your mouth for 12 seconds',
  super: "Rainbow power: unstoppable for 10 seconds. Not in boss fights",
}
// The dev menu's wrench, shown once !dev is typed
const DEV_ICON = ['......##..', '.....##...', '.....##..#', '.....#####', '....####..', '...###....', '..###.....', '.###......', '###.......', '##........']
const DEV_KEY = 'nutsDev'
// Power-ups to buy and summon at any time, one of each held at once
const SHOP_ITEMS = [
  { id: 'golden', name: 'Golden nut', price: 30, key: '1' },
  { id: 'super', name: 'Super nut', price: 75, key: '2' },
]
const SHOP_KEY = 'nutsShop'
const readShop = () => {
  let saved = {}
  try {
    saved = JSON.parse(stored(SHOP_KEY)) || {}
  } catch (e) {
    saved = {}
  }
  const items = saved.items || {}
  return {
    bank: Math.max(0, Math.floor(Number(saved.bank)) || 0),
    owned: Array.isArray(saved.owned) ? saved.owned.filter((id) => SKINS.some((k) => k.id === id)) : [],
    skin: SKINS.some((k) => k.id === saved.skin) ? saved.skin : 'classic',
    char: CHARACTERS.some((c) => c.id === saved.char) ? saved.char : 'squirrel',
    chars: Array.isArray(saved.chars) ? saved.chars.filter((id) => CHARACTERS.some((c) => c.id === id)) : [],
    items: { golden: items.golden ? 1 : 0, super: items.super ? 1 : 0 },
  }
}

const pixelPath = (rows) =>
  rows
    .map((row, y) =>
      row
        .split('')
        .map((c, x) => (c === '#' ? `M${x} ${y}h1v1H${x}z` : ''))
        .join(''),
    )
    .join('')

const KEYS = {
  w: [0, -1],
  arrowup: [0, -1],
  s: [0, 1],
  arrowdown: [0, 1],
  a: [-1, 0],
  arrowleft: [-1, 0],
  d: [1, 0],
  arrowright: [1, 0],
}

const SKY_DAY = new Color('#87c5f0')
const SKY_NIGHT = new Color('#0b1230')
const SKY_DUSK = new Color('#f19a5c')
const SUN_DAY = new Color('#fff4dc')
const SUN_NIGHT = new Color('#8fa6ff')
const BOLT_WHITE = new Color('#eef2ff')

// What grows in each biome: tree kinds (repeats make one likelier), leaf
// colours as [leaves, highlight] pairs, ground plants as [kind, count, colours],
// and the biome's own nut
const FLORA = {
  meadow: {
    trees: ['oak', 'pine', 'birch', 'wide', 'shrub'],
    plants: [['flower', 26, ['red', 'yellow', 'white', 'purple']], ['rock', 3, ['rock']]],
    boulders: [1, ['#8d8a82', '#6e8a4a']], // grey, mossy on top
    litter: ['#5f7a2a', '#7a9038', '#4e6a24'],
    nut: 'acorn',
  },
  fog: {
    trees: ['spruce', 'spruce', 'dead', 'dead', 'birch'],
    foliage: [['#55635a', '#6f7a70'], ['#4a564d', '#626c60'], ['#646a5a', '#7e8470']],
    plants: [['rock', 9, ['rock']], ['paleShroom', 7, ['pale']], ['branch', 4, ['branch']]],
    boulders: [2, ['#7a7d78', '#9aa08e']], // pale with lichen
    litter: ['#7a6a52', '#6a5e4a', '#8a8470'],
    nut: 'chestnut',
  },
  rain: {
    trees: ['oak', 'willow', 'willow', 'wide', 'shrub'],
    foliage: [['#3f7a2a', '#5c9a3a'], ['#2f6b35', '#4c8a4a'], ['#5a8a2a', '#7aa63e']],
    plants: [['fern', 16, ['fern']], ['rock', 5, ['rock']], ['flower', 6, ['white', 'yellow']]],
    litter: ['#8aa63e', '#6a8a2a', '#a8a24a'],
    nut: 'walnut',
  },
  storm: {
    trees: ['spruce', 'pine', 'dead', 'wide'],
    foliage: [['#2c4a2a', '#3d5e33'], ['#4a3a2a', '#5e4a33'], ['#33452a', '#4a5a36']],
    plants: [['rock', 10, ['darkRock']], ['branch', 7, ['branch']], ['fern', 4, ['fern']]],
    boulders: [2, ['#55534f', '#3f3d3a']], // dark slate
    litter: ['#5e4a33', '#4a3a2a', '#6a5a3a'],
    nut: 'hazelnut',
  },
  desert: {
    trees: ['saguaro', 'saguaro', 'barrel', 'prickly', 'organpipe'],
    foliage: [['#4f8a3f', '#6fa85a'], ['#3f7a4a', '#5c9a63'], ['#6a8a3a', '#8aa64e']],
    plants: [['rock', 8, ['sandRock']], ['tumbleweed', 5, ['tumbleweed']], ['bones', 2, ['bone']], ['flower', 6, ['yellow', 'red']]],
    boulders: [2, ['#c48a52', '#a8683a']], // banded red sandstone
    litter: ['#c9955a', '#e3c78a', '#b88a55'],
    grassDensity: 0.3, // sparse, dry tufts
    nut: 'cactusfruit',
  },
  tropical: {
    trees: ['palm', 'palm', 'palm', 'shrub'],
    foliage: [['#2f9a3a', '#4cc04a'], ['#3aa060', '#5cc07a']],
    plants: [['shell', 14, ['shell', 'white']], ['starfish', 5, ['starfish']], ['fern', 6, ['jungleLeaf']], ['rock', 3, ['sandRock']]],
    boulders: [1, ['#9a8f7f', '#c9c2b2']], // weathered, salt-whitened
    litter: ['#f2e2b0', '#ffffff', '#e8d8a8'],
    grassDensity: 0.45,
    lagoons: true, // pools of water ringed with sand
    nut: 'coconut',
  },
  snow: {
    trees: ['spruce', 'spruce', 'spruce', 'pine', 'dead'],
    // Boughs heavy with snow
    foliage: [['#e8eef2', '#ffffff'], ['#9fb4ae', '#e8eef2'], ['#7d958c', '#dfe7ec']],
    plants: [['rock', 8, ['rock']], ['branch', 3, ['branch']]],
    boulders: [2, ['#9aa4ac', '#f2f6f8']], // grey, capped with snow
    litter: ['#ffffff', '#e3ebf0', '#cfd9e0'],
    grassDensity: 0.35,
    nut: 'pinecone',
  },
  jungle: {
    trees: ['kapok', 'kapok', 'palm', 'palm', 'shrub'],
    foliage: [['#2f8a3a', '#4cb04a'], ['#1f7a4a', '#3aa060'], ['#4a9a2a', '#7ac040']],
    plants: [['fern', 18, ['jungleLeaf']], ['bigLeaf', 10, ['jungleLeaf', 'fern']], ['flower', 12, ['pink', 'orange', 'red']]],
    litter: ['#3a8f30', '#4aa03a', '#2a7a2a', '#7ac040'],
    extraTrees: 3, // a denser canopy
    nut: 'coconut',
  },
}
const NUT_COLOURS = {
  acorn: null, // the acorn keeps its theme-tinted materials for the flat view
  chestnut: ['#7a3b1c', '#a65a2e', '#d8c49a'],
  walnut: ['#b88a55', '#7a5530', '#b88a55'],
  hazelnut: ['#a86b34', '#6e4320', '#8a9a4a'],
  coconut: ['#6b4a2e', '#3a2618', '#6b4a2e'],
  cactusfruit: ['#d8305a', '#f2c4d0', '#4f8a3f'],
  pinecone: ['#8a5a2e', '#5a3a1e', '#4f7a3a'],
}
const DECOR_COLOURS = ['#4f7a2a', '#d8343a', '#f2c230', '#f2efe6', '#8a5ad0', '#7d7f80', '#5a7a3a', '#3f8a3a', '#2f9a4a', '#ff5fa2', '#ff8a2a', '#6b4a2a', '#d8d2c0', '#55585c', '#c9955a', '#efe8d8', '#9a7a4a', '#f7b6c2', '#ff7a3a']

// Ambient wildlife per biome, as how many of each (1 = the usual amount)
//   birds by day and bats by night, with the colours their flocks come in
const WILDLIFE = {
  meadow: { fireflies: 1, beetles: 1, butterflies: 1, bees: 1, dragonflies: 0.3, birds: 1, bats: 0.6, leaves: 1, mist: 0, splashes: 0, birdColours: ['#7a5a3a', '#5a4a3a', '#a07850', '#3a3a3a'], wingColours: ['#f2d06b', '#ffffff', '#e09a3e', '#9ad0ff', '#d9532b'], leafColours: ['#d9692b', '#f0a13a', '#b8322a', '#e3b23c'] },
  fog: { fireflies: 0.4, beetles: 0.6, butterflies: 0, bees: 0, dragonflies: 0, birds: 0.5, bats: 1.2, leaves: 0.4, mist: 1, splashes: 0, birdColours: ['#2a2a2e', '#3a3a40'], wingColours: ['#d8d8d0'], leafColours: ['#7a7a6a', '#8a8470'] },
  rain: { fireflies: 0.3, beetles: 0.6, butterflies: 0, bees: 0, dragonflies: 1, birds: 0.2, bats: 0.4, leaves: 0.3, mist: 0.35, splashes: 1, birdColours: ['#3a3a40'], wingColours: ['#ffffff'], leafColours: ['#5c9a3a', '#3f7a2a'] },
  storm: { fireflies: 0, beetles: 0.3, butterflies: 0, bees: 0, dragonflies: 0, birds: 0, bats: 0.8, leaves: 1.6, mist: 0.2, splashes: 1.6, birdColours: ['#2a2a2e'], wingColours: ['#ffffff'], leafColours: ['#3d5e33', '#5e4a33', '#7a6a3a'] },
  desert: { fireflies: 0, beetles: 1, butterflies: 0.2, bees: 0, dragonflies: 0, birds: 0.6, bats: 0.8, leaves: 0, mist: 0, splashes: 0, birdColours: ['#3a2a20', '#4a3a2a'], wingColours: ['#f2d06b'], leafColours: ['#c9955a'] },
  tropical: { fireflies: 0.6, beetles: 0.5, butterflies: 1, bees: 0.3, dragonflies: 0.8, birds: 1.2, bats: 0.5, leaves: 0.2, mist: 0, splashes: 0, birdColours: ['#f2f2f2', '#e8e8e8', '#e8382c', '#2a8af0'], wingColours: ['#ff8a2a', '#2f7bff', '#f5c400'], leafColours: ['#4cc04a'] },
  snow: { fireflies: 0, beetles: 0, butterflies: 0, bees: 0, dragonflies: 0, birds: 0.4, bats: 0.3, leaves: 0, mist: 0.4, splashes: 0, birdColours: ['#2a2a2e', '#f2f2f2'], wingColours: ['#ffffff'], leafColours: ['#ffffff'] },
  jungle: { fireflies: 1.5, beetles: 1, butterflies: 1.6, bees: 0.4, dragonflies: 1, birds: 1.3, bats: 0.7, leaves: 0.3, mist: 0.5, splashes: 0, birdColours: ['#e8382c', '#2a8af0', '#2fbf4a', '#f5c400', '#ff7a1a'], wingColours: ['#2f7bff', '#00c2ff', '#ff8a2a', '#c43cff'], leafColours: ['#2f8a3a', '#4cb04a'] },
}

// Autumn foliage, as [leaves, highlight] pairs
const FOLIAGE = [
  ['#d9692b', '#f0a13a'],
  ['#b8322a', '#d9532b'],
  ['#e3b23c', '#f2d06b'],
  ['#c4802f', '#e09a3e'],
  ['#8f2d1f', '#c0442c'],
  ['#a8572a', '#cf7a35'],
]

// Difficulty: how many lives, how many creatures and how quick they are, and
// how long between hearts. Chosen at the top right or on the pause card.
// The rest sharpen the creatures: how far they spot the squirrel (sight),
// how quick their pounces come (attack scales windup and cooldown), how far
// they spring from (reach), extra hits to kill (hp), how far ahead of a
// running squirrel they aim (lead), how many are found asleep (sleep), how
// soon they spit again (shots, and whether they spit in fine weather too),
// how soon they come back (respawn), and how many turn up ahead (ahead).
// Bosses: bossPace scales the waits before and between their attacks and how
// long the warnings last, bossCount how many shots, spikes or rocks each
// attack has, bossDaze how long they stay dazed, and bossSpit the hits after
// which they also spit while prowling. When lives are down to half or less,
// power-ups turn up lowPowers times as often again.
const CALM = { sight: 1, attack: 1, reach: 1, hp: 0, lead: 0, sleep: 0.55, shots: 1, spitDry: false, respawn: 1, ahead: 0 }
const DIFFICULTIES = {
  easy: { ...CALM, name: 'Easy', lives: 5, creatures: 0.7, speed: 0.85, hearts: 0.7, bossPace: 1.3, bossCount: 0.8, bossDaze: 1.25, bossSpit: 3, lowPowers: 1.5 },
  normal: { ...CALM, name: 'Normal', lives: 5, creatures: 1, speed: 1, hearts: 1, bossPace: 1, bossCount: 1, bossDaze: 1, bossSpit: 2, lowPowers: 0.8 },
  hard: { ...CALM, name: 'Hard', lives: 4, creatures: 1.5, speed: 1.12, hearts: 1.5, bossPace: 0.8, bossCount: 1.25, bossDaze: 0.85, bossSpit: 1, lowPowers: 0.4 },
  insane: {
    name: 'Insane',
    lives: 3,
    creatures: 2.6,
    speed: 1.2,
    hearts: 2,
    sight: 1.7,
    attack: 0.5,
    reach: 1.35,
    hp: 1,
    lead: 1,
    sleep: 0.15,
    shots: 0.5,
    spitDry: true,
    respawn: 0.4,
    ahead: 0.65,
    bossPace: 0.55,
    bossCount: 1.6,
    bossDaze: 0.7,
    bossSpit: 0,
    lowPowers: 0.1,
  },
}
const DIFFICULTY_LEVELS = Object.keys(DIFFICULTIES)
const DIFFICULTY_KEY = 'nutsDifficulty'
const BONUS_HEARTS = 2 // most extra heart slots won from bosses in a run
const MAX_LIVES = Math.max(...DIFFICULTY_LEVELS.map((d) => DIFFICULTIES[d].lives)) + BONUS_HEARTS
const MAX_CREATURES = Math.max(...DIFFICULTY_LEVELS.map((d) => DIFFICULTIES[d].creatures))

const stored = (key) => {
  try {
    return window.localStorage.getItem(key)
  } catch (e) {
    return null
  }
}
const store = (key, value) => {
  try {
    window.localStorage.setItem(key, value)
  } catch (e) {
    // Storage blocked: it just won't be remembered
  }
}
const readDifficulty = () => {
  const level = stored(DIFFICULTY_KEY)
  return DIFFICULTIES[level] ? level : 'easy'
}
// Graphics settings, from the gear at the top right. Each setting has its own
// levels; a preset sets them all at once (to the nearest level a setting has).
// High is the default.
const GRAPHICS_KEY = 'nutsGraphics'
const PRESETS = ['low', 'medium', 'high', 'ultra']
const LEVEL_NAMES = { low: 'Low', medium: 'Medium', high: 'High', ultra: 'Ultra', off: 'Off', on: 'On' }
const GRAPHICS = {
  // How far out the woods are grown and the haze lifts (× the original 900)
  view: { name: 'Draw distance', levels: { low: 0.75, medium: 1, high: 1.25, ultra: 2 } },
  // Pixels drawn per screen pixel (× the device's own)
  resolution: { name: 'Resolution', levels: { low: 0.5, medium: 0.75, high: 1 } },
  // Grass tufts and ground plants per chunk
  foliage: { name: 'Foliage density', levels: { low: 0.35, medium: 0.65, high: 1, ultra: 1.4 } },
  // Birds, insects, bats, falling leaves and mist
  wildlife: { name: 'Ambient wildlife', levels: { low: 0.25, medium: 0.6, high: 1, ultra: 1.5 } },
  // Rain, splashes, dust and the bits things break into
  particles: { name: 'Particle effects', levels: { low: 0.3, medium: 0.6, high: 1 } },
  // The glow around the squirrel
  lighting: { name: 'Dynamic lighting', levels: { off: 0, on: 1 } },
}
const nearestLevel = (key, preset) => {
  const levels = Object.keys(GRAPHICS[key].levels)
  if (levels.includes(preset)) return preset
  if (key === 'lighting') return preset === 'low' ? 'off' : 'on'
  return levels[levels.length - 1]
}
const presetSettings = (preset) => {
  const out = { preset }
  Object.keys(GRAPHICS).forEach((key) => {
    out[key] = nearestLevel(key, preset)
  })
  return out
}
const readGraphics = () => {
  const out = presetSettings('high')
  try {
    const saved = JSON.parse(stored(GRAPHICS_KEY) || '{}')
    Object.keys(GRAPHICS).forEach((key) => {
      if (saved[key] in GRAPHICS[key].levels) out[key] = saved[key]
    })
    if (PRESETS.includes(saved.preset) || saved.preset === 'custom') out.preset = saved.preset
  } catch (e) {
    // Nothing saved, or unreadable: High
  }
  return out
}
let gfx = readGraphics()
const gv = (key) => GRAPHICS[key].levels[gfx[key]]
const viewRadius = () => LOAD_RADIUS * gv('view')

// Each difficulty keeps its own best score
const bestKey = (level) => (level === 'easy' ? BEST_KEY : `${BEST_KEY}:${level}`)
const readBest = (level) => parseInt(stored(bestKey(level)), 10) || 0
const saveBest = (level, best) => store(bestKey(level), best)

// Kept outside the component so the squirrel survives page transitions
const world = {
  squirrel: null,
  acorns: [],
  crumbs: [],
  spawnIn: 1.5,
  staged: false,
  time: DAY_START, // Time of day, 0-1; starts in the morning
  seed: 1997, // The woods; a new one grows after each game over
  eaten: new Set(), // mushrooms eaten this run, so regrown chunks leave them out
  felled: new Map(), // pounces each tree has taken this run, so regrown chunks match
  harvested: new Set(), // trees that have grown their one acorn this run
  startBiome: 'meadow',
  biomeStart: { x: 0, z: 0 }, // where the run began, at the middle of its biome
  score: 0,
  lives: LIVES,
}

const rand = (min, max) => min + Math.random() * (max - min)
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
const lerp = (a, b, t) => a + (b - a) * t
const ease = (t) => t * t * (3 - 2 * t)
const flat = (v) => Math.hypot(v.x, v.z)
// Theme colours can be 8-digit hex, which three.js doesn't parse
const solid = (colour) => (/^#[0-9a-f]{8}$/i.test(colour) ? colour.slice(0, 7) : colour)

// Seeded so the woods grow the same way until the game is lost
const seeded = (seed) => () => {
  seed = (seed + 0x6d2b79f5) | 0
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296
}

const Squirrel = () => {
  const canvasRef = useRef(null)
  const counterRef = useRef(null)
  const promptRef = useRef(null)
  const toastRef = useRef(null)
  const hintRef = useRef(null)
  const touchRef = useRef(null)
  const fpsRef = useRef(null)
  const fadeRef = useRef(null)
  const exitRef = useRef(null)
  const overlayRef = useRef(null)
  const popupsRef = useRef(null)
  const soundRef = useRef(null)
  const pauseRef = useRef(null)
  const levelRef = useRef(null)
  const infoRef = useRef(null)
  const fullRef = useRef(null)
  const gearRef = useRef(null)
  const shopRef = useRef(null)
  const bagRef = useRef(null)
  const storePanelRef = useRef(null)
  const bagPanelRef = useRef(null)
  const devRef = useRef(null)
  const devPanelRef = useRef(null)
  const storePopRef = useRef(null)
  const cameraRef = useRef(null)
  const settingsRef = useRef(null)
  const bossRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const counter = counterRef.current
    const prompt = promptRef.current
    const toast = toastRef.current
    const hint = hintRef.current
    const touchPad = touchRef.current
    // Frames per second, bottom left; O toggles it, remembered for next time
    const fpsEl = fpsRef.current
    const fadeEl = fadeRef.current
    if (stored(FPS_KEY) === 'on') {
      fpsEl.classList.add('show')
      fpsEl.textContent = '-- FPS'
    }
    let fpsFrames = 0
    let fpsTime = 0
    const stickEl = touchPad.querySelector('.nut-stick')
    const knobEl = touchPad.querySelector('.nut-stick__knob')
    const jumpEl = touchPad.querySelector('.nut-touch__jump')
    const attackEl = touchPad.querySelector('.nut-touch__attack')
    const exitBar = exitRef.current
    const overlay = overlayRef.current
    const popups = popupsRef.current
    const soundButton = soundRef.current
    const pauseButton = pauseRef.current
    const levelButton = levelRef.current
    const infoButton = infoRef.current
    const fullButton = fullRef.current
    const gearButton = gearRef.current
    const shopButton = shopRef.current
    const bagButton = bagRef.current
    const storePanel = storePanelRef.current
    const bagPanel = bagPanelRef.current
    const devButton = devRef.current
    const devPanel = devPanelRef.current
    const cameraButton = cameraRef.current
    const settingsPanel = settingsRef.current
    const levelChoices = [...overlay.querySelectorAll('.nut-overlay__levels button')]
    const sound = createSound(stored(SOUND_KEY) !== 'off')
    // Nuts banked for the store, skins bought and worn, power-ups held
    const shop = readShop()
    const saveShop = () => store(SHOP_KEY, JSON.stringify(shop))
    // The dev menu (typed !dev to show it, remembered), and its cheats
    let devOn = stored(DEV_KEY) === 'on'
    let devGod = false
    let devFreeze = false
    document.documentElement.classList.toggle('nut-dev-on', devOn)
    const countEl = counter.querySelector('.nuts-eaten__count')
    const bestEl = counter.querySelector('.nuts-eaten__best')
    const comboEl = counter.querySelector('.nuts-eaten__combo')
    const powersEl = counter.querySelector('.nuts-eaten__powers')
    const bossBar = bossRef.current
    const bossNameEl = bossBar.querySelector('.boss-bar__title')
    const bossFill = bossBar.querySelector('.boss-bar__fill')
    // The streak multiplier next to the score
    function showCombo() {
      const sq = world.squirrel
      const mult = sq ? comboMultiplier(sq.streak || 0) : 1
      comboEl.textContent = mult > 1 ? `×${mult}` : ''
    }
    const lifeIcons = [...counter.querySelectorAll('.nuts-eaten__life')]
    const nav = document.querySelector('nav')
    const stage = document.querySelector('.stage')
    const cursorEl = document.querySelector('#cursor')
    if (!nav) return undefined

    let renderer
    try {
      renderer = new WebGLRenderer({ canvas, alpha: true, antialias: false })
    } catch (e) {
      return undefined // No WebGL: no squirrel
    }
    renderer.autoClear = false

    if (!DIFFICULTIES[world.difficulty]) world.difficulty = readDifficulty()
    const level = () => DIFFICULTIES[world.difficulty]
    const maxLives = () => level().lives + (world.bonusLives || 0)

    // ----- Pointer capture -----
    // In nut mode the cursor is captured so it can't wander out of the window.
    // The browser frees it on Escape, which leaves nut mode. Pausing frees it
    // too so the buttons can be used, and carrying on captures it again.
    let freedOnPurpose = false
    let switchedFullAt = 0
    function lockPointer() {
      const el = document.documentElement
      if (touchOnly || !world.staged || document.pointerLockElement || !el.requestPointerLock) return
      try {
        const request = el.requestPointerLock()
        if (request && request.catch) request.catch(() => {})
      } catch (e) {
        // Not allowed just now (no click or key press behind it); stays free
      }
    }
    function releasePointer() {
      if (!document.pointerLockElement) return
      freedOnPurpose = true
      document.exitPointerLock()
    }
    function onLockChange() {
      const locked = !!document.pointerLockElement
      document.documentElement.classList.toggle('nut-locked', locked)
      if (locked) return
      if (freedOnPurpose) {
        freedOnPurpose = false
        return
      }
      // Switching fullscreen with L or the button can free it too: take it back
      if (Date.now() - switchedFullAt < 1500) {
        if (!world.paused) lockPointer()
        return
      }
      // Freed by the browser: Escape leaves nut mode, while switching away
      // from the window just pauses (see onBlur)
      setTimeout(() => {
        if (world.staged && document.hasFocus() && !document.hidden) setStaged(false)
      }, 120)
    }
    let best = readBest(world.difficulty)
    const bestLabel = counter.querySelector('.nuts-eaten__label')
    const showScore = () => {
      countEl.textContent = world.score
      bestEl.textContent = best
      bestLabel.textContent = `${level().name} best`
      lifeIcons.forEach((h, i) => {
        h.classList.toggle('lost', i >= world.lives)
        h.style.display = i < maxLives() ? '' : 'none'
      })
    }
    if (world.lives > maxLives()) world.lives = maxLives()
    showScore()

    // Nut mode is desktop only; touch screens just get the flat squirrel
    const touchOnly = window.matchMedia('(hover: none)').matches
    document.documentElement.classList.toggle('nut-touch-device', touchOnly)
    const mouse = { x: -9999, y: -9999 }
    const keys = new Set()
    let sprinting = false // Shift held
    // The on-screen joystick on touch screens: -1 to 1 each way, up is -y
    const stick = { x: 0, y: 0 }
    // First person: seen from the squirrel's eyes. The mouse (captured) looks
    // around, Q and E turn, and WASD move relative to where it's looking; on
    // touch screens the joystick turns left and right and moves forward and back.
    let firstPerson = stored(CAMERA_KEY) === 'first'
    let fpBlend = 0 // eases between the chase camera and the eyes
    let lookYaw = 0 // 0 looks the way the chase camera does
    let lookPitch = 0
    const turning = new Set()
    const lookDir = () =>
      new Vector3(-Math.sin(lookYaw) * Math.cos(lookPitch), Math.sin(lookPitch), -Math.cos(lookYaw) * Math.cos(lookPitch))
    const viewHeading = () => Math.atan2(Math.cos(lookYaw), -Math.sin(lookYaw))
    let spaceHeld = false // for gliding
    // ground: nav's top edge on screen, p: progress into nut mode, e: eased p
    const view = { ground: 0, p: 0, e: 0, restGround: 0 }
    const bounds = { x0: 0, x1: 0 }
    let lastCounter = ''
    let promptTimer
    let toastTimer
    let frame
    let last = performance.now()

    // ----- Materials -----
    const themeMain = new Color()
    const themeBg = new Color()
    // Theme-coloured and flat in the flat view, naturally coloured and lit in nut mode
    const tinted = []
    const tint = (natural, role = 'main') => {
      const m = new MeshLambertMaterial()
      m.userData = { natural: new Color(natural), role }
      tinted.push(m)
      return m
    }
    const squirrelMats = [tint('#9c5326'), tint('#140d08', 'bg'), tint('#c7753a')]
    const acornMats = [tint('#c68642'), tint('#7a4a22', 'bg'), tint('#5c3a1e')]
    // Super nuts cycle through the rainbow and glow
    const superMats = [0, 0.33, 0.66].map((offset) => {
      const m = new MeshLambertMaterial()
      m.userData.offset = offset
      return m
    })
    const lambert = (colour) => new MeshLambertMaterial({ color: colour })
    const treeMats = [
      lambert('#6b4226'),
      lambert('#d9692b'),
      lambert('#f0a13a'),
      lambert('#e8e4da'),
      lambert('#2b2724'),
      lambert('#3f7a2a'), // vines
      lambert('#a07c4c'), // palm bark
      lambert('#6b6660'), // dead grey bark
      lambert('#e0306a'), // cactus fruit
      lambert('#ffd23f'), // cactus flowers
      lambert('#8d8a82'), // stone
      lambert('#6e8a4a'), // moss on stone
    ]
    const decorMats = DECOR_COLOURS.map((c) => lambert(c))
    // Every biome's nut, in its own colours
    const nutGeos = {}
    const nutMats = {}
    NUT_KINDS.forEach((kind) => {
      nutGeos[kind] = kind === 'acorn' ? null : buildNutGeometry(kind)
      nutMats[kind] = NUT_COLOURS[kind] ? NUT_COLOURS[kind].map((c) => lambert(c)) : null
    })
    const mushroomMats = [lambert('#d62828'), lambert('#f4efe6')]
    const spiderMats = [lambert('#1c1a1f'), new MeshBasicMaterial({ color: '#ff2a2a' })]
    const hurtMat = new MeshBasicMaterial({ color: '#ff3030' })
    const glowMat = new MeshBasicMaterial({ color: '#9ff4ff' }) // charged by lightning
    const groundMat = lambert('#5f6e2c')
    const grassMat = lambert('#ffffff')
    const shadowMat = new MeshBasicMaterial({ color: 0x000000, transparent: true, depthWrite: false })
    const starMat = new PointsMaterial({
      color: 0xffffff,
      size: 2,
      sizeAttenuation: false,
      transparent: true,
      fog: false,
    })
    const allMats = [
      ...squirrelMats,
      ...acornMats,
      ...superMats,
      ...treeMats,
      ...mushroomMats,
      ...spiderMats,
      hurtMat,
      groundMat,
      grassMat,
      shadowMat,
      starMat,
    ]

    const readColours = () => {
      const styles = getComputedStyle(document.documentElement)
      themeMain.set(solid(styles.getPropertyValue('--main-color').trim() || '#000'))
      themeBg.set(solid(styles.getPropertyValue('--background-color').trim() || '#fff'))
    }

    // ----- Scene -----
    const scene = new Scene()
    scene.fog = new Fog(0xffffff, 1e6, 2e6)
    const camera = new PerspectiveCamera(FLAT_FOV, 1, 1, 1000)
    const hemi = new HemisphereLight(0xffffff, 0x3a5a2a, 0)
    const sun = new DirectionalLight(0xffffff, 0)
    scene.add(hemi, sun, sun.target)

    const geometries = []
    const keep = (g) => {
      geometries.push(g)
      return g
    }
    const squirrelGeo = buildSquirrelGeometries()
    keep(squirrelGeo.body)
    squirrelGeo.legs.forEach(keep)
    const acornGeo = keep(buildAcornGeometry())
    const shadowGeo = keep(new PlaneGeometry(1, 1).rotateX(-Math.PI / 2))
    const crumbGeo = keep(new BoxGeometry(VOXEL, VOXEL, VOXEL))

    const shadowMesh = () => new Mesh(shadowGeo, shadowMat)

    const squirrelGroup = new Group()
    squirrelGroup.rotation.order = 'YXZ'
    const squirrelBody = new Mesh(squirrelGeo.body, squirrelMats)
    const squirrelLegs = squirrelGeo.legs.map((g) => new Mesh(g, squirrelMats))
    squirrelGroup.add(squirrelBody, ...squirrelLegs)
    // Whatever the skin wears on its head, moving with the body
    const hat = new Group()
    squirrelBody.add(hat)
    const hatBox = keep(new BoxGeometry(1, 1, 1).translate(0.5, 0.5, 0.5))
    const hatMats = new Map()
    const hatMat = (colour, lit) => {
      const k = `${colour}${lit ? '!' : ''}`
      if (!hatMats.has(k)) hatMats.set(k, lit ? new MeshBasicMaterial({ color: colour }) : lambert(colour))
      return hatMats.get(k)
    }
    const skinTint = new Color()
    const skinOf = () => SKINS.find((k) => k.id === shop.skin) || SKINS[0]
    const hatParts = (skin) =>
      (HATS[skin.hat] || []).map(([x0, y0, z0, x1, y1, z1, colour, lit]) => {
        const part = new Mesh(hatBox, hatMat(colour, lit))
        part.position.set(x0, y0, z0)
        part.scale.set(x1 - x0, y1 - y0, z1 - z0)
        return part
      })
    const applySkin = () => {
      const skin = skinOf()
      ;[skin.fur, skin.eye, skin.tail].forEach((c, i) => squirrelMats[i].userData.natural.set(c))
      squirrelMats.forEach((m) => {
        m.transparent = !!skin.ghost
        m.opacity = 1
        m.depthWrite = !skin.ghost
        m.needsUpdate = true
      })
      hat.clear()
      hatParts(skin).forEach((part) => hat.add(part))
    }
    applySkin()
    const squirrelShadow = shadowMesh()
    scene.add(squirrelGroup, squirrelShadow)

    // Everything that only exists in nut mode grows out of the ground with it
    const woods = new Group()
    scene.add(woods)

    // Ground and stars travel with the squirrel
    const ground = new Mesh(keep(new PlaneGeometry(6000, 6000).rotateX(-Math.PI / 2)), groundMat)
    const stars = new Points(keep(new BufferGeometry()), starMat)
    woods.add(ground, stars)
    const scatterStars = () => {
      const random = seeded(42)
      const starPos = []
      for (let i = 0; i < 400; i++) {
        const a = random() * Math.PI * 2
        const up = 0.01 + random() * random() * 0.6
        const across = 1800 * Math.sqrt(1 - up * up)
        starPos.push(Math.cos(a) * across, up * 1800, Math.sin(a) * across)
      }
      stars.geometry.setAttribute('position', new Float32BufferAttribute(starPos, 3))
    }
    scatterStars()

    const spiderBodyGeo = keep(buildSpiderBody())
    const spiderLegGeo = keep(
      new BoxGeometry(VOXEL * 0.8, VOXEL * 0.8, VOXEL * 4).translate(0, 0, VOXEL * 2),
    )

    const wolfBodyGeo = keep(buildWolfBody())
    const wolfLegGeo = keep(buildWolfLeg())
    const frogGeo = keep(buildFrog())
    const snakeHeadGeo = keep(buildSnakeHead())
    const snakeSegGeo = keep(buildSnakeSegment())
    const wolfMats = [lambert('#7b8088'), lambert('#454951'), new MeshBasicMaterial({ color: '#ffd23f' })]
    const frogMats = [lambert('#4f8a3a'), lambert('#c9d27a'), new MeshBasicMaterial({ color: '#ffcc33' }), lambert('#2f5a26')]
    const snakeMats = [lambert('#3b3f2a'), lambert('#c9a227'), new MeshBasicMaterial({ color: '#ff3b3b' }), new MeshBasicMaterial({ color: '#ff3355' })]
    const SNAKE_SEGMENTS = 6
    const crocBodyGeo = keep(buildCrocBody())
    const crocLegGeo = keep(new BoxGeometry(VOXEL * 1.6, VOXEL * 2, VOXEL * 1.6).translate(0, -VOXEL, 0))
    const bearGeo = keep(buildBearBody())
    const bearLegGeo = keep(buildBearLeg())
    const bearMats = [lambert('#eef2f4'), lambert('#d8d0bc'), new MeshBasicMaterial({ color: '#141414' })]
    const batGeo = keep(buildBatBody())
    const batWingGeo = keep(buildBatWing())
    const batMats = [lambert('#2a2230'), lambert('#b8a9b0'), new MeshBasicMaterial({ color: '#ff3a3a' })]
    const scorpionGeo = keep(buildScorpionBody())
    const scorpionTailGeo = keep(buildScorpionTail())
    const scorpionMats = [lambert('#c99a4a'), lambert('#5a3a1a'), new MeshBasicMaterial({ color: '#1a1a1a' })]
    const iguanaGeo = keep(buildIguanaBody())
    const iguanaMats = [lambert('#4a9a3a'), lambert('#ff8a2a'), new MeshBasicMaterial({ color: '#1a1a1a' }), lambert('#c9d27a')]
    const crocMats = [lambert('#4a6a32'), lambert('#2f4520'), new MeshBasicMaterial({ color: '#ffd23f' }), lambert('#f2efe6')]

    // A creature's body, legs (on pivots), tail or segments, in materials m.
    // add puts each mesh on its parent (and can keep a list of them).
    const assembleCreature = (kind, group, m, add = (parent, mesh) => parent.add(mesh) && mesh) => {
      const legs = []
      const segs = []
      let body
      let tail = null
      if (kind === 'spider') {
        body = add(group, new Mesh(spiderBodyGeo, m))
        for (let i = 0; i < 8; i++) {
          const side = i < 4 ? 1 : -1
          const pivot = new Group()
          pivot.position.set((-1.5 + (i % 4)) * VOXEL * 1.1, VOXEL * 2, side * VOXEL)
          const leg = add(pivot, new Mesh(spiderLegGeo, m[0]))
          leg.rotation.x = side * 0.5
          if (side < 0) leg.rotation.y = Math.PI
          group.add(pivot)
          legs.push(pivot)
        }
      } else if (kind === 'wolf') {
        body = add(group, new Mesh(wolfBodyGeo, m))
        ;[[3.5, 1.5], [3.5, -1.5], [-4.5, 1.5], [-4.5, -1.5]].forEach(([x, z]) => {
          const pivot = new Group()
          pivot.position.set(x * VOXEL, WOLF_HIP * VOXEL, z * VOXEL)
          add(pivot, new Mesh(wolfLegGeo, m[1]))
          group.add(pivot)
          legs.push(pivot)
        })
      } else if (kind === 'bear') {
        body = add(group, new Mesh(bearGeo, m))
        ;[[4, 2.5], [4, -2.5], [-5.5, 2.5], [-5.5, -2.5]].forEach(([x, z]) => {
          const pivot = new Group()
          pivot.position.set(x * VOXEL, BEAR_HIP * VOXEL, z * VOXEL)
          add(pivot, new Mesh(bearLegGeo, m[0]))
          group.add(pivot)
          legs.push(pivot)
        })
      } else if (kind === 'bat') {
        body = add(group, new Mesh(batGeo, m))
        // Two wings at the shoulders, flapping about the body's length
        ;[1, -1].forEach((side) => {
          const pivot = new Group()
          pivot.position.set(0, VOXEL * 2.5, side > 0 ? VOXEL : 0)
          const wing = add(pivot, new Mesh(batWingGeo, m))
          if (side < 0) wing.rotation.y = Math.PI
          group.add(pivot)
          legs.push(pivot)
        })
      } else if (kind === 'frog') {
        body = add(group, new Mesh(frogGeo, m))
      } else if (kind === 'scorpion') {
        body = add(group, new Mesh(scorpionGeo, m))
        // The tail on its own pivot at the base, so it can cock and strike
        tail = new Group()
        tail.position.set(SCORPION_TAIL[0] * VOXEL, SCORPION_TAIL[1] * VOXEL, 0)
        add(tail, new Mesh(scorpionTailGeo, m))
        group.add(tail)
        for (let i = 0; i < 6; i++) {
          const side = i < 3 ? 1 : -1
          const pivot = new Group()
          pivot.position.set((-1.5 + (i % 3) * 1.4) * VOXEL, VOXEL * 1.6, side * VOXEL * (side > 0 ? 2 : 1))
          const leg = add(pivot, new Mesh(spiderLegGeo, m[1]))
          leg.rotation.x = side * 0.7
          if (side < 0) leg.rotation.y = Math.PI
          group.add(pivot)
          legs.push(pivot)
        }
      } else if (kind === 'croc' || kind === 'iguana') {
        const iguana = kind === 'iguana'
        body = add(group, new Mesh(iguana ? iguanaGeo : crocBodyGeo, m))
        ;(iguana ? [[2, 2.5], [2, -3.5], [-3, 2.5], [-3, -3.5]] : [[3, 3], [3, -3], [-4, 3], [-4, -3]]).forEach(([x, z]) => {
          const pivot = new Group()
          pivot.position.set(x * VOXEL, 2 * VOXEL, z * VOXEL)
          add(pivot, new Mesh(crocLegGeo, m[0]))
          group.add(pivot)
          legs.push(pivot)
        })
      } else {
        body = add(group, new Mesh(snakeHeadGeo, m))
        for (let i = 0; i < SNAKE_SEGMENTS; i++) {
          segs.push(add(group, new Mesh(snakeSegGeo, m[i % 2 ? 1 : 0])))
        }
      }
      return { body, legs, segs, tail }
    }

    // One creature of a biome. parts are the meshes that flash red when hurt.
    const makeEnemy = (kind) => {
      const group = new Group()
      // Yaw first, then roll about its own length (for flipping over)
      group.rotation.order = 'YXZ'
      const parts = []
      const add = (parent, mesh) => {
        parent.add(mesh)
        parts.push([mesh, mesh.material])
        return mesh
      }
      const kindMats = { spider: spiderMats, wolf: wolfMats, bear: bearMats, bat: batMats, frog: frogMats, scorpion: scorpionMats, iguana: iguanaMats, croc: crocMats }[kind] || snakeMats
      const { body, legs, segs, tail } = assembleCreature(kind, group, kindMats, add)
      const shadow = shadowMesh()
      woods.add(group, shadow)
      return {
        kind,
        cfg: ENEMY_KINDS[kind],
        group,
        body,
        parts,
        legs,
        segs,
        tail,
        shadow,
        hp: ENEMY_KINDS[kind].hp,
        flashFor: 0,
        knock: new Vector3(),
        pos: new Vector3(),
        heading: rand(0, Math.PI * 2),
        phase: rand(0, 10),
        alive: false,
        deadFor: 0,
        respawnIn: rand(0, 3),
        wanderTo: null,
      }
    }
    // The player as a character, wearing a skin: a skin recolours its body,
    // second colour and eyes (Classic keeps the character's own), may glow
    // or be see-through, and puts its hat on the creature's head. Used for
    // playing and for the store's pictures.
    const charModel = (charId, skin) => {
      const ch = CHARACTERS.find((c) => c.id === charId) || CHARACTERS[0]
      const inner = new Group()
      let base
      let eye = 1
      let second = 2
      let mats
      let legs = []
      let length = 32
      if (ch.id === 'squirrel') {
        base = [skin.fur, skin.eye, skin.tail]
        mats = base.map((c) => new MeshLambertMaterial({ color: c }))
        const body = new Mesh(squirrelGeo.body, mats)
        hatParts(skin).forEach((part) => body.add(part))
        inner.add(body, new Mesh(squirrelGeo.legs[0], mats))
      } else {
        base = ch.colours.slice()
        eye = ch.id === 'spider' ? 1 : 2
        second = ch.id === 'spider' ? -1 : 1
        if (skin.id !== 'classic') {
          base[0] = skin.fur
          base[eye] = skin.eye
          if (second > 0) base[second] = skin.tail
        }
        mats = base.map((c, i) => (i === eye ? new MeshBasicMaterial({ color: c }) : new MeshLambertMaterial({ color: c })))
        const built = assembleCreature(ch.id, inner, mats)
        legs = built.legs
        const geo = built.body.geometry
        if (!geo.boundingBox) geo.computeBoundingBox()
        const box = geo.boundingBox
        length = box.max.x - box.min.x
        // Hats sit on the front of the head, as they do on the squirrel's
        const hatAt = new Group()
        hatAt.position.set(box.max.x - 14, box.max.y - 14, 0)
        hatParts(skin).forEach((part) => hatAt.add(part))
        built.body.add(hatAt)
      }
      const glowOf = skin.id === 'classic' && ch.glow ? [ch.glow, 0, ch.glow] : skin.glow || [0, 0, 0]
      const glow = mats.map((m, i) => (i === 0 ? glowOf[0] : i === eye ? glowOf[1] : i === second ? glowOf[2] : 0))
      if (skin.ghost) {
        mats.forEach((m) => {
          m.transparent = true
          m.opacity = 0.6
          m.depthWrite = false
        })
      }
      const scale = ch.id === 'squirrel' ? 1 : clamp(34 / length, 0.55, 1.5)
      inner.scale.setScalar(scale)
      const group = new Group()
      group.add(inner)
      return { group, mats, colours: base.map((c) => new Color(c)), glow, ghost: !!skin.ghost, rainbow: !!skin.rainbow, legs, kind: ch.id, skin }
    }
    // Its colours each frame: the skin's glow and rainbow, and a super nut's
    // rainbow or poison's green over the top
    const styleRig = (rig, sq) => {
      rig.mats.forEach((m, i) => {
        m.color.copy(rig.colours[i])
        if (rig.rainbow) m.color.setHSL((clock * 0.35 + i * 0.25) % 1, 0.85, 0.58)
        if (sq && sq.superFor > 0) m.color.setHSL((clock * 0.8 + i * 0.2) % 1, 0.9, 0.6)
        if (sq && sq.poisonFor > 0) m.color.lerp(POISON_GREEN, 0.5)
        if (m.emissive) {
          m.emissive.copy(m.color)
          m.emissiveIntensity = Math.max(rig.glow[i] || 0, rig.rainbow ? 0.3 : 0, sq && sq.superFor > 0 ? 0.45 : 0)
        }
        if (rig.ghost) m.opacity = 0.55 + 0.1 * Math.sin(clock * 3)
      })
    }
    // Its legs on the move, the way each creature walks
    const stepRig = (rig, moving, t) => {
      rig.legs.forEach((leg, i) => {
        const pair = i === 0 || i === 3 ? 0 : Math.PI
        if (rig.kind === 'spider' || rig.kind === 'scorpion') leg.rotation.y = Math.sin(t + (i % 2) * Math.PI) * 0.45 * moving
        else leg.rotation.z = Math.sin(t + pair) * 0.65 * moving
      })
    }

    // Playing as a creature: it stands in for the squirrel's body in the woods
    // (the flat view on the page keeps the squirrel)
    const charGroup = new Group()
    squirrelGroup.add(charGroup)
    let charRig = null
    const applyCharacter = () => {
      if (charRig) {
        charGroup.remove(charRig.group)
        charRig.mats.forEach((m) => m.dispose())
        charRig = null
      }
      if (shop.char === 'squirrel') return
      charRig = charModel(shop.char, skinOf())
      charGroup.add(charRig.group)
    }
    const applyLook = () => {
      applySkin()
      applyCharacter()
    }
    applyCharacter()

    // Every creature, a pool of each kind; only the current biome's come out
    const spiders = []
    Object.keys(ENEMY_KINDS).forEach((kind) => {
      for (let i = 0; i < Math.ceil(ENEMY_KINDS[kind].night * MAX_CREATURES); i++) {
        const en = makeEnemy(kind)
        en.index = i
        spiders.push(en)
      }
    })

    // ----- Biomes -----
    // Big cells around where the run began, each with its own biome. The
    // starting cell is centred on the start, so it's a long walk to the next.
    const biomeCells = new Map()
    const biomeOfCell = (i, j) => {
      const k = `${i},${j}`
      if (!biomeCells.has(k)) {
        if (biomeCells.size > 5000) biomeCells.clear()
        biomeCells.set(k, growBiomeCell(i, j))
      }
      return biomeCells.get(k)
    }
    // Phone screens are too dim for the Thunderstorm biome, so it's left out there
    const biomeKinds = touchOnly ? BIOME_KINDS.filter((k) => k !== 'storm') : BIOME_KINDS
    const growBiomeCell = (i, j) => {
      const random = seeded(Math.imul(world.seed ^ 0x5bd1e995, 31) ^ Math.imul(i, 73856093) ^ Math.imul(j, 19349663))
      const jitter = [(random() - 0.5) * 0.5 * BIOME_CELL, (random() - 0.5) * 0.5 * BIOME_CELL]
      const kind = biomeKinds[Math.floor(random() * biomeKinds.length)]
      if (i === 0 && j === 0) return { x: 0, z: 0, kind: world.startBiome }
      return { x: i * BIOME_CELL + jitter[0], z: j * BIOME_CELL + jitter[1], kind }
    }
    // Biome at a point in generation coordinates
    const biomeAt = (gx, gz) => {
      const lx = gx - world.biomeStart.x
      const lz = gz - world.biomeStart.z
      const ci = Math.round(lx / BIOME_CELL)
      const cj = Math.round(lz / BIOME_CELL)
      let best = null
      let bestD = Infinity
      for (let i = ci - 1; i <= ci + 1; i++) {
        for (let j = cj - 1; j <= cj + 1; j++) {
          const cell = biomeOfCell(i, j)
          const d = Math.hypot(cell.x - lx, cell.z - lz)
          if (d < bestD) {
            bestD = d
            best = cell.kind
          }
        }
      }
      return best
    }
    // How deep into the frozen peaks a point is: 0 outside, rising to 1 well
    // inside, so the mountains climb up from the biome's edge
    const snowDepth = (gx, gz) => {
      const lx = gx - world.biomeStart.x
      const lz = gz - world.biomeStart.z
      const ci = Math.round(lx / BIOME_CELL)
      const cj = Math.round(lz / BIOME_CELL)
      let d1 = Infinity
      let d2 = Infinity
      let kind = null
      for (let i = ci - 1; i <= ci + 1; i++) {
        for (let j = cj - 1; j <= cj + 1; j++) {
          const cell = biomeOfCell(i, j)
          const d = Math.hypot(cell.x - lx, cell.z - lz)
          if (d < d1) {
            d2 = d1
            d1 = d
            kind = cell.kind
          } else if (d < d2) d2 = d
        }
      }
      return kind === 'snow' ? clamp((d2 - d1) / 2400, 0, 1) : 0
    }
    let biome = 'meadow' // where the squirrel is now
    // Weather that rolls in wherever the squirrel is: after a while of clear
    // skies, rain or a thunderstorm passes over, then clears again
    let weather = 'clear'
    let weatherFor = rand(60, 110)
    const updateWeather = (dt) => {
      if (!world.staged || world.paused) return
      weatherFor -= dt
      if (weatherFor > 0) return
      if (weather === 'clear') {
        weather = Math.random() < 0.45 ? 'storm' : 'rain'
        weatherFor = rand(35, 70)
      } else {
        weather = 'clear'
        weatherFor = rand(60, 120)
      }
    }
    // What the sky's doing: a rainy or stormy biome, or weather passing over
    // In the frozen peaks a blizzard blows up now and then: thick, driving
    // snow, a strong wind, and the view closing in
    let blizzard = 0
    let blizzardFor = rand(25, 50)
    let blizzardOn = false
    const updateBlizzard = (dt) => {
      const here = world.staged && biome === 'snow' && !under
      if (here && !world.paused) {
        blizzardFor -= dt
        if (blizzardFor <= 0) {
          blizzardOn = !blizzardOn
          blizzardFor = blizzardOn ? rand(14, 28) : rand(30, 60)
        }
      }
      blizzard = clamp(blizzard + (here && blizzardOn ? dt / 4 : -dt / 3), 0, 1)
    }
    const wetness = () => {
      if (!world.staged || biome === 'snow' || under) return 'clear'
      if (biome === 'storm' || weather === 'storm') return 'storm'
      if (biome === 'rain' || weather === 'rain') return 'rain'
      return 'clear'
    }
    // The look of the weather, eased towards the current biome's
    const atmo = {
      sky: new Color(BIOMES.meadow.sky),
      skyMix: 0,
      light: 1,
      fog: 1,
      rain: 0,
      storm: 0,
      ground: new Color(BIOMES.meadow.ground),
    }
    const biomeSky = {}
    const biomeGround = {}
    const biomeGrass = {}
    const biomeLitter = {}
    BIOME_KINDS.forEach((k) => {
      biomeSky[k] = new Color(BIOMES[k].sky)
      biomeGround[k] = new Color(BIOMES[k].ground)
      biomeGrass[k] = BIOMES[k].grass.map((c) => new Color(c))
      biomeLitter[k] = FLORA[k].litter.map((c) => new Color(c))
    })
    // ----- Seasons -----
    // A long run turns through the year, three minutes a season, starting in
    // autumn: leaves frost over and snow lies in winter (rain falls as snow),
    // blossom-fresh greens in spring, deep greens in summer, then autumn's
    // colours again. Nuts are plentiful in autumn and scarce in winter.
    const SEASONS = ['autumn', 'winter', 'spring', 'summer']
    const SEASON_LENGTH = 180
    const SEASON_LEAVES = { autumn: null, winter: ['#dfe7ec', 0.65], spring: ['#8fd05a', 0.6], summer: ['#3f8f3a', 0.65] }
    const SEASON_NUTS = { autumn: 1.3, winter: 0.5, spring: 0.85, summer: 1 }
    const SEASONAL = ['meadow', 'fog', 'rain', 'storm']
    let seasonClock = 0
    const seasonState = () => {
      const t = seasonClock % (SEASON_LENGTH * 4)
      const i = Math.floor(t / SEASON_LENGTH)
      // Eases into the next over the last 20 seconds
      const mix = clamp((t - i * SEASON_LENGTH - (SEASON_LENGTH - 20)) / 20, 0, 1)
      return { now: SEASONS[i], next: SEASONS[(i + 1) % 4], mix }
    }
    const seasonWeight = (name) => {
      const st = seasonState()
      return (st.now === name ? 1 - st.mix : 0) + (st.next === name ? st.mix : 0)
    }
    const snowColour = new Color('#e9eef2')
    let winterNow = 0
    const leafTint = new Color()
    let leafTimer = 0
    const updateSeasons = (dt) => {
      if (world.staged && !world.paused && view.p > 0.5) seasonClock += dt
      // Seasons turn quietly: no card names them
      const here = SEASONAL.includes(biome) ? 1 : 0
      const winter = seasonWeight('winter') * here * (view.p > 0.5 ? 1 : 0)
      // Snow lying on the ground, grass and hills
      winterNow = Math.max(winter, biome === 'snow' && view.p > 0.5 ? 0.6 : 0)
      grassMat.emissive.setScalar(0.32 * winterNow)
      terrainMat.emissive.setScalar(0.5 * winterNow)
      // Leaf colours, a few times a second
      leafTimer -= dt
      if (leafTimer > 0) return
      leafTimer = 0.25
      trees.forEach((t) => {
        if (!t.leafBase) t.leafBase = [t.mats[1].color.clone(), t.mats[2].color.clone()]
        const seasonal = SEASONAL.includes(t.biome) && !NOT_TREES.includes(t.kind)
        ;[1, 2].forEach((j) => {
          const c = t.mats[j].color.copy(t.leafBase[j - 1])
          if (!seasonal) return
          SEASONS.forEach((name) => {
            const look = SEASON_LEAVES[name]
            const w = seasonWeight(name)
            if (look && w > 0) c.lerp(leafTint.set(look[0]), look[1] * w)
          })
        })
      })
    }

    const startRun = (kind) => {
      const s = world.squirrel
      seasonClock = 0
      world.startBiome = kind
      biomeCells.clear()
      world.biomeStart = { x: (s ? s.pos.x : 0) + origin.x, z: (s ? s.pos.z : 0) + origin.z }
      biome = kind
      weather = 'clear'
      weatherFor = rand(60, 110)
    }

    // Rain: streaks falling around the squirrel
    const RAIN_DROPS = 700
    const rainMat = new MeshBasicMaterial({ color: '#c3d2e2', transparent: true, opacity: 0.55 })
    const rainMesh = new InstancedMesh(keep(new BoxGeometry(0.35, 11, 0.35)), rainMat, RAIN_DROPS)
    rainMesh.frustumCulled = false
    rainMesh.count = 0
    woods.add(rainMesh)
    const drops = Array.from({ length: RAIN_DROPS }, () => new Vector3(rand(-380, 380), rand(0, 320), rand(-420, 260)))
    const rainQuat = new Quaternion().setFromEuler(new Euler(0, 0, 0.12))
    const rainScale = new Vector3(1, 1, 1)
    const rainAt = new Vector3()
    const updateRain = (dt) => {
      const s = world.squirrel
      // The frozen peaks always have a few flakes falling
      const frozen = world.staged && biome === 'snow'
      const amount = view.p > 0.5 && !under ? Math.max(clamp(atmo.rain / 1.6, 0, 1), frozen ? 0.2 + 0.8 * blizzard : 0) : 0
      rainMesh.count = Math.round(RAIN_DROPS * amount * gv('particles'))
      if (!rainMesh.count) return
      // In winter it snows: big slow flakes that drift
      const snow = frozen ? 1 : SEASONAL.includes(biome) ? seasonWeight('winter') : 0
      rainMat.color.set(snow > 0.5 ? '#ffffff' : '#c3d2e2')
      rainScale.set(snow > 0.5 ? 5 : 1, snow > 0.5 ? 0.2 : 1, snow > 0.5 ? 5 : 1)
      const fall = (420 + atmo.rain * 160) * dt * (snow > 0.5 ? 0.22 : 1)
      for (let i = 0; i < rainMesh.count; i++) {
        const d = drops[i]
        d.y -= fall
        d.x -= fall * 0.12 + (snow > 0.5 ? Math.sin(clock + i) * dt * 12 : 0)
        // Driven sideways in a blizzard
        if (blizzard > 0) {
          d.x += wind.x * 3 * dt
          d.z += wind.z * 3 * dt
          d.y += fall * 0.5 * blizzard
          if (Math.abs(d.x) > 400) d.x = -Math.sign(d.x) * 380
          if (d.z > 280 || d.z < -440) d.z = d.z > 0 ? -420 : 260
        }
        if (d.y < 0) {
          d.set(rand(-380, 380), rand(260, 340), rand(-420, 260))
        }
        rainAt.set(s.pos.x + d.x, d.y, s.pos.z + d.z)
        bugMatrix.compose(rainAt, rainQuat, rainScale)
        rainMesh.setMatrixAt(i, bugMatrix)
      }
      rainMesh.instanceMatrix.needsUpdate = true
    }

    // Storms: lightning flashes, a jagged bolt far off, then thunder
    const boltMat = new LineBasicMaterial({ color: '#f4f7ff', fog: false, transparent: true })
    const bolt = new Line(keep(new BufferGeometry()), boltMat)
    bolt.frustumCulled = false
    bolt.visible = false
    woods.add(bolt)
    let lightningIn = 6
    let flash = 0
    const updateLightning = (dt) => {
      flash = Math.max(0, flash - dt * 2.5)
      bolt.visible = flash > 0.35 && view.p > 0.5 && !under
      boltMat.opacity = clamp(flash, 0, 1)
      if (under) flash = 0
      if (!world.staged || view.p < 0.5 || atmo.storm < 0.5 || world.paused || under) return
      lightningIn -= dt
      if (lightningIn > 0) return
      lightningIn = rand(4, 11)
      flash = 1
      const s = world.squirrel
      let base = new Vector3(s.pos.x + rand(-700, 700), 0, s.pos.z - rand(700, 1300))
      // Now and then it hits a creature nearby, leaving it charged and glowing
      const nearby = spiders.filter((sp) => sp.alive && !sp.leaving && flat(sp.pos.clone().sub(s.pos)) < 420)
      if (nearby.length && Math.random() < 0.55) {
        const struck = nearby[Math.floor(Math.random() * nearby.length)]
        struck.chargedFor = CHARGED_TIME
        base = struck.pos.clone()
        sound.play('zap')
      }
      const points = []
      let x = base.x
      for (let y = 700; y >= 0; y -= 70) {
        points.push(x, y, base.z)
        x += rand(-45, 45)
      }
      bolt.geometry.setAttribute('position', new Float32BufferAttribute(points, 3))
      sound.play('thunder', rand(0.3, 1.4))
    }

    // ----- Endless woods -----
    // Each chunk grows from the run's seed and its coordinates, so walking back
    // over old ground grows the same trees. Far chunks fade and are dropped.
    const grassGeo = keep(new BoxGeometry(VOXEL, 1, VOXEL).translate(0, 0.5, 0))
    // Late-season grass with fallen leaves among it
    const greens = ['#6d7f2e', '#7f8d34', '#5c6e28', '#8a8438'].map((c) => new Color(c))
    const chunks = new Map()
    let trees = []
    let mushrooms = []
    let waters = [] // pools, in world coordinates
    let caves = [] // cave mouths
    let falls = [] // waterfalls
    let peaks = [] // lookout peaks
    let holes = [] // sinkholes down into the caves
    // Underground: the cave system the squirrel's in, or null up top
    let under = null
    // Where generation coordinates sit in the world; moved by recentre()
    const origin = { x: 0, z: 0 }

    const chunkSeed = (cx, cz) =>
      Math.imul(world.seed, 73856093) ^ Math.imul(cx, 19349663) ^ Math.imul(cz, 83492791)

    // ----- Terrain -----
    // Smooth value noise from the run's seed, in generation coordinates
    const hash2 = (ix, iz) => {
      let h = Math.imul(ix, 374761393) ^ Math.imul(iz, 668265263) ^ Math.imul(world.seed | 0, 1442695041)
      h = Math.imul(h ^ (h >>> 13), 1274126177)
      return ((h ^ (h >>> 16)) >>> 0) / 4294967296
    }
    const noise2 = (x, z) => {
      const ix = Math.floor(x)
      const iz = Math.floor(z)
      const fx = x - ix
      const fz = z - iz
      const u = fx * fx * (3 - 2 * fx)
      const v = fz * fz * (3 - 2 * fz)
      const a = hash2(ix, iz)
      const b = hash2(ix + 1, iz)
      const c = hash2(ix, iz + 1)
      const d = hash2(ix + 1, iz + 1)
      return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v
    }
    // How hilly a stretch of country is: open ground in places, hill country
    // in others (where valleys between the hills hold water)
    // Flat where the run starts
    const startFade = (gx, gz) => {
      const start = world.biomeStart || { x: 0, z: 0 }
      return clamp((Math.hypot(gx - start.x, gz - start.z) - 240) / 360, 0, 1)
    }
    const hilliness = (gx, gz) => startFade(gx, gz) * clamp((noise2(gx / 1500 + 11.3, gz / 1500 + 7.1) - 0.38) / 0.3, 0, 1)
    // Height of the ground at a generation point: whole blocks, by column
    const terrainCache = new Map()
    const rawHeight = (cx, cz) => {
      const x = (cx + 0.5) * TERRAIN_CELL
      const z = (cz + 0.5) * TERRAIN_CELL
      const mask = hilliness(x, z)
      // The frozen peaks rise into mountains many times the hills' height,
      // ridged, with valleys between
      const frozen = snowDepth(x, z) * startFade(x, z)
      if (mask <= 0 && frozen <= 0) return 0
      const n = noise2(x / 420, z / 420) * 0.65 + noise2(x / 150 + 5.7, z / 150 + 9.2) * 0.35
      const hill = Math.pow(clamp((n - 0.42) / 0.45, 0, 1), 1.3)
      let steps = Math.floor(mask * hill * TERRAIN_STEPS + 0.2)
      if (frozen > 0) {
        const ridge = 1 - Math.abs(noise2(x / 900 + 1.7, z / 900 + 4.4) * 2 - 1)
        const m = Math.pow(clamp((noise2(x / 520 + 9.1, z / 520 + 2.3) * 0.6 + ridge * 0.6 - 0.4) / 0.65, 0, 1), 1.5)
        steps = Math.max(steps, Math.floor(clamp(frozen * 1.6, 0, 1) * m * MOUNTAIN_STEPS + 0.3))
      }
      // Craggy country climbs in cliffs three blocks tall (a jump each)
      // rather than one block at a time
      const crag = noise2(x / 700 + 3.3, z / 700 + 8.1) > 0.56
      return (crag ? Math.floor(steps / 3) * 3 : steps) * TERRAIN_STEP
    }
    // Caves: here and there a hillside has a cave three columns wide and three
    // deep cut into its foot, roofed over by the hill (built up into a rocky
    // brow where the hill is low). One chance per 8x8 block of columns, all
    // from the seed.
    const CAVE_REGION = 8
    const CAVE_HEAD = 30 // headroom inside
    const caveCache = new Map()
    const caveIn = (rx, rz) => {
      const k = rx * 100003 + rz
      if (caveCache.has(k)) return caveCache.get(k)
      let cave = null
      if (hash2(rx * 7 + 3, rz * 13 + 5) < 0.4) {
        const ax = rx * CAVE_REGION + 2 + Math.floor(hash2(rx, rz * 3 + 1) * 4)
        const az = rz * CAVE_REGION + 2 + Math.floor(hash2(rx * 5 + 2, rz) * 4)
        const dirs = [[0, 1], [0, -1], [1, 0], [-1, 0]]
        const first = Math.floor(hash2(rx + 9, rz + 4) * 4)
        for (let n = 0; n < 4 && !cave; n++) {
          const [dx, dz] = dirs[(first + n) % 4]
          const lx = dz
          const lz = dx
          const floor = rawHeight(ax + dx, az + dz)
          const front = [-1, 0, 1].map((w) => [ax + lx * w + dx, az + lz * w + dz])
          if (front.some(([x, z]) => rawHeight(x, z) > floor)) continue
          const cells = []
          for (let d = 0; d < 3; d++) for (let w = -1; w <= 1; w++) cells.push([ax + lx * w - dx * d, az + lz * w - dz * d])
          // Into the hill: every column higher than the ground outside
          if (!cells.every(([x, z]) => rawHeight(x, z) >= floor + TERRAIN_STEP)) continue
          const roof = (x, z) => Math.max(rawHeight(x, z), floor + CAVE_HEAD + TERRAIN_STEP)
          cave = { floor, dir: [dx, dz], front, cells: cells.map(([x, z]) => [x, z, roof(x, z)]) }
          // The middle of its mouth, in generation coordinates
          cave.gx = (ax + 0.5 + dx * 0.5) * TERRAIN_CELL
          cave.gz = (az + 0.5 + dz * 0.5) * TERRAIN_CELL
        }
      }
      caveCache.set(k, cave)
      return cave
    }
    // Cave systems, planned cheaply from the seed: their caverns, the hillside
    // mouths that lead in (two at most) and the sinkholes open to the sky
    const underPlans = new Map()
    // What's left in each cave the squirrel has been down (its nuts and its
    // prize), kept until it wanders far enough off that the woods round the
    // ways in are dropped. Only then does the cave start afresh.
    const caveMemory = new Map()
    const underPlan = (ux, uz) => {
      const k = `${ux},${uz}`
      if (underPlans.has(k)) return underPlans.get(k)
      let plan = null
      if (world.staged && hash2(ux * 31 + 7, uz * 17 + 3) < UNDER_CHANCE) {
        const random = seeded(chunkSeed(ux * 977 + 13, uz * 631 + 7))
        const N = UNDER_REGION
        const x0 = ux * N
        const z0 = uz * N
        const rooms = []
        const want = 4 + Math.floor(random() * 3)
        for (let tries = 0; rooms.length < want && tries < 80; tries++) {
          const r = 6 + random() * 5
          const ci = 14 + random() * (N - 28)
          const cj = 14 + random() * (N - 28)
          if (rooms.every((o) => Math.hypot(o.ci - ci, o.cj - cj) > o.r + r + 10)) rooms.push({ ci, cj, r })
        }
        const per = N / CAVE_REGION
        const mouths = []
        for (let a = 1; a < per - 1; a++) {
          for (let b = 1; b < per - 1; b++) {
            const cave = caveIn(ux * per + a, uz * per + b)
            if (cave) mouths.push({ rx: ux * per + a, rz: uz * per + b, cave, order: hash2(a * 3 + ux, b * 5 + uz) })
          }
        }
        mouths.sort((m1, m2) => m1.order - m2.order)
        mouths.length = Math.min(mouths.length, 2)
        // Sinkholes, on level ground where there is some: always two ways in at least
        const flatAt = (room) => {
          const ci = x0 + Math.floor(room.ci)
          const cj = z0 + Math.floor(room.cj)
          const h = rawHeight(ci, cj)
          for (let i = -2; i <= 2; i++) for (let j = -2; j <= 2; j++) if (rawHeight(ci + i, cj + j) !== h) return false
          return true
        }
        const wantHoles = mouths.length ? 1 : 2
        const holeRooms = rooms.filter(flatAt).slice(0, wantHoles)
        rooms.forEach((room) => {
          if (holeRooms.length < wantHoles && !holeRooms.includes(room)) holeRooms.push(room)
        })
        const centre = [(x0 + N / 2) * TERRAIN_CELL, (z0 + N / 2) * TERRAIN_CELL]
        const kinds = BIOME_CAVES[biomeAt(centre[0], centre[1])] || BIOME_CAVES.meadow
        const type = CAVE_TYPES[noise2(ux * 0.37 + 5.1, uz * 0.37 + 2.9) > 0.55 ? kinds[1] : kinds[0]]
        plan = { ux, uz, x0, z0, rooms, mouths, holeRooms, type, id: k }
        plan.holes = holeRooms.map((room) => ({
          gx: (x0 + Math.floor(room.ci) + 0.5) * TERRAIN_CELL,
          gz: (z0 + Math.floor(room.cj) + 0.5) * TERRAIN_CELL,
          room,
        }))
      }
      if (underPlans.size > 400) underPlans.clear()
      underPlans.set(k, plan)
      return plan
    }
    const planOf = (cx, cz) => underPlan(Math.floor(cx / UNDER_REGION), Math.floor(cz / UNDER_REGION))
    // A hillside cave, if one of its system's mouths is in this block of columns
    const mouthIn = (rx, rz) => {
      const per = UNDER_REGION / CAVE_REGION
      const plan = underPlan(Math.floor(rx / per), Math.floor(rz / per))
      const m = plan && plan.mouths.find((mo) => mo.rx === rx && mo.rz === rz)
      return m ? m.cave : null
    }
    // The cave (and roof height) a column belongs to, if any
    const caveCell = (cx, cz) => {
      if (!world.staged) return null
      const cave = mouthIn(Math.floor(cx / CAVE_REGION), Math.floor(cz / CAVE_REGION))
      if (!cave) return null
      const cell = cave.cells.find(([x, z]) => x === cx && z === cz)
      return cell ? { cave, roof: cell[2] } : null
    }
    const terrainHeight = (gx, gz) => {
      if (!world.staged) return 0
      const cx = Math.floor(gx / TERRAIN_CELL)
      const cz = Math.floor(gz / TERRAIN_CELL)
      const k = cx * 100003 + cz
      const cached = terrainCache.get(k)
      if (cached !== undefined) return cached
      const inCave = caveCell(cx, cz)
      const h = inCave ? inCave.cave.floor : rawHeight(cx, cz)
      if (terrainCache.size > 400000) terrainCache.clear()
      terrainCache.set(k, h)
      return h
    }
    // Cave walls: in and out only through the mouth
    const cellOf = (x, z) => [Math.floor((x + origin.x) / TERRAIN_CELL), Math.floor((z + origin.z) / TERRAIN_CELL)]
    const caveWall = (x0, z0, x1, z1) => {
      if (under) return false
      const [ax, az] = cellOf(x0, z0)
      const [bx, bz] = cellOf(x1, z1)
      if (ax === bx && az === bz) return false
      const a = caveCell(ax, az)
      const b = caveCell(bx, bz)
      if (!a === !b) return false
      const inside = a || b
      const [ox, oz] = a ? [bx, bz] : [ax, az]
      return !inside.cave.front.some(([x, z]) => x === ox && z === oz)
    }
    // A cave roof over a world point: [underside, top], or null
    const roofAt = (x, z) => {
      if (under) return null
      const inCave = caveCell(Math.floor((x + origin.x) / TERRAIN_CELL), Math.floor((z + origin.z) / TERRAIN_CELL))
      return inCave ? [inCave.cave.floor + CAVE_HEAD, inCave.roof] : null
    }
    // Underground: what's at a world point: 0 rock, 1 floor, 2 water, 3 lava
    const underCellAt = (x, z) => {
      const i = Math.floor((x + origin.x) / TERRAIN_CELL) - under.x0
      const j = Math.floor((z + origin.z) / TERRAIN_CELL) - under.z0
      if (i < 0 || j < 0 || i >= UNDER_REGION || j >= UNDER_REGION) return 0
      return under.grid[i * UNDER_REGION + j]
    }
    // The ground under a world point (0 in the flat view); underground, the
    // cave floor, or the top of the rock
    const floorAt = (x, z) => (under ? (underCellAt(x, z) ? 0 : UNDER_WALL) : terrainHeight(x + origin.x, z + origin.z))
    // The blocks themselves: a top on every raised column, and a side down
    // to each lower neighbour, banded every block
    const terrainMat = new MeshLambertMaterial({ vertexColors: true })
    const SNOWY = ['fog', 'storm', 'snow']
    const SNOW_LINE = 60
    // On a snowy peak: slippery underfoot
    const onSnow = (s) =>
      (under ? s.grounded && !!under.type.slippery : false) ||
      (s.grounded && floorAt(s.pos.x, s.pos.z) >= SNOW_LINE && SNOWY.includes(biomeAt(s.pos.x + origin.x, s.pos.z + origin.z)))
    const TERRAIN_LOOK = {
      meadow: ['#6f8a3a', '#7a5a3a', '#6a4c30'],
      fog: ['#6b7568', '#6e6e68', '#5e5e58'],
      rain: ['#4e7f34', '#5e4a32', '#4f3e2a'],
      storm: ['#3e4f30', '#4a4a48', '#3c3c3a'],
      jungle: ['#3f8a3a', '#5a4630', '#4a3a28'],
      desert: ['#d9b878', '#c48a52', '#a8683a'],
      tropical: ['#e3cf96', '#c9b27a', '#b39a64'],
      snow: ['#eef3f6', '#8e9aa4', '#7c8892'],
    }
    const buildTerrain = (gx, gz, biomeKind) => {
      const cells = Math.ceil(CHUNK / TERRAIN_CELL)
      const [topHex, sideHex, bandHex] = TERRAIN_LOOK[biomeKind] || TERRAIN_LOOK.meadow
      const snowy = SNOWY.includes(biomeKind)
      const top = new Color(topHex)
      const side = new Color(sideHex)
      const band = new Color(bandHex)
      const pos = []
      const nor = []
      const col = []
      const quad = (a, b, c, d, n, colour) => {
        ;[a, b, c, a, c, d].forEach((v) => {
          pos.push(v[0], v[1], v[2])
          nor.push(n[0], n[1], n[2])
          col.push(colour.r, colour.g, colour.b)
        })
      }
      const tint = new Color()
      const heightOf = (i, j) => terrainHeight(gx + (i + 0.5) * TERRAIN_CELL, gz + (j + 0.5) * TERRAIN_CELL)
      const caveOf = (i, j) => caveCell(Math.floor(gx / TERRAIN_CELL) + i, Math.floor(gz / TERRAIN_CELL) + j)
      const gloom = new Color('#2a221c')
      const pitch = new Color('#050403')
      for (let i = 0; i < cells; i++) {
        for (let j = 0; j < cells; j++) {
          // A cave's roof: the hill carries on over the hollow beneath
          const inCave = caveOf(i, j)
          if (inCave) {
            const low = inCave.cave.floor + CAVE_HEAD
            const high = inCave.roof
            const mouth = (ni, nj) =>
              inCave.cave.front.some(([x, z]) => x === Math.floor(gx / TERRAIN_CELL) + ni && z === Math.floor(gz / TERRAIN_CELL) + nj)
            const x0 = i * TERRAIN_CELL
            const z0 = j * TERRAIN_CELL
            const x1 = x0 + TERRAIN_CELL
            const z1 = z0 + TERRAIN_CELL
            quad([x0, high, z0], [x0, high, z1], [x1, high, z1], [x1, high, z0], [0, 1, 0], top)
            quad([x0, low, z0], [x1, low, z0], [x1, low, z1], [x0, low, z1], [0, -1, 0], gloom)
            // A dark floor, so the hollow reads from outside
            const fl = inCave.cave.floor + 0.3
            quad([x0, fl, z0], [x0, fl, z1], [x1, fl, z1], [x1, fl, z0], [0, 1, 0], gloom)
            ;[
              [i - 1, j, [x0, z1], [x0, z0], [-1, 0, 0]],
              [i + 1, j, [x1, z0], [x1, z1], [1, 0, 0]],
              [i, j - 1, [x0, z0], [x1, z0], [0, 0, -1]],
              [i, j + 1, [x1, z1], [x0, z1], [0, 0, 1]],
            ].forEach(([ni, nj, a, b, n]) => {
              if (caveOf(ni, nj)) return
              // Open at the mouth; walled in down the sides and back, where a
              // dark passage leads on down
              const from = mouth(ni, nj) ? Math.max(low, heightOf(ni, nj)) : Math.max(inCave.cave.floor, heightOf(ni, nj))
              const [dx, dz] = inCave.cave.dir
              const passage = ni - i === -dx && nj - j === -dz && !mouth(ni, nj)
              for (let y = from; y < high; y += TERRAIN_STEP) {
                const y1 = Math.min(high, y + TERRAIN_STEP)
                const dark = passage && y < inCave.cave.floor + CAVE_HEAD
                quad([b[0], y, b[1]], [a[0], y, a[1]], [a[0], y1, a[1]], [b[0], y1, b[1]], n, dark ? pitch : (y / TERRAIN_STEP) % 2 ? band : side)
              }
            })
            continue
          }
          const h = heightOf(i, j)
          if (h <= 0) continue
          const x0 = i * TERRAIN_CELL
          const z0 = j * TERRAIN_CELL
          const x1 = x0 + TERRAIN_CELL
          const z1 = z0 + TERRAIN_CELL
          // Higher tops a touch lighter, so the steps read; snow on the
          // tall peaks in the fog and storms
          if (snowy && h >= SNOW_LINE) tint.set('#eef3f6')
          else tint.copy(top).offsetHSL(0, 0, ((h / TERRAIN_STEP) % 2 ? 0.02 : 0) + h / 2400)
          quad([x0, h, z0], [x0, h, z1], [x1, h, z1], [x1, h, z0], [0, 1, 0], tint)
          const sides = [
            [heightOf(i - 1, j), [x0, z1], [x0, z0], [-1, 0, 0]],
            [heightOf(i + 1, j), [x1, z0], [x1, z1], [1, 0, 0]],
            [heightOf(i, j - 1), [x0, z0], [x1, z0], [0, 0, -1]],
            [heightOf(i, j + 1), [x1, z1], [x0, z1], [0, 0, 1]],
          ]
          sides.forEach(([low, a, b, n]) => {
            for (let y = low; y < h; y += TERRAIN_STEP) {
              const y1 = Math.min(h, y + TERRAIN_STEP)
              // Wound to face outward, so the side isn't culled from the front
              quad([b[0], y, b[1]], [a[0], y, a[1]], [a[0], y1, a[1]], [b[0], y1, b[1]], n, (y / TERRAIN_STEP) % 2 ? band : side)
            }
          })
        }
      }
      if (!pos.length) return null
      const geometry = new BufferGeometry()
      geometry.setAttribute('position', new Float32BufferAttribute(pos, 3))
      geometry.setAttribute('normal', new Float32BufferAttribute(nor, 3))
      geometry.setAttribute('color', new Float32BufferAttribute(col, 3))
      return new Mesh(geometry, terrainMat)
    }

    // Keep the line the squirrel walks in the flat view clear
    const inClearingAt = (x, z) => x > bounds.x0 - 60 && x < bounds.x1 + 60 && z > -130 && z < 90
    const lagoonGeo = keep(new CircleGeometry(1, 28).rotateX(-Math.PI / 2))
    const waterMat = new MeshLambertMaterial({ color: '#2fb2d6', emissive: '#0a4a5e', transparent: true, opacity: 0.85 })
    const sandMat = lambert('#ecd9a0')
    const bankMat = lambert('#6f6a3e')
    const pitMat = new MeshBasicMaterial({ color: '#050403' })
    const rimRockGeo = keep(new BoxGeometry(1, 1, 1))
    const rimMats = {}
    const holeRimMat = (colour) => {
      if (!rimMats[colour]) rimMats[colour] = lambert(colour)
      return rimMats[colour]
    }
    // Merged shapes with their own colours: boxes, quads and soft glows
    const shapes = () => {
      const pos = []
      const nor = []
      const col = []
      const quad = (a, b, c, d, n, colour) => {
        ;[a, b, c, a, c, d].forEach((v) => {
          pos.push(v[0], v[1], v[2])
          nor.push(n[0], n[1], n[2])
          col.push(colour.r, colour.g, colour.b)
        })
      }
      // A wall face from (ax, az) to (bx, bz), facing n, from y0 up to y1
      const wall = (ax, az, bx, bz, y0, y1, n, colour) => quad([bx, y0, bz], [ax, y0, az], [ax, y1, az], [bx, y1, bz], n, colour)
      const cube = (x0, y0, z0, x1, y1, z1, colour) => {
        quad([x0, y1, z0], [x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [0, 1, 0], colour)
        wall(x0, z1, x0, z0, y0, y1, [-1, 0, 0], colour)
        wall(x1, z0, x1, z1, y0, y1, [1, 0, 0], colour)
        wall(x0, z0, x1, z0, y0, y1, [0, 0, -1], colour)
        wall(x1, z1, x0, z1, y0, y1, [0, 0, 1], colour)
      }
      // A flat disc, bright in the middle and fading to the rim
      const fan = (x, y, z, r, inner, outer) => {
        const n = 12
        for (let k = 0; k < n; k++) {
          const a0 = (k / n) * Math.PI * 2
          const a1 = ((k + 1) / n) * Math.PI * 2
          ;[[x, y, z, inner], [x + Math.cos(a1) * r, y, z + Math.sin(a1) * r, outer], [x + Math.cos(a0) * r, y, z + Math.sin(a0) * r, outer]].forEach(([vx, vy, vz, c]) => {
            pos.push(vx, vy, vz)
            nor.push(0, 1, 0)
            col.push(c.r, c.g, c.b)
          })
        }
      }
      const geometry = () => {
        const g = new BufferGeometry()
        g.setAttribute('position', new Float32BufferAttribute(pos, 3))
        g.setAttribute('normal', new Float32BufferAttribute(nor, 3))
        g.setAttribute('color', new Float32BufferAttribute(col, 3))
        return g
      }
      return { quad, wall, cube, fan, geometry, size: () => pos.length }
    }
    const fungusMat = new MeshBasicMaterial({ vertexColors: true })
    const haloMat = new MeshBasicMaterial({ vertexColors: true, transparent: true, blending: AdditiveBlending, depthWrite: false })
    const stemColour = new Color('#d8d2c0')
    const black = new Color('#000000')
    const fallGeo = keep(new PlaneGeometry(1, 1).translate(0, 0.5, 0))
    const fallMat = new MeshBasicMaterial({ color: '#a6e2ff', transparent: true, opacity: 0.82, side: DoubleSide, depthWrite: false })
    const buildChunk = (cx, cz, grown) => {
      const random = seeded(chunkSeed(cx, cz))
      const gx = cx * CHUNK
      const gz = cz * CHUNK
      const group = new Group()
      group.position.set(gx - origin.x, 0, gz - origin.z)
      const chunk = { cx, cz, group, trees: [], mushrooms: [], grass: null, grow: grown ? 1 : 0 }
      const chunkBiome = world.staged ? biomeAt(gx + CHUNK / 2, gz + CHUNK / 2) : 'meadow'
      const grassOf = world.staged ? biomeGrass[chunkBiome] : greens
      const flora = FLORA[chunkBiome]
      // Tropical shores have pools of water ringed with sand
      chunk.water = []
      // Hills, and pools of water in the valleys between them
      chunk.terrain = buildTerrain(gx, gz, chunkBiome)
      if (chunk.terrain) group.add(chunk.terrain)
      const lowAll = (w) =>
        [0, 1, 2, 3, 4, 5, 6, 7, 8].every((k) => {
          const a = (k / 8) * Math.PI * 2
          const r = k === 8 ? 0 : 1.25
          return terrainHeight(gx + w.lx + Math.cos(a) * w.rx * r, gz + w.lz + Math.sin(a) * w.rz * r) === 0
        })
      // Sinkholes down into the caves: a dark pit in a ring of rock
      chunk.holes = []
      const ccx0 = Math.floor(gx / TERRAIN_CELL / UNDER_REGION)
      const ccz0 = Math.floor(gz / TERRAIN_CELL / UNDER_REGION)
      for (let ux = ccx0; ux <= Math.floor((gx + CHUNK - 1) / TERRAIN_CELL / UNDER_REGION); ux++) {
        for (let uz = ccz0; uz <= Math.floor((gz + CHUNK - 1) / TERRAIN_CELL / UNDER_REGION); uz++) {
          const plan = world.staged ? underPlan(ux, uz) : null
          if (!plan) continue
          plan.holes.forEach((hole) => {
            if (hole.gx < gx || hole.gx >= gx + CHUNK || hole.gz < gz || hole.gz >= gz + CHUNK) return
            const lx = hole.gx - gx
            const lz = hole.gz - gz
            const y = terrainHeight(hole.gx, hole.gz)
            const rimMat = holeRimMat(plan.type.wall)
            const rim = new Mesh(lagoonGeo, rimMat)
            rim.position.set(lx, y + 0.2, lz)
            rim.scale.set(21, 1, 21)
            const pit = new Mesh(lagoonGeo, pitMat)
            pit.position.set(lx, y + 0.35, lz)
            pit.scale.set(14, 1, 14)
            group.add(rim, pit)
            for (let k = 0; k < 7; k++) {
              const a = (k / 7) * Math.PI * 2 + random()
              const rock = new Mesh(rimRockGeo, rimMat)
              const size = 4 + random() * 4
              rock.position.set(lx + Math.cos(a) * 19, y + size / 2, lz + Math.sin(a) * 19)
              rock.scale.set(size, size * (0.7 + random() * 0.6), size)
              rock.rotation.y = random() * 2
              group.add(rock)
            }
            chunk.holes.push({ lx, lz, y, plan, hole })
          })
        }
      }
      const nearHole = (x, z, gap) => chunk.holes.some((h) => Math.hypot(h.lx - x, h.lz - z) < gap)
      const valley = hilliness(gx + CHUNK / 2, gz + CHUNK / 2) > 0.45
      if (flora.lagoons || valley) {
        const pools = flora.lagoons ? (random() < 0.65 ? 1 : 2) : random() < 0.7 ? 1 : 0
        for (let i = 0; i < pools; i++) {
          const w = { lx: random() * CHUNK, lz: random() * CHUNK, rx: 45 + random() * 50, rz: 35 + random() * 40 }
          if (inClearingAt(gx + w.lx, gz + w.lz) || !lowAll(w) || nearHole(w.lx, w.lz, Math.max(w.rx, w.rz) + 40)) continue
          chunk.water.push(w)
          // Sandy beaches on the shore, muddy banks elsewhere
          const sand = new Mesh(lagoonGeo, flora.lagoons ? sandMat : bankMat)
          sand.position.set(w.lx, 0.15, w.lz)
          sand.scale.set(w.rx + 22, 1, w.rz + 22)
          const water = new Mesh(lagoonGeo, waterMat)
          water.position.set(w.lx, 0.45, w.lz)
          // A way across: a fallen log, or a line of stepping stones
          w.crossing = random() < 0.5 ? 'log' : 'stones'
          water.scale.set(w.rx, 1, w.rz)
          group.add(sand, water)
        }
      }
      const wet = (x, z, pad = 0) => chunk.water.some((w) => Math.hypot((x - w.lx) / (w.rx + pad), (z - w.lz) / (w.rz + pad)) < 1)
      const litterOf = biomeLitter[chunkBiome]
      // Keep the line the squirrel walks in the flat view clear
      const inClearing = inClearingAt
      const spots = chunk.holes.map((h) => ({ lx: h.lx, lz: h.lz })) // every tree's spot, felled or not, so the layout stays put
      const clear = (x, z, gap) =>
        !inClearing(gx + x, gz + z) &&
        !wet(x, z, 20) &&
        spots.every((t) => Math.hypot(t.lx - x, t.lz - z) > gap) &&
        chunk.mushrooms.every((m) => Math.hypot(m.lx - x, m.lz - z) > gap * 0.6)

      // Some biomes have boulders among the trees: solid, stood on, smashed in three
      const rocks = flora.boulders ? Math.floor(random() * (flora.boulders[0] + 1)) : 0
      const treeCount = 2 + Math.floor(random() * 4) + (flora.extraTrees || 0) + rocks
      for (let i = 0, tries = 0; i < treeCount && tries < 40; tries++) {
        const x = random() * CHUNK
        const z = random() * CHUNK
        if (!clear(x, z, 80)) continue
        const built = buildTree(random, i < rocks ? 'boulder' : flora.trees[Math.floor(random() * flora.trees.length)])
        // Own materials in its own leaf colours, so it can fade when it blocks the camera
        const palette = flora.foliage || FOLIAGE
        const leaves = palette[Math.floor(random() * palette.length)]
        const mats = treeMats.map((m, j) => {
          const c = m.clone()
          if (j === 1 || j === 2) c.color.set(leaves[j - 1])
          if ((j === 10 || j === 11) && flora.boulders) c.color.set(flora.boulders[1][j - 10])
          c.transparent = true
          return c
        })
        const rot = Math.floor(random() * 4) * (Math.PI / 2)
        const id = `${cx},${cz},t${i}`
        spots.push({ lx: x, lz: z })
        i++
        const hits = world.felled.get(id) || 0
        if (hits >= TREE_HITS) {
          built.geometry.dispose()
          mats.forEach((m) => m.dispose())
          continue
        }
        if (hits > 0) {
          built.voxels = damageTree(built.voxels, hits).voxels
          built.geometry.dispose()
          Object.assign(built, meshTree(built.voxels, built.offset))
          // Down to a stump: nothing up there to stand on but its top
          if (hits >= 2 || NOT_TREES.includes(built.kind)) built.canopyY = Math.min(built.canopyY, Math.max(0, ...built.tops.values()))
        }
        // Standing on whatever ground is there, hilltops included
        const baseY = terrainHeight(gx + x, gz + z)
        const mesh = new Mesh(built.geometry, mats)
        mesh.position.set(x, baseY, z)
        mesh.rotation.y = rot
        group.add(mesh)
        chunk.trees.push({ ...built, biome: chunkBiome, canopyY: built.canopyY + baseY, baseY, id, hits, chunk, lx: x, lz: z, x: 0, z: 0, rot, mesh, mats, fade: 1 })
      }

      // Logs and stepping stones across the pools, along the narrower way
      const addSolid = (kind, x, z, rot, size, n) => {
        const built = buildTree(random, kind, size)
        const id = `${cx},${cz},x${n}`
        const hits = world.felled.get(id) || 0
        if (hits >= TREE_HITS) {
          built.geometry.dispose()
          return
        }
        if (hits > 0) {
          built.voxels = damageTree(built.voxels, hits).voxels
          built.geometry.dispose()
          Object.assign(built, meshTree(built.voxels, built.offset))
        }
        const mats = treeMats.map((m) => {
          const c = m.clone()
          c.transparent = true
          return c
        })
        const mesh = new Mesh(built.geometry, mats)
        mesh.position.set(x, 0, z)
        mesh.rotation.y = rot
        group.add(mesh)
        chunk.trees.push({ ...built, baseY: 0, id, hits, chunk, lx: x, lz: z, x: 0, z: 0, rot, mesh, mats, fade: 1 })
      }
      let solids = 0
      chunk.water.forEach((w) => {
        if (!w.crossing) return
        const alongX = w.rx < w.rz
        const half = (alongX ? w.rx : w.rz) + 14
        const rot = alongX ? 0 : Math.PI / 2
        if (w.crossing === 'log') addSolid('log', w.lx, w.lz, rot, Math.ceil((half * 2) / TREE_VOXEL), solids++)
        else {
          for (let d = -half + 6; d <= half - 6; d += 17) {
            addSolid('stone', w.lx + (alongX ? d : random() * 6 - 3), w.lz + (alongX ? random() * 6 - 3 : d), 0, 2, solids++)
          }
        }
      })

      // Waterfalls: a cliff three blocks or more dropping to the valley
      // floor, with a pool at its foot. Now and then a nut tumbles down it.
      chunk.falls = []
      if (valley) {
        const cells = Math.ceil(CHUNK / TERRAIN_CELL)
        // The tallest cliffs first: a drop of two blocks or more
        const at = (i, j) => terrainHeight(gx + (i + 0.5) * TERRAIN_CELL, gz + (j + 0.5) * TERRAIN_CELL)
        const edges = []
        for (let i = 2; i < cells - 2; i++) {
          for (let j = 2; j < cells - 2; j++) {
            const h = at(i, j)
            if (h < 20) continue
            ;[[0, 1], [0, -1], [1, 0], [-1, 0]].forEach(([dx, dz]) => {
              const base = at(i + dx, j + dz)
              if (h - base >= 20 && !caveCell(Math.floor(gx / TERRAIN_CELL) + i + dx, Math.floor(gz / TERRAIN_CELL) + j + dz)) edges.push([i, j, h, base, dx, dz])
            })
          }
        }
        edges.sort((a, b) => b[2] - b[3] - (a[2] - a[3]))
        const edge = edges[Math.floor(random() * Math.min(3, edges.length))]
        if (edge) {
          const [i, j, h, base, dx, dz] = edge
          // The face it pours over, a cell's width
          const lx = (i + 0.5 + dx * 0.5) * TERRAIN_CELL
          const lz = (j + 0.5 + dz * 0.5) * TERRAIN_CELL
          const sheet = new Mesh(fallGeo, fallMat)
          sheet.position.set(lx + dx * 0.4, base, lz + dz * 0.4)
          sheet.rotation.y = dx ? Math.PI / 2 : 0
          sheet.scale.set(TERRAIN_CELL * 0.9, h - base, 1)
          group.add(sheet)
          // A plunge pool at its foot when that's the valley floor
          const pool = { lx: lx + dx * 22, lz: lz + dz * 22, rx: 18, rz: 18 }
          if (base === 0 && lowAll(pool) && !nearHole(pool.lx, pool.lz, 50)) {
            const water = new Mesh(lagoonGeo, waterMat)
            water.position.set(pool.lx, 0.45, pool.lz)
            water.scale.set(pool.rx, 1, pool.rz)
            const bank = new Mesh(lagoonGeo, bankMat)
            bank.position.set(pool.lx, 0.15, pool.lz)
            bank.scale.set(pool.rx + 8, 1, pool.rz + 8)
            group.add(bank, water)
            chunk.water.push(pool)
          }
          chunk.falls.push({ lx, lz, h, base, dx, dz })
        }
      }

      // Caves whose mouths open in this chunk
      chunk.caves = []
      const crx0 = Math.floor(gx / TERRAIN_CELL / CAVE_REGION)
      const crx1 = Math.floor((gx + CHUNK - 1) / TERRAIN_CELL / CAVE_REGION)
      const crz0 = Math.floor(gz / TERRAIN_CELL / CAVE_REGION)
      const crz1 = Math.floor((gz + CHUNK - 1) / TERRAIN_CELL / CAVE_REGION)
      for (let rx = crx0; rx <= crx1; rx++) {
        for (let rz = crz0; rz <= crz1; rz++) {
          const cave = mouthIn(rx, rz)
          if (!cave || cave.gx < gx || cave.gx >= gx + CHUNK || cave.gz < gz || cave.gz >= gz + CHUNK) continue
          chunk.caves.push({ lx: cave.gx - gx, lz: cave.gz - gz, floor: cave.floor, dir: cave.dir })
        }
      }

      // Lookout peaks: the top of a tall hill holds a golden nut, once a run
      chunk.peak = null
      let peakH = 0
      for (let i = 0; i < 32; i += 2) {
        for (let j = 0; j < 32; j += 2) {
          const h = terrainHeight(gx + (i + 0.5) * TERRAIN_CELL, gz + (j + 0.5) * TERRAIN_CELL)
          if (h > peakH) {
            peakH = h
            chunk.peak = { lx: (i + 0.5) * TERRAIN_CELL, lz: (j + 0.5) * TERRAIN_CELL, id: `peak:${cx},${cz}` }
          }
        }
      }
      if (peakH < 90 || random() > (peakH > 150 ? 0.06 : 0.12)) chunk.peak = null

      const shroomCount = random() < 0.35 ? 1 : 0 // roughly one every three chunks
      for (let i = 0, tries = 0; i < shroomCount && tries < 30; tries++) {
        const x = random() * CHUNK
        const z = random() * CHUNK
        if (!clear(x, z, 40)) continue
        const geometry = buildMushroom(random)
        const id = `${cx},${cz},${i}`
        i++
        if (world.eaten.has(id)) {
          geometry.dispose()
          continue
        }
        const mesh = new Mesh(geometry, mushroomMats)
        mesh.position.set(x, terrainHeight(gx + x, gz + z), z)
        group.add(mesh)
        chunk.mushrooms.push({ id, lx: x, lz: z, x: 0, z: 0, mesh, chunk })
      }

      // Flowers, ferns, rocks and the like, all in one mesh
      const decor = new Map()
      flora.plants.forEach(([kind, count, colours]) => {
        for (let i = 0; i < Math.round(count * gv('foliage')); i++) {
          const x = random() * CHUNK
          const z = random() * CHUNK
          const colour = DECOR[colours[Math.floor(random() * colours.length)]]
          if (inClearing(gx + x, gz + z) || wet(x, z, 4) || spots.some((t) => Math.hypot(t.lx - x, t.lz - z) < 16)) continue
          // Stamped on its own, then lifted onto the ground where it stands
          const lift = Math.round(terrainHeight(gx + x, gz + z) / VOXEL)
          if (!lift) stampDecor(decor, kind, Math.round(x / VOXEL), Math.round(z / VOXEL), random, colour)
          else {
            const piece = new Map()
            stampDecor(piece, kind, Math.round(x / VOXEL), Math.round(z / VOXEL), random, colour)
            piece.forEach((v, k) => {
              const [vx, vy, vz] = k.split(',').map(Number)
              decor.set(`${vx},${vy + lift},${vz}`, v)
            })
          }
        }
      })
      if (decor.size) {
        chunk.decor = new Mesh(meshDecor(decor), decorMats)
        chunk.decorVoxels = decor
        group.add(chunk.decor)
      }
      // The thunderstorm's gloom is lit by clumps of glowing fungi, each
      // casting a soft pool of light around it
      if (chunkBiome === 'storm') {
        const caps = shapes()
        const halos = shapes()
        const glowColours = ['#7dffcf', '#b47dff', '#7dc8ff']
        for (let k = 0, tries = 0; k < 4 && tries < 20; tries++) {
          const x = random() * CHUNK
          const z = random() * CHUNK
          if (!clear(x, z, 26)) continue
          k++
          const y = terrainHeight(gx + x, gz + z)
          const colour = new Color(glowColours[Math.floor(random() * glowColours.length)])
          for (let n = 0; n < 3 + Math.floor(random() * 3); n++) {
            const mx = x + (random() - 0.5) * 14
            const mz = z + (random() - 0.5) * 14
            const tall = 2 + random() * 4
            caps.cube(mx - 0.6, y, mz - 0.6, mx + 0.6, y + tall, mz + 0.6, stemColour)
            const w = 1.6 + random() * 1.4
            caps.cube(mx - w, y + tall, mz - w, mx + w, y + tall + 1.4, mz + w, colour)
          }
          halos.fan(x, y + 0.5, z, 34, colour.clone().multiplyScalar(0.45), black)
        }
        if (caps.size()) {
          chunk.glow = [new Mesh(caps.geometry(), fungusMat), new Mesh(halos.geometry(), haloMat)]
          group.add(...chunk.glow)
        }
      }

      const tufts = Math.round(((CHUNK * CHUNK) / 90) * (flora.grassDensity || 1) * gv('foliage'))
      const grass = new InstancedMesh(grassGeo, grassMat, tufts)
      grass.frustumCulled = false
      const m = new Matrix4()
      for (let i = 0; i < tufts; i++) {
        const sx = 0.5 + random() * 0.5
        const sy = 1.5 + random() * 4
        const sz = 0.5 + random() * 0.5
        const px = random() * CHUNK
        const pz = random() * CHUNK
        // None growing in the water
        if ((chunk.water.length && wet(px, pz, 8)) || nearHole(px, pz, 17)) m.makeScale(0.001, 0.001, 0.001)
        else m.makeScale(sx, sy, sz)
        m.setPosition(px, terrainHeight(gx + px, gz + pz), pz)
        grass.setMatrixAt(i, m)
        const pile = random() < 0.15 ? litterOf : grassOf
        grass.setColorAt(i, pile[Math.floor(random() * pile.length)])
      }
      group.add(grass)
      chunk.grass = grass
      woods.add(group)
      return chunk
    }

    const dropChunk = (chunk) => {
      woods.remove(chunk.group)
      chunk.trees.forEach((t) => {
        t.mesh.geometry.dispose()
        t.mats.forEach((m) => m.dispose())
      })
      chunk.mushrooms.forEach((m) => m.mesh.geometry.dispose())
      if (chunk.decor) chunk.decor.geometry.dispose()
      if (chunk.terrain) chunk.terrain.geometry.dispose()
      if (chunk.glow) chunk.glow.forEach((m) => m.geometry.dispose())
      chunk.grass.dispose()
    }

    // World positions of everything solid in the loaded chunks
    const indexChunks = () => {
      trees = []
      waters = []
      mushrooms = []
      caves = []
      falls = []
      peaks = []
      holes = []
      chunks.forEach((c) => {
        const { x, z } = c.group.position
        ;(c.holes || []).forEach((h) => holes.push({ x: x + h.lx, z: z + h.lz, y: h.y, plan: h.plan, hole: h.hole }))
        ;(c.caves || []).forEach((cv) => caves.push({ x: x + cv.lx, z: z + cv.lz, floor: cv.floor, dir: cv.dir }))
        ;(c.falls || []).forEach((f) => falls.push({ ...f, x: x + f.lx, z: z + f.lz }))
        if (c.peak) peaks.push({ x: x + c.peak.lx, z: z + c.peak.lz, id: c.peak.id })
        c.trees.forEach((t) => {
          t.x = x + t.lx
          t.z = z + t.lz
          trees.push(t)
        })
        ;(c.water || []).forEach((w) => waters.push({ x: x + w.lx, z: z + w.lz, rx: w.rx, rz: w.rz }))
        c.mushrooms.forEach((mu) => {
          mu.x = x + mu.lx
          mu.z = z + mu.lz
          mushrooms.push(mu)
        })
      })
      // Underground none of the woods above count, only the cave's own pools
      if (under) {
        trees = []
        mushrooms = []
        caves = []
        falls = []
        peaks = []
        holes = []
        waters = under.pools
      }
    }

    // Load chunks near the squirrel (a couple a frame once playing) and fade out far ones
    const updateChunks = (dt) => {
      const s = world.squirrel
      const gx = s.pos.x + origin.x
      const gz = s.pos.z + origin.z
      const grown = chunks.size === 0
      const reach = Math.ceil(viewRadius() / CHUNK)
      const ccx = Math.floor(gx / CHUNK)
      const ccz = Math.floor(gz / CHUNK)
      const gap = (cx, cz) => Math.hypot((cx + 0.5) * CHUNK - gx, (cz + 0.5) * CHUNK - gz)
      let built = 0
      let changed = false
      const fresh = []
      for (let dx = -reach; dx <= reach; dx++) {
        for (let dz = -reach; dz <= reach; dz++) {
          const cx = ccx + dx
          const cz = ccz + dz
          const k = `${cx},${cz}`
          if (chunks.has(k) || gap(cx, cz) > viewRadius() || (!grown && built >= 2)) continue
          const chunk = buildChunk(cx, cz, grown)
          chunks.set(k, chunk)
          fresh.push(chunk)
          built++
          changed = true
        }
      }
      chunks.forEach((c, k) => {
        const far = gap(c.cx, c.cz) > DROP_RADIUS * gv('view')
        c.grow = clamp(c.grow + (far ? -dt : dt) * 1.5, 0, 1)
        if (far && c.grow === 0) {
          dropChunk(c)
          chunks.delete(k)
          changed = true
        }
      })
      if (changed) indexChunks()
      // Woods grown in again while a boss fight is on are clear inside the ring
      if (fightOn && fresh.length) fresh.forEach((c) => clearArena(c, true))
      if (world.staged && view.p > 0.5 && !under) fresh.forEach(seedNuts)
    }

    // Height of a tree's canopy at a world point, or 0 if the point misses it
    const treeTopAt = (t, x, z) => {
      const dx = x - t.x
      const dz = z - t.z
      const cos = Math.cos(t.rot)
      const sin = Math.sin(t.rot)
      const lx = dx * cos - dz * sin
      const lz = dx * sin + dz * cos
      const vx = Math.floor(lx / TREE_VOXEL - t.offset[0])
      const vz = Math.floor(lz / TREE_VOXEL - t.offset[2])
      const top = t.tops.get(`${vx},${vz}`)
      return top ? top + (t.baseY || 0) : 0
    }

    // What it would stand on at (x, z): the highest treetop no more than a
    // step above its feet, or the forest floor
    const groundAt = (x, z, y) => {
      let best = floorAt(x, z)
      // On top of a cave's roof
      const roof = roofAt(x, z)
      if (roof && y >= roof[0] && roof[1] <= y + STEP_UP) best = roof[1]
      let tree = null
      trees.forEach((t) => {
        const reach = t.canopyRadius + TREE_VOXEL * 2
        if (Math.abs(x - t.x) > reach || Math.abs(z - t.z) > reach) return
        const top = treeTopAt(t, x, z)
        if (top > best && top <= y + STEP) {
          best = top
          tree = t
        }
      })
      return { height: best, tree }
    }

    const clearChunks = () => {
      if (under) leaveUnder()
      terrainCache.clear()
      caveCache.clear()
      underPlans.clear()
      chunks.forEach(dropChunk)
      chunks.clear()
      indexChunks()
    }

    // ----- Sun and moon -----
    // They sweep low across the far horizon, ahead of the camera
    const sunMesh = new Mesh(keep(new BoxGeometry(70, 70, 6)), new MeshBasicMaterial({ color: '#ffd75e', fog: false }))
    const sunGlow = new Mesh(
      keep(new BoxGeometry(110, 110, 2)),
      new MeshBasicMaterial({ color: '#ffe9a8', fog: false, transparent: true, opacity: 0.3 }),
    )
    sunGlow.position.z = -4
    sunMesh.add(sunGlow)
    const moonMesh = new Mesh(keep(new BoxGeometry(50, 50, 6)), new MeshBasicMaterial({ color: '#e9eef7', fog: false }))
    const craterMat = new MeshBasicMaterial({ color: '#c3cad8', fog: false })
    ;[
      [-10, 8, 12],
      [12, -6, 9],
      [4, 14, 6],
    ].forEach(([x, y, size]) => {
      const crater = new Mesh(keep(new BoxGeometry(size, size, 1)), craterMat)
      crater.position.set(x, y, 3.5)
      moonMesh.add(crater)
    })
    woods.add(sunMesh, moonMesh)

    // ----- Critters -----
    // Harmless wildlife kept in a ring around the squirrel: fireflies, beetles,
    // butterflies, bees, dragonflies, birds by day and bats by night, falling
    // leaves, drifting mist and rain splashes. How much of each depends on the
    // biome (WILDLIFE), eased in and out as biomes change.
    const fireflyMat = new MeshBasicMaterial({ color: '#ffffff' })
    const fireflyLit = new Color('#f4ff8a')
    const fireflyDark = new Color('#3a3d24')
    const fireflyGlow = new Color()
    const beetleMat = lambert('#ffffff')
    const butterflyMat = new MeshBasicMaterial({ color: '#ffffff', side: DoubleSide })
    const beeMat = new MeshBasicMaterial({ color: '#ffffff' })
    const dragonflyMat = new MeshBasicMaterial({ color: '#ffffff' })
    const flyerMat = lambert('#ffffff')
    const wingMat = new MeshLambertMaterial({ color: '#ffffff', side: DoubleSide })
    const leafMat = new MeshLambertMaterial({ color: '#ffffff', side: DoubleSide })
    const mistMat = new MeshBasicMaterial({ color: '#e8ecef', transparent: true, opacity: 0, depthWrite: false })
    const splashMat = new MeshBasicMaterial({ color: '#dfe8f2', transparent: true, opacity: 0.7 })
    const swarm = (geometry, material, count) => {
      const mesh = new InstancedMesh(keep(geometry), material, count)
      mesh.frustumCulled = false
      // Colours first: the colour buffer is sized from the count when it's made
      for (let i = 0; i < count; i++) mesh.setColorAt(i, new Color('#ffffff'))
      woods.add(mesh)
      return {
        mesh,
        max: count,
        bugs: Array.from({ length: count }, () => ({ pos: null, phase: rand(0, 100), heading: rand(0, 7) })),
      }
    }
    const fireflies = swarm(new BoxGeometry(1.2, 1.2, 1.2), fireflyMat, 75)
    const beetles = swarm(new BoxGeometry(2.4, 1.2, 1.6).translate(0, 0.6, 0), beetleMat, 40)
    const butterflies = swarm(new PlaneGeometry(3.4, 2.4), butterflyMat, 24)
    const bees = swarm(new BoxGeometry(1, 0.8, 0.8), beeMat, 20)
    const dragonflies = swarm(buildDragonfly(), dragonflyMat, 16)
    const flyers = swarm(new BoxGeometry(2.8, 1.4, 1.4), flyerMat, 24)
    // Two wings per flyer, hinged at the body and reaching out sideways
    const wings = swarm(new PlaneGeometry(2.4, 3.6).rotateX(-Math.PI / 2).translate(0, 0, 2.2), wingMat, 48)
    const leaves = swarm(new PlaneGeometry(2.2, 2.2), leafMat, 90)
    const mist = swarm(new BoxGeometry(46, 10, 46), mistMat, 100)
    const splashes = swarm(new BoxGeometry(2.4, 0.6, 2.4), splashMat, 60)
    const beetleColours = ['#2b2420', '#3a3a2a', '#1f2a1f', '#b8322a'].map((c) => new Color(c))
    const beeColours = ['#f5c400', '#2a2216'].map((c) => new Color(c))
    const dragonflyColours = ['#2fd0ff', '#3aff8a', '#ff4a6a', '#4a6aff'].map((c) => new Color(c))
    const batColour = new Color('#2a2430')
    fireflies.bugs.forEach((b, i) => fireflies.mesh.setColorAt(i, fireflyLit))
    beetles.bugs.forEach((b, i) => beetles.mesh.setColorAt(i, beetleColours[i % beetleColours.length]))
    bees.bugs.forEach((b, i) => bees.mesh.setColorAt(i, beeColours[i % 2]))
    dragonflies.bugs.forEach((b, i) => dragonflies.mesh.setColorAt(i, dragonflyColours[i % dragonflyColours.length]))
    const wildColours = {}
    BIOME_KINDS.forEach((k) => {
      const w = WILDLIFE[k]
      wildColours[k] = {
        birds: w.birdColours.map((c) => new Color(c)),
        wings: w.wingColours.map((c) => new Color(c)),
        leaves: w.leafColours.map((c) => new Color(c)),
      }
    })
    // How much of each kind is about, eased towards the biome's amounts
    const wild = { ...WILDLIFE.meadow }
    const WILD_KEYS = ['fireflies', 'beetles', 'butterflies', 'bees', 'dragonflies', 'birds', 'bats', 'leaves', 'mist', 'splashes']

    const bugMatrix = new Matrix4()
    const bugQuat = new Quaternion()
    const bugEuler = new Euler(0, 0, 0, 'YXZ')
    const bugScale = new Vector3()
    const unit = new Vector3(1, 1, 1)
    let clock = 0

    const updateCritters = (dt, day) => {
      clock += dt
      const s = world.squirrel
      const { e } = view
      const here = world.staged ? biome : 'meadow'
      const colours = wildColours[here]
      const settle = 1 - Math.exp(-0.6 * dt)
      const wetNow = wetness()
      WILD_KEYS.forEach((k) => {
        let want = WILDLIFE[here][k]
        if (wetNow !== 'clear') {
          if (k === 'splashes') want = Math.max(want, WILDLIFE[wetNow].splashes)
          if (k === 'butterflies' || k === 'bees') want *= 0.15
          if (k === 'leaves' && wetNow === 'storm') want = Math.max(want, 1.2)
        }
        wild[k] = lerp(wild[k], want, settle)
      })
      const pick = (list) => list[Math.floor(Math.random() * list.length)]
      // Puts a critter somewhere new near the squirrel once it's left behind;
      // says whether it moved, so it can be recoloured for the biome
      const keepNear = (b, height, reach = 380) => {
        if (b.pos && flat(b.pos.clone().sub(s.pos)) < reach) return false
        const a = rand(0, Math.PI * 2)
        const r = b.pos ? rand(reach * 0.55, reach * 0.95) : rand(30, reach * 0.95)
        b.pos = new Vector3(s.pos.x + Math.cos(a) * r, height, s.pos.z + Math.sin(a) * r)
        return true
      }
      const place = (swarmOf, i, b, scale, pitch = 0, roll = 0) => {
        bugEuler.set(pitch, b.heading, roll)
        bugQuat.setFromEuler(bugEuler)
        bugScale.copy(scale)
        bugMatrix.compose(b.pos, bugQuat, bugScale)
        swarmOf.mesh.setMatrixAt(i, bugMatrix)
      }
      const show = (swarmOf, amount, base) => {
        swarmOf.mesh.count = Math.min(swarmOf.max, Math.round(base * amount * gv('wildlife')))
        swarmOf.mesh.visible = swarmOf.mesh.count > 0 && view.p > 0.01 && !under
        return swarmOf.mesh.count
      }
      const done = (swarmOf) => {
        swarmOf.mesh.instanceMatrix.needsUpdate = true
        if (swarmOf.mesh.instanceColor) swarmOf.mesh.instanceColor.needsUpdate = true
      }
      const night = 1 - day

      // Fireflies drift and blink after dark
      const fireflyCount = show(fireflies, wild.fireflies * (night > 0.02 ? 1 : 0), 50)
      for (let i = 0; i < fireflyCount; i++) {
        const b = fireflies.bugs[i]
        keepNear(b, rand(4, 40))
        b.pos.x += Math.sin(clock * 0.7 + b.phase) * 10 * dt
        b.pos.z += Math.cos(clock * 0.6 + b.phase * 1.3) * 10 * dt
        b.pos.y = clamp(b.pos.y + Math.sin(clock * 1.1 + b.phase) * 6 * dt, 3, 45)
        // Same size throughout; they blink and fade in brightness instead
        const lit = Math.sin(clock * 2.3 + b.phase * 7) > 0.2 ? 1 : 0.15
        place(fireflies, i, b, bugScale.copy(unit).multiplyScalar(Math.max(0.001, 0.6 * e)))
        fireflies.mesh.setColorAt(i, fireflyGlow.copy(fireflyDark).lerp(fireflyLit, lit * night))
      }
      done(fireflies)

      // Beetles amble about on the ground, now and then changing course
      const beetleCount = show(beetles, wild.beetles, 30)
      for (let i = 0; i < beetleCount; i++) {
        const b = beetles.bugs[i]
        keepNear(b, 0)
        if (Math.random() < dt * 0.5) b.heading += rand(-1.5, 1.5)
        b.pos.x += Math.cos(b.heading) * 9 * dt
        b.pos.z -= Math.sin(b.heading) * 9 * dt
        place(beetles, i, b, bugScale.copy(unit).multiplyScalar(Math.max(0.001, e)))
      }
      done(beetles)

      // Butterflies flap lazy loops by day, in the biome's colours
      const butterflyCount = show(butterflies, wild.butterflies * (day > 0.02 ? 1 : 0), 14)
      for (let i = 0; i < butterflyCount; i++) {
        const b = butterflies.bugs[i]
        if (keepNear(b, 12) || b.biome !== here) {
          b.biome = here
          butterflies.mesh.setColorAt(i, pick(colours.wings))
        }
        b.heading += Math.sin(clock * 0.5 + b.phase) * dt
        b.pos.x += Math.cos(b.heading) * 22 * dt
        b.pos.z -= Math.sin(b.heading) * 22 * dt
        b.pos.y = 10 + Math.sin(clock * 1.3 + b.phase) * 6
        const flap = 0.2 + 0.8 * Math.abs(Math.sin(clock * 18 + b.phase))
        const size = Math.max(0.001, day * e)
        place(butterflies, i, b, bugScale.set(flap * size, size, size), -Math.PI / 2)
      }
      done(butterflies)

      // Bees buzz in jittery loops close to the flowers by day
      const beeCount = show(bees, wild.bees * (day > 0.3 ? 1 : 0), 20)
      for (let i = 0; i < beeCount; i++) {
        const b = bees.bugs[i]
        if (keepNear(b, 4, 260)) b.home = b.pos.clone()
        const t = clock * 3 + b.phase
        b.pos.set(b.home.x + Math.sin(t * 1.7) * 8, 4 + Math.abs(Math.sin(t * 2.3)) * 6, b.home.z + Math.cos(t * 1.3) * 8)
        b.heading = t * 1.7
        place(bees, i, b, bugScale.copy(unit).multiplyScalar(Math.max(0.001, e)))
      }
      done(bees)

      // Dragonflies hover, then dart off somewhere close by
      const dragonflyCount = show(dragonflies, wild.dragonflies * (day > 0.2 ? 1 : 0), 12)
      for (let i = 0; i < dragonflyCount; i++) {
        const b = dragonflies.bugs[i]
        if (keepNear(b, rand(6, 18), 300) || !b.to) b.to = b.pos.clone()
        if (b.pos.distanceTo(b.to) < 1 && Math.random() < dt * 1.2) {
          b.to = b.pos.clone().add(new Vector3(rand(-40, 40), rand(-4, 4), rand(-40, 40)))
          b.to.y = clamp(b.to.y, 5, 22)
        }
        const step = b.to.clone().sub(b.pos)
        const gap = step.length()
        if (gap > 0.5) {
          b.heading = Math.atan2(-step.z, step.x)
          b.pos.addScaledVector(step, Math.min(1, (90 * dt) / gap))
        }
        b.pos.y += Math.sin(clock * 9 + b.phase) * 0.05
        place(dragonflies, i, b, bugScale.copy(unit).multiplyScalar(Math.max(0.001, e)))
      }
      done(dragonflies)

      // Birds wheel high overhead by day; bats flit low and erratic by night
      const bats = night > 0.5
      const flyerCount = show(flyers, bats ? wild.bats : wild.birds, 14)
      wings.mesh.count = flyerCount * 2
      wings.mesh.visible = flyers.mesh.visible
      for (let i = 0; i < flyerCount; i++) {
        const b = flyers.bugs[i]
        if (!b.centre || flat(b.centre.clone().sub(s.pos)) > 520 || b.bat !== bats || b.biome !== here) {
          b.bat = bats
          b.biome = here
          // Out ahead of the camera, where they're in view against the sky
          b.centre = new Vector3(s.pos.x + rand(-320, 320), 0, s.pos.z - rand(bats ? 60 : 180, bats ? 260 : 560))
          b.radius = bats ? rand(30, 90) : rand(60, 180)
          b.height = bats ? rand(20, 50) : rand(45, 110)
          b.speed = (bats ? rand(1.4, 2.4) : rand(0.35, 0.7)) * (Math.random() < 0.5 ? 1 : -1)
          b.angle = rand(0, Math.PI * 2)
          const colour = bats ? batColour : pick(colours.birds)
          flyers.mesh.setColorAt(i, colour)
          wings.mesh.setColorAt(i * 2, colour)
          wings.mesh.setColorAt(i * 2 + 1, colour)
        }
        b.angle += b.speed * dt
        const wobble = bats ? Math.sin(clock * 5 + b.phase) * 12 : Math.sin(clock * 0.8 + b.phase) * 6
        b.pos = b.pos || new Vector3()
        b.pos.set(
          b.centre.x + Math.cos(b.angle) * (b.radius + wobble),
          b.height + Math.sin(clock * 1.3 + b.phase) * 4,
          b.centre.z + Math.sin(b.angle) * (b.radius + wobble),
        )
        // Facing along the circle it flies
        const dir = Math.sign(b.speed)
        b.heading = Math.atan2(-Math.cos(b.angle) * dir, -Math.sin(b.angle) * dir)
        const size = Math.max(0.001, e)
        place(flyers, i, b, bugScale.copy(unit).multiplyScalar(size))
        // Birds flap, then glide; bats never stop flapping
        const beat = bats ? Math.sin(clock * 22 + b.phase) : Math.sin(clock * 9 + b.phase) * (Math.sin(clock * 0.7 + b.phase) > 0 ? 1 : 0.15)
        const flap = beat * 0.7
        bugEuler.set(flap, b.heading, 0)
        bugQuat.setFromEuler(bugEuler)
        bugMatrix.compose(b.pos, bugQuat, bugScale.copy(unit).multiplyScalar(size))
        wings.mesh.setMatrixAt(i * 2, bugMatrix)
        bugEuler.set(flap, b.heading + Math.PI, 0)
        bugQuat.setFromEuler(bugEuler)
        bugMatrix.compose(b.pos, bugQuat, bugScale)
        wings.mesh.setMatrixAt(i * 2 + 1, bugMatrix)
      }
      done(flyers)
      done(wings)

      // Leaves flutter down, blown sideways in a storm
      const leafCount = show(leaves, wild.leaves, 50)
      const wind = here === 'storm' ? 70 : 8
      for (let i = 0; i < leafCount; i++) {
        const b = leaves.bugs[i]
        if (keepNear(b, rand(40, 140), 320) || b.pos.y <= 0.5 || b.biome !== here) {
          if (b.pos.y <= 0.5) b.pos.y = rand(60, 140)
          b.biome = here
          leaves.mesh.setColorAt(i, pick(colours.leaves))
        }
        const fall = here === 'storm' ? 34 : 14
        b.pos.y = Math.max(0, b.pos.y - fall * dt)
        b.pos.x += (Math.sin(clock * 1.6 + b.phase) * 12 + wind) * dt
        b.pos.z += Math.cos(clock * 1.2 + b.phase) * 6 * dt
        b.heading += dt * 2
        place(leaves, i, b, bugScale.copy(unit).multiplyScalar(Math.max(0.001, e)), Math.sin(clock * 3 + b.phase) * 1.2, Math.cos(clock * 2 + b.phase))
      }
      done(leaves)

      // Low banks of mist drifting through
      // Spread out to the edge of view; one left behind drifts in again out
      // at the edge, where the woods grow in, ahead of the squirrel (or the
      // way the camera looks), rather than appearing close by
      const mistCount = show(mist, wild.mist > 0.02 ? 1 : 0, 100)
      mistMat.opacity = clamp(wild.mist, 0, 1) * 0.16 * view.p
      const mistReach = viewRadius()
      const mistAhead = flat(s.vel) > 30 ? Math.atan2(s.vel.z, s.vel.x) : Math.atan2(s.pos.z - camera.position.z, s.pos.x - camera.position.x)
      for (let i = 0; i < mistCount; i++) {
        const b = mist.bugs[i]
        if (!b.pos || flat(b.pos.clone().sub(s.pos)) > mistReach * 0.95) {
          const fresh = !b.pos
          const a = fresh ? rand(0, Math.PI * 2) : mistAhead + rand(-0.9, 0.9)
          const r = fresh ? Math.sqrt(Math.random()) * mistReach * 0.9 : mistReach * rand(0.75, 0.9)
          b.pos = new Vector3(s.pos.x + Math.cos(a) * r, 0, s.pos.z + Math.sin(a) * r)
          b.lift = rand(3, 10)
        }
        b.pos.y = floorAt(b.pos.x, b.pos.z) + b.lift
        b.pos.x += Math.sin(clock * 0.1 + b.phase) * 6 * dt
        b.pos.z += Math.cos(clock * 0.08 + b.phase) * 4 * dt
        place(mist, i, b, bugScale.set(1 + Math.sin(b.phase) * 0.4, 1, 1 + Math.cos(b.phase) * 0.4).multiplyScalar(Math.max(0.001, e)))
      }
      done(mist)

      // Raindrops splashing up off the ground
      const splashCount = show(splashes, world.paused ? 0 : wild.splashes, 36)
      for (let i = 0; i < splashCount; i++) {
        const b = splashes.bugs[i]
        b.life = (b.life || 0) - dt
        if (b.life <= 0 || !b.pos) {
          b.life = rand(0.15, 0.35)
          b.max = b.life
          b.pos = new Vector3(s.pos.x + rand(-260, 260), 0.3, s.pos.z + rand(-320, 120))
        }
        const t = 1 - b.life / b.max
        place(splashes, i, b, bugScale.set(0.4 + t, 1 - t * 0.8, 0.4 + t).multiplyScalar(Math.max(0.001, e)))
      }
      done(splashes)
    }

    // ----- Poison bubbles -----
    const bubbleGeo = keep(new BoxGeometry(1.6, 1.6, 1.6))
    const bubbleMat = new MeshBasicMaterial({ color: '#7dff6a', transparent: true, opacity: 0.75 })
    const bubbles = []
    let bubbleIn = 0
    const POISON_GREEN = new Color('#5dff4a')

    const updateBubbles = (dt) => {
      const s = world.squirrel
      bubbleIn -= dt
      if (s.poisonFor > 0 && bubbleIn <= 0) {
        bubbleIn = 0.07
        let b = bubbles.find((x) => x.life <= 0)
        if (!b) {
          b = { mesh: new Mesh(bubbleGeo, bubbleMat), life: 0 }
          woods.add(b.mesh)
          bubbles.push(b)
        }
        b.life = rand(0.6, 1)
        b.mesh.position.set(s.pos.x + rand(-10, 10), s.pos.y + rand(6, 16), s.pos.z + rand(-6, 6))
        b.rise = rand(18, 34)
      }
      bubbles.forEach((b) => {
        b.life -= dt
        b.mesh.visible = b.life > 0
        if (b.life <= 0) return
        b.mesh.position.y += b.rise * dt
        b.mesh.position.x += Math.sin(b.life * 12) * 6 * dt
        b.mesh.scale.setScalar(Math.max(0.001, Math.min(1, b.life * 2) * view.e))
      })
    }

    // ----- Hearts -----
    const heartGeo = keep(buildHeartGeometry())
    const heartMat = new MeshLambertMaterial({ color: '#e23b4a', emissive: '#6a1018' })
    let hearts = []
    let heartIn = 4

    const clearHearts = () => {
      hearts.forEach((h) => woods.remove(h.mesh))
      hearts = []
    }

    // Meshes for acorns and crumbs, created as needed for the shared world state
    const acornMeshes = new Map()
    const meshFor = (a) => {
      let m = acornMeshes.get(a)
      if (!m) {
        const kind = a.kind || 'acorn'
        const geometry = nutGeos[kind] || acornGeo
        m = { body: new Mesh(geometry, a.super ? superMats : nutMats[kind] || acornMats), shadow: shadowMesh() }
        scene.add(m.body, m.shadow)
        acornMeshes.set(a, m)
      }
      return m
    }
    const crumbMeshes = []

    // ----- Layout -----
    const resize = () => {
      renderer.setPixelRatio(Math.max(0.5, (window.devicePixelRatio || 1) * gv('resolution')))
      renderer.setSize(window.innerWidth, window.innerHeight, false)
      // Huge screens can ask for more pixels than the browser will draw; it
      // quietly draws fewer, which would crop and shift the view. Ask for
      // what it can draw instead.
      const gl = renderer.getContext()
      const fit = Math.min(gl.drawingBufferWidth / canvas.width, gl.drawingBufferHeight / canvas.height)
      if (fit < 0.999) {
        renderer.setPixelRatio(renderer.getPixelRatio() * fit * 0.99)
        renderer.setSize(window.innerWidth, window.innerHeight, false)
      }
      bounds.x0 = nav.offsetLeft
      bounds.x1 = nav.offsetLeft + nav.offsetWidth
    }

    // Derive nut mode progress from how far the nav has travelled, so the
    // camera stays in step with the page's CSS transition
    const measure = () => {
      const rect = nav.getBoundingClientRect()
      const inset = nav.offsetLeft
      view.restGround = nav.offsetTop
      const zoomGround = view.restGround * STAGE_SCALE + stageShift()
      view.p = clamp((rect.top - view.restGround) / (zoomGround - view.restGround), 0, 1)
      view.e = ease(view.p)
      view.ground = rect.top
      // Fully in, the page is hidden outright, so none of it shows round the
      // edges of very wide screens
      const hidePage = world.staged && view.p >= 0.999 ? 'hidden' : ''
      if (stage && stage.style.visibility !== hidePage) stage.style.visibility = hidePage

      const narrow = window.innerWidth < 600
      const counterKey = `${inset}|${rect.top}|${view.p}|${narrow}`
      if (counterKey !== lastCounter) {
        lastCounter = counterKey
        counter.style.left = `${inset}px`
        counter.style.height = `${Math.min(rect.top, view.restGround + 40 * view.p)}px`
        // It doubles in size in nut mode, except on phones where there's no room
        counter.style.fontSize = `${narrow ? COUNTER_SIZE : COUNTER_SIZE * (1 + view.p)}px`
      }
    }

    // A dolly zoom from a far, narrow side-on view (world x = screen x, y = 0
    // on the nav's top edge) to a chase camera behind the squirrel. The frame
    // shrinks from the whole window to the strip above the nav as it goes.
    const follow = new Vector3()
    let camLift = GAME_PITCH
    let camPull = 1 // 1 at the usual distance, less when hills crowd in
    const updateCamera = (dt) => {
      const W = window.innerWidth
      const H = window.innerHeight
      const { e, ground } = view
      const s = world.squirrel
      const floorHere = floorAt(s.pos.x, s.pos.z)
      const aim = new Vector3(s.pos.x, 14 + floorHere + (s.pos.y - floorHere) * 0.7, s.pos.z - (under ? 0 : 30))
      if (e === 0) follow.copy(aim)
      else follow.lerp(aim, Math.min(1, dt * 6))

      const target = new Vector3(W / 2, 0, 0).lerp(follow, e)
      const visible = Math.min(ground, H)
      // Ultrawide screens narrow the view from top to bottom, so the extra
      // width doesn't leave the squirrel a speck in the middle
      const wide = clamp(W / visible / 2, 1, 4) // 16:9 and 16:10 windows (with the browser's bars) stay as they are
      const gameFov = (2 * Math.atan(Math.tan(((GAME_FOV / 2) * Math.PI) / 180) / Math.sqrt(wide)) * 180) / Math.PI
      const fov = Math.exp(lerp(Math.log(FLAT_FOV), Math.log(gameFov), e))
      const half = Math.tan(((fov / 2) * Math.PI) / 180)
      // Tall, narrow screens (phones held upright) pull back to see more around it
      const portrait = clamp(1.25 / (W / H), 1, 2)
      const gameView = 2 * GAME_DISTANCE * portrait * Math.tan(((gameFov / 2) * Math.PI) / 180)
      const frameH = Math.max(1, lerp(H, visible, e))
      const distance = lerp(H, gameView, e) / (2 * half)
      // Tip up over any hill between the camera and the squirrel
      let need = GAME_PITCH
      if (e > 0.5 && under) {
        // Underground it looks down more steeply, and straight down over
        // any rock between it and the squirrel in a narrow passage
        need = UNDER_PITCH
        const back = Math.cos(UNDER_PITCH) * (lerp(H, gameView, e) / (2 * half))
        for (let d = 8; d < back; d += 6) {
          if (!underCellAt(target.x - 6, target.z + d) || !underCellAt(target.x + 6, target.z + d)) {
            need = Math.max(need, Math.atan((UNDER_WALL + 6 - target.y) / Math.max(4, d - 8)))
          }
        }
      } else if (e > 0.5) {
        // Above ground: the lowest angle from which nothing stands between
        // the camera and the squirrel, tipping right over to look straight
        // down if a hill or cliff behind it would hide it otherwise
        const eye = new Vector3(s.pos.x, s.pos.y + SQ_H * 0.6, s.pos.z)
        const clear = (pitch) => {
          const cy = target.y + Math.sin(pitch) * distance
          const cz = target.z + Math.cos(pitch) * distance
          for (let f = 0.06; f < 1; f += 0.06) {
            const z = eye.z + (cz - eye.z) * f
            if (floorAt(target.x + (eye.x - target.x) * (1 - f), z) + 4 > eye.y + (cy - eye.y) * f) return false
          }
          return true
        }
        need = TOP_PITCH
        for (let pitch = GAME_PITCH; pitch < TOP_PITCH; pitch += 0.06) {
          if (clear(pitch)) {
            need = pitch
            break
          }
        }
      }
      camLift = lerp(camLift, Math.min(need, TOP_PITCH), Math.min(1, dt * 5))
      const pitch = (e > 0.5 ? camLift : GAME_PITCH) * e
      camera.position.set(
        target.x,
        target.y + Math.sin(pitch) * distance,
        target.z + Math.cos(pitch) * distance,
      )
      // Still hidden even looking straight down (only ever under an
      // overhang): come in a little closer, never so close it loses the squirrel
      let pull = 1
      if (e > 0.5 && !under && camLift > TOP_PITCH - 0.05) {
        const span = camera.position.clone().sub(target)
        for (let f = 0.3; f <= 1; f += 0.05) {
          if (floorAt(target.x + span.x * f, target.z + span.z * f) + 6 > target.y + span.y * f) {
            pull = Math.max(0.6, f - 0.05)
            break
          }
        }
      }
      // In quickly as the view is blocked, back out gently once it's clear
      camPull = pull < camPull ? lerp(camPull, pull, Math.min(1, dt * 12)) : lerp(camPull, pull, Math.min(1, dt * 2))
      if (camPull < 0.999) camera.position.sub(target).multiplyScalar(camPull).add(target)
      camera.lookAt(target)
      // First person: from the squirrel's eyes, looking where it's turned
      const fp = fpBlend * e
      if (fp > 0.001) {
        const dir = lookDir()
        const eye = new Vector3(s.pos.x, s.pos.y + SQ_H * 0.85, s.pos.z).addScaledVector(new Vector3(dir.x, 0, dir.z).normalize(), SQ_D * 0.35)
        camera.position.lerp(eye, fp)
        camera.lookAt(target.clone().lerp(eye.clone().addScaledVector(dir, 100), fp))
      }
      camera.fov = lerp(fov, FP_FOV, fp)
      camera.aspect = W / frameH
      camera.near = Math.max(0.5, distance - 2000)
      camera.far = distance + 4000
      camera.setViewOffset(W, frameH, 0, frameH / 2 - lerp(visible, visible / 2, e), W, H)
      camera.updateProjectionMatrix()

      scene.fog.near = distance + lerp(1e5, (under ? 150 : 280 * atmo.fog) * gv('view'), e)
      scene.fog.far = scene.fog.near + lerp(1e5, (under ? 420 : 600 * atmo.fog) * gv('view'), e)
    }

    // ----- Staging -----
    const showPrompt = (show) => {
      clearTimeout(promptTimer)
      prompt.classList.remove('centered')
      prompt.classList.toggle('show', show)
      if (show) promptTimer = setTimeout(() => prompt.classList.remove('show'), PROMPT_TIME)
    }
    const showToast = (text) => {
      clearTimeout(toastTimer)
      showPrompt(false)
      toast.textContent = text
      toast.classList.add('show')
      toastTimer = setTimeout(() => toast.classList.remove('show'), TOAST_TIME)
    }

    const setStaged = (staged) => {
      const s = world.squirrel
      if (staged && !world.staged) {
        showPrompt(true)
        // A fresh run each time; it finishes what it was doing before handing over
        world.score = 0
        world.lives = maxLives()
        showScore()
        // Grow the woods afresh around wherever it stands, in the meadow
        origin.x = 0
        origin.z = 0
        world.bonusLives = 0
        if (s) {
          s.powers = { magnet: 0, pepper: 0, feather: 0, shield: 0 }
          s.shield = false
          s.streak = 0
        }
        world.eaten = new Set()
        world.felled = new Map()
        world.harvested = new Set()
        caveMemory.clear()
        startRun('meadow')
        clearChunks()
        clearHearts()
        if (s) s.introFor = INTRO_TIME
        lockPointer()
      }
      if (!staged && world.staged) {
        showPrompt(false)
        if (s) s.superFor = 0
        clearBoss()
        clearPickups()
        world.paused = false
        pauseButton.classList.remove('paused')
        showOverlay(null)
        toast.classList.remove('show')
        keys.clear()
        releasePointer()
        if (s) {
          if (under) leaveUnder()
          s.caveFor = 0
          if (s.deadFor > 0) respawn(s, false)
          s.climb = null
          recentre()
        }
        clearHearts()
      }
      world.staged = staged
      if (stage) {
        stage.style.transform = staged ? `translateY(${stageShift()}px) scale(${STAGE_SCALE})` : ''
        stage.style.opacity = staged ? STAGE_OPACITY : ''
      }
      counter.classList.toggle('show', staged)
      exitBar.classList.toggle('show', staged)
      soundButton.classList.toggle('show', staged)
      pauseButton.classList.toggle('show', staged)
      levelButton.classList.toggle('show', staged)
      infoButton.classList.toggle('show', staged)
      touchPad.classList.toggle('show', staged && touchOnly)
      if (staged && touchOnly) window.scrollTo(0, 0)
      fullButton.classList.toggle('show', staged)
      gearButton.classList.toggle('show', staged)
      shopButton.classList.toggle('show', staged)
      devButton.classList.toggle('show', staged && devOn)
      bagButton.classList.toggle('show', staged)
      cameraButton.classList.toggle('show', staged)
      document.documentElement.classList.toggle('nut-mode', staged)
      // The tap that comes in mustn't land on a button that's just appeared under it
      if (staged) {
        document.documentElement.classList.add('nut-entering')
        setTimeout(() => document.documentElement.classList.remove('nut-entering'), 500)
      }
      // Drop any text left selected from the page
      if (staged && window.getSelection) window.getSelection().removeAllRanges()
      updateHover()
    }

    // Over the strip above the nav: a dot cursor and, out of nut mode, the invitation
    function updateHover() {
      const overStrip = mouse.y >= 0 && mouse.y < view.ground
      if (cursorEl) cursorEl.classList.toggle('in-sky', world.staged || overStrip)
      // Touch screens can't hover, and get no invitation: tapping the strip
      // above the nav is an easter egg
      hint.classList.toggle('show', !world.staged && overStrip && !touchOnly)
    }

    readColours()
    resize()
    measure()

    if (!world.squirrel) {
      world.squirrel = {
        pos: new Vector3(bounds.x0 + (bounds.x1 - bounds.x0) * 0.3, 0, -SQ_D / 2),
        vel: new Vector3(),
        heading: 0,
        phase: 0,
        target: null,
        introFor: 0,
        eatFor: 0,
        wanderTo: null,
        idleFor: 1,
        deadFor: 0,
        safeFor: 0,
        attackFor: 0,
        attackCd: 0,
        tilt: 0,
        climb: null,
      }
    }
    follow.set(world.squirrel.pos.x, 14, world.squirrel.pos.z - 30)
    setStaged(world.staged)

    // ----- Acorns -----
    // Rainbow super nuts turn up by chance, but never during a boss fight
    // (a flag rather than the boss itself, which is set up further on)
    let fightOn = false
    const superChance = () => (fightOn ? 0 : SUPER_CHANCE)
    const spawnAcorn = (pos, isSuper = false) => {
      world.acorns.push({
        kind: world.staged ? FLORA[biome].nut : 'acorn',
        pos,
        vel: new Vector3(rand(-30, 30), -rand(20, 60), rand(-30, 30)),
        quat: new Quaternion(),
        super: isSuper,
      })
    }
    // Whether a point is in the camera's view (with a margin)
    const onScreen = (x, y, z) => {
      const at = new Vector3(x, y, z).project(camera)
      return at.z < 1 && Math.abs(at.x) < 1.15 && Math.abs(at.y) < 1.15
    }
    // Flat view: from the sky just above the screen. Nut mode: from a tree.
    const dropAcorn = () => {
      const s = world.squirrel
      if (under) return
      if (view.p > 0.5) {
        // Each tree grows just the one acorn, and only where it can't be
        // seen appearing: off screen, or far off in the haze
        const near = trees.filter((t) => {
          if (t.hits >= 2 || NOT_TREES.includes(t.kind) || world.harvested.has(t.id)) return false
          const d = Math.hypot(t.x - s.pos.x, t.z - s.pos.z)
          return d < viewRadius() - 40 && (d > 640 * gv('view') || !onScreen(t.x, t.canopyY, t.z))
        })
        // On the move, most grow in trees along the way ahead, so a squirrel
        // running flat out still comes across them; otherwise ones nearby
        const pace = flat(s.vel)
        const ahead = near.filter((t) => {
          const dx = t.x - s.pos.x
          const dz = t.z - s.pos.z
          const d = Math.hypot(dx, dz)
          return pace > 60 ? d > 150 && d < 750 && (dx * s.vel.x + dz * s.vel.z) / pace > d * 0.7 : d < 600
        })
        const pool = ahead.length && Math.random() < 0.8 ? ahead : near
        const tree = pool[Math.floor(Math.random() * pool.length)]
        if (tree) growNut(tree)
      } else {
        spawnAcorn(
          new Vector3(
            rand(bounds.x0 + AC_R, bounds.x1 - AC_R),
            view.ground + AC_R * 2,
            clamp(s.pos.z + rand(-40, 40), -DEPTH + AC_R, -AC_R),
          ),
        )
      }
    }
    // A tree's one acorn
    const growNut = (tree) => {
      world.harvested.add(tree.id)
      // Ripens on top of the crown, on the side facing the camera so it's
      // in view; most drop after a while, some stay put
      const cos = Math.cos(tree.rot)
      const sin = Math.sin(tree.rot)
      const toCamera = new Vector3(camera.position.x - tree.x, 0, camera.position.z - tree.z).normalize()
      const spots = [...tree.tops.entries()].map(([column, top]) => {
        const [vx, vz] = column.split(',').map(Number)
        const lx = (vx + 0.5 + tree.offset[0]) * TREE_VOXEL
        const lz = (vz + 0.5 + tree.offset[2]) * TREE_VOXEL
        const x = cos * lx + sin * lz
        const z = -sin * lx + cos * lz
        return { x: tree.x + x, z: tree.z + z, top, front: x * toCamera.x + z * toCamera.z }
      })
      // Most have already fallen, and lie on the ground under the crown
      if (Math.random() < GROUND_NUTS) {
        const clear = (tree.trunkRadius || 6) + AC_R * 2
        const below = spots.filter((p) => Math.hypot(p.x - tree.x, p.z - tree.z) > clear)
        const a = rand(0, Math.PI * 2)
        const r = clear + rand(4, 24)
        const spot = below.length
          ? below[Math.floor(Math.random() * below.length)]
          : { x: tree.x + Math.cos(a) * r, z: tree.z + Math.sin(a) * r }
        spawnAcorn(new Vector3(spot.x, floorAt(spot.x, spot.z) + AC_R, spot.z), Math.random() < superChance())
        world.acorns[world.acorns.length - 1].vel.set(0, 0, 0)
        return
      }
      spots.sort((a, b) => b.front - a.front)
      const spot = spots[Math.floor(Math.random() * Math.max(1, Math.ceil(spots.length * 0.25)))]
      spawnAcorn(new Vector3(spot.x, spot.top + (tree.baseY || 0) + AC_R, spot.z), Math.random() < superChance())
      const acorn = world.acorns[world.acorns.length - 1]
      acorn.vel.set(0, 0, 0)
      acorn.hang = tree
      acorn.hangHits = tree.hits
      acorn.fallIn = Math.random() < HANGING_STAY ? Infinity : rand(1.5, 5)
    }
    // Woods grown out at the edge of view come with nuts already on some trees
    const seedNuts = (chunk) => {
      const plenty = SEASONS.reduce((sum, name) => sum + SEASON_NUTS[name] * seasonWeight(name), 0)
      chunk.trees.forEach((t) => {
        if (t.hits >= 2 || NOT_TREES.includes(t.kind) || world.harvested.has(t.id)) return
        if (Math.random() < 0.1 * plenty) growNut(t)
      })
    }

    // Push a circle out of tree trunks, optionally bouncing its velocity;
    // returns the last trunk it touched
    const dodgeTrunks = (pos, radius, vel, bounce) => {
      let touched = null
      trees.forEach((t) => {
        if (pos.y >= t.canopyY || !t.trunkRadius) return
        const dx = pos.x - t.x
        const dz = pos.z - t.z
        const d = Math.hypot(dx, dz)
        const min = t.trunkRadius + radius
        if (d >= min || d === 0) return
        pos.x = t.x + (dx / d) * min
        pos.z = t.z + (dz / d) * min
        if (vel) {
          const n = new Vector3(dx / d, 0, dz / d)
          const into = vel.dot(n)
          if (into < 0) vel.addScaledVector(n, -into * (1 + bounce))
        }
        touched = t
      })
      return touched
    }

    const spin = new Vector3()
    const turn = new Quaternion()
    const updateAcorns = (dt) => {
      const xMin = bounds.x0 + AC_R
      const xMax = bounds.x1 - AC_R
      const zMin = -DEPTH + AC_R
      const zMax = -AC_R
      // The flat view keeps acorns along the nav; the woods have no edges
      const fenced = !world.staged
      if (!fenced) {
        const s = world.squirrel
        world.acorns = world.acorns.filter((a) => flat(a.pos.clone().sub(s.pos)) < DROP_RADIUS * gv('view'))
      }
      world.acorns.forEach((a) => {
        if (a.magnet) {
          a.pos.addScaledVector(a.vel, dt)
          return
        }
        if (a.hang) {
          // Only starts to loosen once the squirrel is close
          if (flat(a.pos.clone().sub(world.squirrel.pos)) < 300) a.fallIn -= dt
          if (a.fallIn > 0 && trees.includes(a.hang) && a.hang.hits === a.hangHits) return
          a.hang = null
          a.vel.set(rand(-20, 20), 0, rand(-20, 20))
        }
        a.vel.y -= GRAVITY * dt
        a.pos.addScaledVector(a.vel, dt)
        if (!fenced) {
          // No walls
        } else if (a.pos.x < xMin || a.pos.x > xMax) {
          a.pos.x = clamp(a.pos.x, xMin, xMax)
          a.vel.x *= -0.6
        }
        if (fenced && (a.pos.z < zMin || a.pos.z > zMax)) {
          a.pos.z = clamp(a.pos.z, zMin, zMax)
          a.vel.z *= -0.6
        }
        if (view.p > 0.5 && a.pos.y < 60) dodgeTrunks(a.pos, AC_R, a.vel, 0.6)
        if (under && !underCellAt(a.pos.x, a.pos.z)) {
          a.pos.x -= a.vel.x * dt
          a.pos.z -= a.vel.z * dt
          a.vel.x *= -0.5
          a.vel.z *= -0.5
        }
        const floorA = view.p > 0.5 ? floorAt(a.pos.x, a.pos.z) : 0
        // In a pool it floats, bobbing and drifting gently
        const pool = view.p > 0.5 && floorA === 0 && waters.find((w) => Math.hypot((a.pos.x - w.x) / w.rx, (a.pos.z - w.z) / w.rz) < 1)
        if (pool && a.pos.y <= 0.45 + AC_R * 0.6) {
          a.pos.y = 0.45 + AC_R * 0.4 + Math.sin(clock * 3 + a.pos.x) * 0.6
          a.vel.y = 0
          a.vel.x = a.vel.x * Math.exp(-1.2 * dt) + (pool.z - a.pos.z) * 0.02 * dt * 10
          a.vel.z = a.vel.z * Math.exp(-1.2 * dt) - (pool.x - a.pos.x) * 0.02 * dt * 10
        } else if (a.pos.y <= floorA + AC_R) {
          a.pos.y = floorA + AC_R
          if (a.vel.y < -80) {
            // Lumpy acorns kick off sideways when they hit the ground
            const impact = -a.vel.y
            a.vel.y = impact * 0.4
            a.vel.x += rand(-1, 1) * impact * 0.2
            a.vel.z += rand(-1, 1) * impact * 0.2
          } else {
            a.vel.y = 0
            const friction = Math.exp(-1.5 * dt)
            a.vel.x *= friction
            a.vel.z *= friction
            if (flat(a.vel) < 4) {
              a.vel.x = 0
              a.vel.z = 0
            }
          }
        }
        // Roll: spin about the axis perpendicular to travel
        spin.set(a.vel.z, 0, -a.vel.x).divideScalar(AC_R)
        const angle = spin.length() * dt
        if (angle > 0) {
          turn.setFromAxisAngle(spin.normalize(), angle)
          a.quat.premultiply(turn)
        }
      })

      // Nudge overlapping acorns apart
      for (let i = 0; i < world.acorns.length; i++) {
        for (let j = i + 1; j < world.acorns.length; j++) {
          const a = world.acorns[i]
          const b = world.acorns[j]
          if (a.hang || b.hang) continue
          const d = b.pos.clone().sub(a.pos)
          const dist = d.length()
          if (dist > 0 && dist < AC_R * 2) {
            d.divideScalar(dist)
            const push = (AC_R * 2 - dist) / 2
            a.pos.addScaledVector(d, -push)
            b.pos.addScaledVector(d, push)
            const closing = b.vel.clone().sub(a.vel).dot(d)
            if (closing < 0) {
              a.vel.addScaledVector(d, closing * 0.9)
              b.vel.addScaledVector(d, -closing * 0.9)
            }
          }
        }
      }
    }

    const updateCrumbs = (dt) => {
      world.crumbs = world.crumbs.filter((c) => (c.life -= dt) > 0)
      world.crumbs.forEach((c) => {
        c.vel.y -= GRAVITY * dt
        c.pos.addScaledVector(c.vel, dt)
        c.pos.y = Math.max(VOXEL / 2, c.pos.y)
      })
    }

    // ----- Squirrel -----
    const facing = (s) => new Vector3(Math.cos(s.heading), 0, -Math.sin(s.heading))
    const mouthOf = (s) => s.pos.clone().addScaledVector(facing(s), MOUTH)

    // Only nuts eaten under the player's control score
    const eat = (s, acorn, scores) => {
      world.acorns = world.acorns.filter((a) => a !== acorn)
      if (acorn.caveId) world.harvested.add(acorn.caveId)
      if (s.target === acorn) s.target = null
      s.eatFor = 0.9
      // The squirrel on its own stops to eat; the player's squirrel eats on
      // the run without losing any speed
      if (!scores) {
        s.vel.x = 0
        s.vel.z = 0
      }
      if (scores) {
        // Eaten in quick succession, nuts build a streak worth double or triple
        s.streak = clock - (s.lastEat === undefined ? -99 : s.lastEat) < COMBO_TIME ? (s.streak || 0) + 1 : 1
        s.lastEat = clock
        const mult = comboMultiplier(s.streak)
        world.score += mult
        // Banked for the nut store
        if (world.staged) {
          shop.bank += mult
          saveShop()
        }
        if (world.score > best) {
          best = world.score
          saveBest(world.difficulty, best)
        }
        showScore()
        popup(`+${mult}`, acorn.super ? 'super' : 'nut')
        showCombo()
        if (!acorn.super) sound.play('eat')
      }
      if (acorn.super && world.staged) {
        s.superFor = SUPER_TIME
        s.poisonFor = 0
        showToast('Super nut!')
        sound.play('super')
      }
      const mouth = mouthOf(s)
      for (let i = 0; i < 6; i++) {
        world.crumbs.push({
          pos: new Vector3(mouth.x, SQ_H - 8, mouth.z),
          vel: new Vector3(rand(-60, 60), rand(40, 140), rand(-60, 60)),
          life: rand(0.4, 0.7),
        })
      }
    }

    // A spider bite or a poison mushroom costs a life where it stands: a bite
    // leaves it flashing and briefly untouchable, poison glows green for a
    // while. Losing the last life ends the game; it flops over and waits.
    const hurt = (s, kind) => {
      if (s.superFor > 0 || devGod) return
      // A shield acorn makes it untouchable while it lasts
      if (s.powers && s.powers.shield > 0) {
        s.safeFor = Math.max(s.safeFor, 0.6)
        if (clock - (s.blockedAt || -9) > 0.6) {
          popup('Blocked', 'power')
          sound.play('clank')
        }
        s.blockedAt = clock
        return
      }
      s.streak = 0
      showCombo()
      world.lives -= 1
      showScore()
      popup('-1', 'life')
      sound.play(kind === 'poison' ? 'poison' : 'hurt')
      if (kind === 'poison') s.poisonFor = POISON_TIME
      s.safeFor = kind === 'poison' ? POISON_TIME : 1.5
      if (world.lives > 0) return
      s.deadFor = 1.6
      s.climb = null
      s.vel.set(0, 160, 0)
      s.target = null
      s.gameOver = true
    }

    // The mushroom is gone once eaten, for the rest of the run
    const eatMushroom = (s, shroom) => {
      world.eaten.add(shroom.id)
      shroom.chunk.group.remove(shroom.mesh)
      shroom.mesh.geometry.dispose()
      shroom.chunk.mushrooms = shroom.chunk.mushrooms.filter((m) => m !== shroom)
      indexChunks()
      if (s.superFor > 0) sound.play('munch')
      else hurt(s, 'poison')
    }

    const newGame = () => {
      world.seed = Math.floor(Math.random() * 1e9)
      world.time = DAY_START
      world.bonusLives = 0
      clearBoss()
      bossIn = rand(120, 200)
      clearPickups()
      const sq = world.squirrel
      if (sq) {
        sq.powers = { magnet: 0, pepper: 0, feather: 0, shield: 0 }
        sq.shield = false
        sq.streak = 0
      }
      const others = biomeKinds.filter((k) => k !== world.startBiome)
      startRun(others[Math.floor(Math.random() * others.length)])
      world.eaten = new Set()
      world.felled = new Map()
      world.harvested = new Set()
      caveMemory.clear()
      world.acorns = []
      world.crumbs = []
      world.score = 0
      world.lives = maxLives()
      clearChunks()
      clearHearts()
      updateChunks(0)
      spiders.forEach((sp) => {
        sp.alive = false
        sp.deadFor = 0
        sp.respawnIn = rand(1, 4)
      })
      showScore()
    }

    // Back on its feet where it fell, clear of mushrooms and briefly untouchable
    function respawn(s, fresh = true) {
      if (s.gameOver) {
        s.gameOver = false
        newGame()
      }
      s.deadFor = 0
      s.tilt = 0
      s.poisonFor = 0
      s.vel.set(0, 0, 0)
      if (fresh) {
        const nasty = () => mushrooms.some((m) => Math.hypot(m.x - s.pos.x, m.z - s.pos.z) < 40)
        for (let i = 0; i < 20 && nasty(); i++) s.pos.z += 30
        s.safeFor = 2
      }
      s.pos.y = floorAt(s.pos.x, s.pos.z)
    }

    // Shift the whole world so the squirrel lands back on the nav for the flat
    // view. Everything moves together, camera included, so nothing jumps.
    function recentre() {
      const s = world.squirrel
      const dx = clamp(s.pos.x, xMinSq(), xMaxSq()) - s.pos.x
      const dz = -SQ_D / 2 - s.pos.z
      const shift = (v) => {
        v.x += dx
        v.z += dz
      }
      shift(s.pos)
      shift(follow)
      world.acorns.forEach((a) => shift(a.pos))
      world.crumbs.forEach((c) => shift(c.pos))
      spiders.forEach((sp) => shift(sp.pos))
      chunks.forEach((c) => shift(c.group.position))
      origin.x -= dx
      origin.z -= dz
      indexChunks()
      s.wanderTo = null
      s.target = null
      // Acorns left out in the woods don't come back to the nav, and none stay up trees
      world.acorns = world.acorns.filter((a) => a.pos.x > bounds.x0 && a.pos.x < bounds.x1 && (a.kind || 'acorn') === 'acorn')
      world.acorns.forEach((a) => {
        a.hang = null
      })
    }

    const xMinSq = () => bounds.x0 + SQ_W / 2 + 2
    const xMaxSq = () => bounds.x1 - SQ_W / 2 - 2
    const zMinSq = -DEPTH + SQ_D / 2
    const zMaxSq = -SQ_D / 2

    // Finds its own way to acorns, or wanders when there are none
    const autopilot = (s, dt, desired, is3D) => {
      const reachable = world.acorns.filter((a) => !a.hang)
      if (!s.target && reachable.length) {
        const gap = (a) => (is3D ? flat(a.pos.clone().sub(s.pos)) : Math.abs(a.pos.x - s.pos.x))
        s.target = reachable.reduce((best, a) => (gap(a) < gap(best) ? a : best))
      }
      if (s.target) {
        const t = s.target.pos
        const onFloor = t.y - floorAt(t.x, t.z) <= AC_R + 1
        const grounded = s.grounded !== false && s.vel.y === 0
        if (is3D) {
          const to = t.clone().sub(s.pos).setY(0)
          const reach = to.length() - MOUTH
          if (reach < 4 && onFloor && grounded) eat(s, s.target, false)
          else if (reach >= 4) desired.copy(to.normalize()).multiplyScalar(Math.min(RUN_SPEED, reach * 8))
          else s.heading = Math.atan2(-to.z, to.x)
        } else {
          // Flat view: depth doesn't show, so line the mouth up in x only
          const dx = t.x - (s.pos.x + Math.sign(Math.cos(s.heading) || 1) * MOUTH)
          if (Math.abs(dx) < 4 && onFloor && grounded) eat(s, s.target, false)
          else if (Math.abs(dx) >= 4) desired.x = Math.sign(dx) * Math.min(RUN_SPEED, Math.abs(dx) * 8)
        }
        return
      }
      s.idleFor -= dt
      if (!s.wanderTo && s.idleFor <= 0) {
        s.wanderTo = is3D
          ? new Vector3(s.pos.x + rand(-200, 200), 0, s.pos.z + rand(-200, 200))
          : new Vector3(rand(xMinSq(), xMaxSq()), 0, s.pos.z)
      }
      if (s.wanderTo) {
        const to = s.wanderTo.clone().sub(s.pos).setY(0)
        if (!is3D) to.z = 0
        if (to.length() < 4) {
          s.wanderTo = null
          s.idleFor = rand(1.5, 4)
        } else {
          desired.copy(to.normalize()).multiplyScalar(RUN_SPEED * 0.5)
        }
      }
    }

    const updateSquirrel = (dt) => {
      const s = world.squirrel
      const is3D = view.p > 0.5
      const playing = world.staged && is3D && s.introFor <= 0
      const hazards = world.staged && view.p > 0.9 && s.introFor <= 0

      s.introFor = Math.max(0, s.introFor - dt)
      s.safeFor = Math.max(0, s.safeFor - dt)
      s.poisonFor = Math.max(0, (s.poisonFor || 0) - dt)
      s.superFor = Math.max(0, (s.superFor || 0) - dt)
      s.attackCd = Math.max(0, s.attackCd - dt)
      s.attackFor = Math.max(0, s.attackFor - dt)
      if (s.target && !world.acorns.includes(s.target)) s.target = null
      s.caveCd = Math.max(0, (s.caveCd || 0) - dt)
      // On the way down into the caves or back up: the view fades to black
      // and back, and it comes out somewhere else
      if (s.caveFor > 0) {
        s.caveFor -= dt
        s.vel.set(0, 0, 0)
        if (!s.caveSwitched && s.caveFor <= CAVE_TRIP / 2) {
          s.caveSwitched = true
          const trip = s.caveTrip
          if (trip.down) enterUnder(trip.plan, trip.x, trip.z, trip.fall)
          else surface(trip.exit)
        }
        if (s.caveFor > 0) return
        if (s.caveFall) {
          s.vel.y = -40
          s.caveFall = false
        }
      }
      if (s.deadFor > 0) {
        // Flopped over; wait to come back, or after the last life, for a click
        s.deadFor -= dt
        s.tilt = Math.min(Math.PI / 2, s.tilt + dt * 8)
        s.vel.x = 0
        s.vel.z = 0
        if (s.deadFor <= 0) {
          if (s.gameOver) {
            s.deadFor = 0.001
            if (overlay.dataset.kind !== 'over') {
              showOverlay('over')
              sound.play('over')
            }
          } else {
            respawn(s)
          }
        }
      }

      const input = new Vector3()
      keys.forEach((k) => {
        input.x += KEYS[k][0]
        input.z += KEYS[k][1]
      })
      // The joystick: a gentle push walks slower, all the way out runs
      const push = Math.hypot(stick.x, stick.y)
      if (push > 0.15 && !world.paused) {
        input.x += stick.x / push
        input.z += stick.y / push
      }
      const stickPace = push > 0.15 ? clamp((push - 0.15) / 0.55, 0.35, 1) : 1
      const running = sprinting || push > 0.92
      // First person: turn, then move relative to the view
      const local = input.clone()
      if (firstPerson && playing) {
        lookYaw += ((turning.has('q') ? 1 : 0) - (turning.has('e') ? 1 : 0)) * 2.4 * dt
        if (push > 0.15) {
          // The stick turns rather than sidesteps
          lookYaw -= stick.x * 2.6 * dt
          input.x -= stick.x / push
          local.x -= stick.x / push
        }
        input.applyAxisAngle(new Vector3(0, 1, 0), lookYaw)
      }

      if (s.climb && (!playing || s.deadFor > 0 || !trees.includes(s.climb))) s.climb = null
      if (s.climb) {
        climb(s, dt, local)
        return
      }

      const desired = new Vector3()
      if (s.deadFor > 0) {
        // Nothing to do while dead
      } else if (playing) {
        if (input.lengthSq() > 0) {
          // Groggy while the poison works
          // Wading through water is slow going
          const wading = s.pos.y < 1 && waters.some((w) => Math.hypot((s.pos.x - w.x) / w.rx, (s.pos.z - w.z) / w.rz) < 1)
          const pace = (running ? SPRINT_SPEED : PLAYER_SPEED * stickPace) * (s.powers && s.powers.pepper > 0 ? 1.6 : 1) * (wading ? 0.55 : 1)
          desired.copy(input.normalize()).multiplyScalar(pace * (s.poisonFor > 0 ? 0.6 : 1))
          s.eatFor = 0
        } else if (s.eatFor > 0) {
          s.eatFor -= dt
        }
        s.target = null
        s.wanderTo = null
      } else if (s.eatFor > 0) {
        s.eatFor -= dt
      } else {
        autopilot(s, dt, desired, is3D)
      }

      // Ease toward desired speed. Mid-pounce the keys don't steer: it flies
      // its arc, skids to a stop on landing, then walks on as before.
      if (s.attackFor > 0) {
        if (s.grounded) {
          const skid = Math.exp(-12 * dt)
          s.vel.x *= skid
          s.vel.z *= skid
        }
      } else {
        // Puddles in the rain are slippery: slow to get going, slow to stop
        const slippy = s.grounded && (onPuddle(s) || onSnow(s))
        const accel = (desired.lengthSq() === 0 ? 1200 : 900) * dt * (slippy ? 0.08 : 1)
        const change = desired.sub(new Vector3(s.vel.x, 0, s.vel.z))
        if (change.length() > accel) change.setLength(accel)
        s.vel.x += change.x
        s.vel.z += change.z
      }

      // Turn to face travel; the flat view only ever faces left or right
      const speed = flat(s.vel)
      let aim = s.heading
      if (s.deadFor > 0) aim = s.heading
      else if (s.attackFor > 0) {
        // Mid-pounce the keys only turn it on the spot; the arc carries it
        if (playing && input.lengthSq() > 0) aim = Math.atan2(-input.z, input.x)
      }
      else if (speed > 5) aim = is3D ? Math.atan2(-s.vel.z, s.vel.x) : s.vel.x >= 0 ? 0 : Math.PI
      else if (!is3D) aim = Math.cos(s.heading) >= 0 ? 0 : Math.PI
      // First person: it faces where it's looking, even sidestepping
      if (firstPerson && playing && s.deadFor <= 0) aim = viewHeading()
      const diff = ((aim - s.heading + Math.PI * 3) % (Math.PI * 2)) - Math.PI
      s.heading += clamp(diff, -14 * dt, 14 * dt)

      const wasX = s.pos.x
      const wasZ = s.pos.z
      s.pos.x += s.vel.x * dt
      s.pos.z += s.vel.z * dt
      if (is3D && world.staged) {
        // Blocks more than one step up are walls: slide along them
        const lift = s.pos.y + STEP_UP
        const ahead = (x, z) => {
          const sp = flat(s.vel) || 1
          const fx = x + (s.vel.x / sp) * 5
          const fz = z + (s.vel.z / sp) * 5
          // A cave's side or back wall stops it like a cliff (unless it's up on the roof)
          if (s.pos.y < floorAt(wasX, wasZ) + CAVE_HEAD && caveWall(wasX, wasZ, fx, fz)) return Infinity
          // Underground the rock is solid, however high it jumps
          if (under && !underCellAt(fx, fz)) return Infinity
          return floorAt(fx, fz)
        }
        if (ahead(s.pos.x, s.pos.z) > lift) {
          if (ahead(wasX, s.pos.z) <= lift) {
            s.pos.x = wasX
            s.vel.x = 0
          } else if (ahead(s.pos.x, wasZ) <= lift) {
            s.pos.z = wasZ
            s.vel.z = 0
          } else {
            s.pos.x = wasX
            s.pos.z = wasZ
            s.vel.x = 0
            s.vel.z = 0
          }
        }
      }
      // The flat view keeps it on the nav; the woods have no edges, except
      // a boss's bramble ring
      if (!world.staged) {
        s.pos.x = clamp(s.pos.x, xMinSq(), xMaxSq())
        s.pos.z = clamp(s.pos.z, zMinSq, zMaxSq)
      } else {
        holdInArena(s)
      }
      if (is3D) {
        const trunk = dodgeTrunks(s.pos, SQ_D, null)
        // A super squirrel flattens any tree it runs into
        if (trunk && playing && s.superFor > 0 && s.deadFor <= 0) {
          trunk.hits = TREE_HITS - 1
          hitTree(trunk)
        } else if (trunk && trunk.hits < 2 && !NOT_TREES.includes(trunk.kind) && playing && s.grounded && Math.abs(s.pos.y - (trunk.baseY || 0)) < 2 && s.deadFor <= 0 && input.lengthSq() > 0) {
          const toward = new Vector3(trunk.x - s.pos.x, 0, trunk.z - s.pos.z).normalize()
          if (input.clone().normalize().dot(toward) > 0.5) s.climb = trunk
        }
      }

      // Jumps, falls and death hops, onto the floor or a treetop
      const footing = is3D && world.staged ? groundAt(s.pos.x, s.pos.z, s.pos.y) : { height: 0, tree: null }
      // Holding Space on the way down spreads its arms and legs into a glide
      s.gliding = playing && spaceHeld && !s.climb && s.deadFor <= 0 && s.vel.y < 0 && s.pos.y > footing.height + 2
      // A blizzard shoves it about even on the ground
      if (playing && blizzard > 0 && !s.gliding && !s.climb && s.deadFor <= 0) {
        s.pos.x += wind.x * 0.3 * dt
        s.pos.z += wind.z * 0.3 * dt
      }
      if (s.vel.y !== 0 || s.pos.y > footing.height) {
        // Updrafts over the hilltops lift a glide; storm winds push it about
        const updraft = s.gliding && footing.height >= 40 && s.pos.y < footing.height + 150
        if (updraft) s.vel.y = Math.min(s.vel.y + 300 * dt, 45)
        else if (s.gliding) s.vel.y = Math.max(s.vel.y - GRAVITY * 0.2 * dt, -GLIDE_FALL)
        else s.vel.y -= GRAVITY * dt
        if (s.gliding) {
          s.pos.x += wind.x * dt
          s.pos.z += wind.z * dt
        }
        s.pos.y += s.vel.y * dt
        // The cave roof underground
        if (under && s.pos.y + 16 > UNDER_WALL) {
          s.pos.y = UNDER_WALL - 16
          if (s.vel.y > 0) s.vel.y = 0
        }
        // Head room inside a cave
        const roof = roofAt(s.pos.x, s.pos.z)
        if (roof && s.pos.y < roof[0] && s.pos.y + 12 > roof[0] && s.vel.y > 0) {
          s.pos.y = roof[0] - 12
          s.vel.y = 0
        }
        if (s.pos.y <= footing.height && s.vel.y <= 0) {
          s.pos.y = footing.height
          s.vel.y = 0
        }
      } else {
        s.pos.y = footing.height
      }
      s.grounded = s.vel.y === 0 && s.pos.y === footing.height
      // Sheltered under a cave roof: no lightning strikes in here
      const overhead = roofAt(s.pos.x, s.pos.z)
      s.sheltered = under ? true : !!overhead && s.pos.y < overhead[0]
      s.perch = s.grounded ? footing.tree : null
      if (s.grounded) {
        s.doubled = false
        s.gliding = false
      }

      if (s.deadFor <= 0 && is3D) {
        // Eats any acorn it runs over
        const mouth = mouthOf(s)
        const snack = world.acorns.find((a) =>
          a.hang
            ? Math.abs(a.pos.y - AC_R - s.pos.y) < 10 &&
              (flat(a.pos.clone().sub(mouth)) < 10 || flat(a.pos.clone().sub(s.pos)) < 13)
            : a.pos.y - AC_R - s.pos.y <= 4 &&
              s.pos.y - floorAt(s.pos.x, s.pos.z) < 6 &&
              (flat(a.pos.clone().sub(mouth)) < 9 || flat(a.pos.clone().sub(s.pos)) < 12),
        )
        if (snack) eat(s, snack, playing)

        if (hazards && s.pos.y - floorAt(s.pos.x, s.pos.z) < 6) {
          const shroom = mushrooms.find((m) => Math.hypot(m.x - s.pos.x, m.z - s.pos.z) < 10)
          if (shroom) eatMushroom(s, shroom)
        }
        // Down into the caves: a sinkhole, or the dark passage at the back of
        // a cave in a hillside (not while a boss has it penned in)
        const penned = boss && boss.phase === 'fight'
        if (playing && !under && !penned && s.caveCd <= 0 && !s.climb && s.deadFor <= 0) {
          const hole = holes.find((h) => Math.hypot(h.x - s.pos.x, h.z - s.pos.z) < 10 && Math.abs(s.pos.y - h.y) < 6)
          if (hole) goUnder(hole.plan, hole.x, hole.z, true)
          else {
            const [cx, cz] = cellOf(s.pos.x, s.pos.z)
            const inCave = caveCell(cx, cz)
            if (inCave && s.pos.y < inCave.cave.floor + 20) {
              const at = inCave.cave.cells.findIndex(([x, z]) => x === cx && z === cz)
              if (at >= 6) goUnder(planOf(cx, cz), s.pos.x, s.pos.z, false)
            }
          }
        }
        if (playing && under && s.deadFor <= 0) {
          // Out again by any way but the one it came in, once it's left that
          const exit = under.exits.find((ex) => Math.hypot(ex.x - s.pos.x, ex.z - s.pos.z) < ex.r)
          if (!exit) under.armed = true
          else if (under.armed && s.pos.y < 30) {
            s.caveFor = CAVE_TRIP
            s.caveSwitched = false
            s.caveTrip = { down: false, exit }
            sound.play('jump')
          }
          // Lava burns: a heart, and a yelp up out of it
          if (s.pos.y < 3 && underCellAt(s.pos.x, s.pos.z) === 3) {
            if (s.safeFor <= 0) hurt(s, 'burn')
            if (s.deadFor <= 0) {
              s.vel.set(-s.vel.x * 0.4, 330, -s.vel.z * 0.4)
              s.pos.y = 3.5
            }
            for (let i = 0; i < 6; i++) shatter([piece(s.pos.clone().setY(2), lavaColours[i % 3], 1.6, s.pos.clone().setY(-4))])
          }
        }
      }

      s.phase += dt * (speed / 12 + (s.eatFor > 0 ? 10 : 0))
    }

    // ----- Underground -----
    // The cave system planned for a patch, carved out the first time it's
    // needed: caverns joined by winding tunnels, the hillside mouths' passages
    // leading in, pools or lava in some caverns, pillars of rock, and what
    // lights it all
    const carveUnder = (plan) => {
      if (plan.grid) return plan
      const N = UNDER_REGION
      const grid = new Uint8Array(N * N)
      const path = new Uint8Array(N * N)
      const random = seeded(chunkSeed(plan.ux * 389 + 3, plan.uz * 211 + 9))
      const idx = (i, j) => i * N + j
      const inside = (i, j) => i > 0 && j > 0 && i < N - 1 && j < N - 1
      const disc = (ci, cj, r, mark) => {
        for (let i = Math.floor(ci - r); i <= Math.ceil(ci + r); i++) {
          for (let j = Math.floor(cj - r); j <= Math.ceil(cj + r); j++) {
            if (!inside(i, j) || Math.hypot(i + 0.5 - ci, j + 0.5 - cj) > r) continue
            grid[idx(i, j)] = 1
            if (mark) path[idx(i, j)] = 1
          }
        }
      }
      // Caverns with ragged edges
      plan.rooms.forEach((room) => {
        const R = room.r * 1.3
        for (let i = Math.floor(room.ci - R); i <= Math.ceil(room.ci + R); i++) {
          for (let j = Math.floor(room.cj - R); j <= Math.ceil(room.cj + R); j++) {
            if (!inside(i, j)) continue
            const dx = i + 0.5 - room.ci
            const dz = j + 0.5 - room.cj
            const a = Math.atan2(dz, dx)
            const edge = room.r * (0.8 + 0.45 * noise2(room.ci + Math.cos(a) * 1.3, room.cj + Math.sin(a) * 1.3))
            if (Math.hypot(dx, dz) < edge) grid[idx(i, j)] = 1
          }
        }
        disc(room.ci, room.cj, 1.6, true)
      })
      // Winding tunnels, with a safe line down the middle that nothing blocks
      const tunnel = (a, b) => {
        const dx = b[0] - a[0]
        const dz = b[1] - a[1]
        const len = Math.hypot(dx, dz) || 1
        const nx = -dz / len
        const nz = dx / len
        const amp = 2 + random() * 6
        const waves = 1 + Math.floor(random() * 2)
        const phase = random() * Math.PI
        const width = 2.3 + random() * 1.1
        for (let t = 0; t <= 1; t += 0.4 / len) {
          const bend = Math.sin(t * Math.PI) * Math.sin(t * Math.PI * waves + phase) * amp
          const ci = a[0] + dx * t + nx * bend
          const cj = a[1] + dz * t + nz * bend
          disc(ci, cj, width * (0.85 + 0.3 * noise2(ci * 0.4, cj * 0.4)), false)
          disc(ci, cj, 1.2, true)
        }
      }
      const centres = plan.rooms.map((r) => [r.ci, r.cj])
      // Every cavern joined up (nearest first), and one loop besides
      const joined = [0]
      const links = []
      while (joined.length < centres.length) {
        let best = null
        joined.forEach((a) => {
          centres.forEach((c, b) => {
            if (joined.includes(b)) return
            const d = Math.hypot(c[0] - centres[a][0], c[1] - centres[a][1])
            if (!best || d < best[2]) best = [a, b, d]
          })
        })
        joined.push(best[1])
        links.push([best[0], best[1]])
      }
      if (centres.length >= 4) {
        const a = Math.floor(random() * centres.length)
        const b = (a + 2) % centres.length
        if (!links.some(([p, q]) => (p === a && q === b) || (p === b && q === a))) links.push([a, b])
      }
      links.forEach(([a, b]) => tunnel(centres[a], centres[b]))
      // The hillside mouths: their own hollow, then a passage on into the hill
      const exits = []
      plan.mouths.forEach(({ cave }) => {
        cave.cells.forEach(([x, z]) => disc(x - plan.x0 + 0.5, z - plan.z0 + 0.5, 0.75, true))
        const [dx, dz] = cave.dir
        const back = cave.cells[7]
        const start = [back[0] - plan.x0 + 0.5 - dx * 1.5, back[1] - plan.z0 + 0.5 - dz * 1.5]
        disc(start[0], start[1], 1.6, true)
        let near = centres[0]
        centres.forEach((c) => {
          if (Math.hypot(c[0] - start[0], c[1] - start[1]) < Math.hypot(near[0] - start[0], near[1] - start[1])) near = c
        })
        tunnel(start, near)
        const front = cave.cells[1]
        exits.push({ kind: 'mouth', cave, gx: (front[0] + 0.5) * TERRAIN_CELL, gz: (front[1] + 0.5) * TERRAIN_CELL, r: 12 })
      })
      plan.holes.forEach((hole) => exits.push({ kind: 'hole', hole, gx: hole.gx, gz: hole.gz, r: 13 }))
      const isHole = (room) => plan.holeRooms.includes(room)
      // Pillars of rock in the bigger caverns
      plan.rooms.forEach((room) => {
        if (room.r < 8 || isHole(room)) return
        for (let n = 0; n < 1 + Math.floor(random() * 2); n++) {
          const a = random() * Math.PI * 2
          const pi = Math.floor(room.ci + Math.cos(a) * room.r * 0.5)
          const pj = Math.floor(room.cj + Math.sin(a) * room.r * 0.5)
          ;[[0, 0], [1, 0], [0, 1], [1, 1]].forEach(([i, j]) => {
            if (inside(pi + i, pj + j) && !path[idx(pi + i, pj + j)]) grid[idx(pi + i, pj + j)] = 0
          })
        }
      })
      // Pools, or lava, in some caverns, never across the safe line
      const type = plan.type
      const pools = []
      plan.rooms.forEach((room) => {
        if (isHole(room)) return
        const fill = type.lava ? 3 : type.pools ? 2 : 0
        if (!fill || random() > (type.lava ? 0.85 : 0.6)) return
        const a = random() * Math.PI * 2
        const pr = room.r * (type.lava ? 0.55 : 0.45)
        const pi = room.ci + Math.cos(a) * room.r * 0.35
        const pj = room.cj + Math.sin(a) * room.r * 0.35
        for (let i = Math.floor(pi - pr); i <= Math.ceil(pi + pr); i++) {
          for (let j = Math.floor(pj - pr); j <= Math.ceil(pj + pr); j++) {
            if (!inside(i, j) || Math.hypot(i + 0.5 - pi, j + 0.5 - pj) > pr) continue
            if (grid[idx(i, j)] === 1 && !path[idx(i, j)]) grid[idx(i, j)] = fill
          }
        }
        pools.push({ i: pi, j: pj, r: pr, kind: fill })
      })
      // What lights it: along the walls, spaced out
      const lights = []
      const decor = []
      const spaced = (list, i, j, gap) => list.every((l) => Math.hypot(l.i - i, l.j - j) > gap)
      const DIRS = [[-1, 0], [1, 0], [0, -1], [0, 1]]
      for (let i = 1; i < N - 1; i++) {
        for (let j = 1; j < N - 1; j++) {
          if (grid[idx(i, j)] !== 1) continue
          const rock = DIRS.filter(([di, dj]) => !grid[idx(i + di, j + dj)])
          const h = hash2(plan.x0 + i, plan.z0 + j)
          const h2 = hash2(plan.z0 + j * 7, plan.x0 + i * 3)
          if (rock.length) {
            if (type.light === 'torch' && h < 0.09 && spaced(lights, i, j, 7)) lights.push({ i, j, kind: 'torch', dir: rock[0] })
            else if (type.light === 'crystal' && h < 0.1 && spaced(lights, i, j, 6)) lights.push({ i, j, kind: 'crystal', dir: rock[0] })
            else if (type.glow && type.light !== 'crystal' && h < 0.12 && !path[idx(i, j)] && spaced(lights, i, j, 6)) lights.push({ i, j, kind: 'mushroom', dir: rock[0] })
            else if (h2 < 0.06 && !path[idx(i, j)]) decor.push({ i, j, kind: 'spire', dir: rock[0] })
            if (type.glowworms && h2 > 0.55) decor.push({ i, j, kind: 'worms', dirs: rock })
          } else if (h2 < 0.015 && !path[idx(i, j)]) decor.push({ i, j, kind: 'spire', dir: [0, 0] })
        }
      }
      pools.forEach((pool) => {
        if (pool.kind === 3 || type.light === 'pool') lights.push({ i: pool.i, j: pool.j, kind: pool.kind === 3 ? 'lava' : 'pool', r: pool.r })
      })
      const open = []
      for (let k = 0; k < N * N; k++) if (grid[k] === 1) open.push(k)
      Object.assign(plan, { grid, path, exits, pools, lights, decor, open })
      return plan
    }

    // Lights for the caves: a handful moved each moment to the sources
    // nearest the squirrel. Only switched on underground, so the woods above
    // don't pay for them.
    const caveLights = Array.from({ length: CAVE_LIGHTS }, () => {
      const light = new PointLight('#ffaa55', 0, 160, 2)
      light.visible = false
      woods.add(light)
      return light
    })
    const caveRockMat = new MeshLambertMaterial({ vertexColors: true })
    const caveGlowMat = new MeshBasicMaterial({ vertexColors: true })
    const lavaMat = new MeshBasicMaterial({ vertexColors: true })
    const glowWaterMat = new MeshBasicMaterial({ color: '#2fd8ff', transparent: true, opacity: 0.82 })
    const ladderRail = new Color('#6e4524')
    const ladderRung = new Color('#a8743f')
    const flameMat = new MeshBasicMaterial({ color: '#ffb347' })
    const FLAMES = 90
    const flames = new InstancedMesh(keep(new BoxGeometry(2.6, 4.2, 2.6).translate(0, 2.1, 0)), flameMat, FLAMES)
    flames.frustumCulled = false
    flames.count = 0
    woods.add(flames)
    const lavaColours = ['#ff5a14', '#ff8a1e', '#ffb030', '#e8400c'].map((c) => new Color(c))
    const LIGHT_LOOK = {
      torch: { colour: '#ffa24a', power: 1.6, range: 150, y: 36, flicker: true },
      crystal: { power: 1.3, range: 130, y: 14 },
      mushroom: { power: 1.1, range: 110, y: 8 },
      lava: { colour: '#ff6a20', power: 2, range: 190, y: 10, flicker: true },
      pool: { power: 1.2, range: 150, y: 8 },
      shaft: { colour: '#fff1cf', power: 1.8, range: 180, y: 60 },
    }

    // The cave's look, built when the squirrel goes in
    const buildUnder = (plan) => {
      const N = UNDER_REGION
      const C = TERRAIN_CELL
      const { grid, type } = plan
      const rock = shapes()
      const glow = shapes()
      const lava = shapes()
      const water = shapes()
      const floorC = new Color(type.floor)
      const wallC = new Color(type.wall)
      const bandC = new Color(type.band)
      const topC = new Color(type.wall).multiplyScalar(0.12)
      const roofC = new Color(type.band).multiplyScalar(0.45)
      const glowC = new Color(type.glow || '#ffffff')
      const tint = new Color()
      const at = (i, j) => (i < 0 || j < 0 || i >= N || j >= N ? 0 : grid[i * N + j])
      const nearOpen = (i, j) => {
        for (let a = -4; a <= 4; a++) for (let b = -4; b <= 4; b++) if (at(i + a, j + b)) return true
        return false
      }
      for (let i = 0; i < N; i++) {
        for (let j = 0; j < N; j++) {
          const x0 = i * C
          const z0 = j * C
          const x1 = x0 + C
          const z1 = z0 + C
          const cell = at(i, j)
          if (!cell) {
            // The top of the rock, near black, so passages beyond don't show through
            if (nearOpen(i, j)) rock.quad([x0, UNDER_WALL, z0], [x0, UNDER_WALL, z1], [x1, UNDER_WALL, z1], [x1, UNDER_WALL, z0], [0, 1, 0], topC)
            continue
          }
          const h = hash2(plan.x0 + i, plan.z0 + j)
          tint.copy(floorC).offsetHSL(0, 0, (h - 0.5) * 0.06 - (cell > 1 ? 0.12 : 0))
          rock.quad([x0, 0, z0], [x0, 0, z1], [x1, 0, z1], [x1, 0, z0], [0, 1, 0], tint)
          rock.quad([x0, UNDER_WALL, z0], [x1, UNDER_WALL, z0], [x1, UNDER_WALL, z1], [x0, UNDER_WALL, z1], [0, -1, 0], roofC)
          if (cell === 2) water.quad([x0, 0.45, z0], [x0, 0.45, z1], [x1, 0.45, z1], [x1, 0.45, z0], [0, 1, 0], wallC)
          if (cell === 3) lava.quad([x0, 1, z0], [x0, 1, z1], [x1, 1, z1], [x1, 1, z0], [0, 1, 0], lavaColours[Math.floor(h * 4)])
          // Walls round it, two bands of rock
          ;[
            [i - 1, j, [x0, z0], [x0, z1], [1, 0, 0]],
            [i + 1, j, [x1, z1], [x1, z0], [-1, 0, 0]],
            [i, j - 1, [x1, z0], [x0, z0], [0, 0, 1]],
            [i, j + 1, [x0, z1], [x1, z1], [0, 0, -1]],
          ].forEach(([ni, nj, a, b, n]) => {
            if (at(ni, nj)) return
            tint.copy(wallC).offsetHSL(0, 0, (h - 0.5) * 0.08)
            rock.wall(a[0], a[1], b[0], b[1], 0, UNDER_WALL * 0.45, n, tint)
            tint.copy(bandC).offsetHSL(0, 0, (h - 0.5) * 0.08)
            rock.wall(a[0], a[1], b[0], b[1], UNDER_WALL * 0.45, UNDER_WALL, n, tint)
          })
        }
      }
      // Spires of rock, and glowworms speckling the walls
      plan.decor.forEach((d) => {
        const h = hash2(d.i * 13 + plan.x0, d.j * 7 + plan.z0)
        const dir = d.dir || [0, 0]
        const cx = (d.i + 0.5) * C + dir[0] * 2.5
        const cz = (d.j + 0.5) * C + dir[1] * 2.5
        if (d.kind === 'spire') {
          const tall = 8 + h * 14
          tint.copy(wallC).offsetHSL(0, 0, 0.05)
          rock.cube(cx - 3, 0, cz - 3, cx + 3, tall * 0.45, cz + 3, tint)
          rock.cube(cx - 2, tall * 0.45, cz - 2, cx + 2, tall * 0.8, cz + 2, tint)
          rock.cube(cx - 1, tall * 0.8, cz - 1, cx + 1, tall, cz + 1, tint)
        } else if (d.kind === 'worms') {
          tint.set(type.glowworms)
          d.dirs.forEach(([di, dj]) => {
            for (let k = 0; k < 3; k++) {
              const y = 32 + hash2(d.i * 5 + k, d.j * 9 - k) * 34
              const along = (hash2(d.i + k * 11, d.j * 3) - 0.5) * C * 0.8
              const px = (d.i + 0.5) * C + di * (C / 2 - 0.3) + (dj ? along : 0)
              const pz = (d.j + 0.5) * C + dj * (C / 2 - 0.3) + (di ? along : 0)
              glow.cube(px - 0.5, y - 0.5, pz - 0.5, px + 0.5, y + 0.5, pz + 0.5, tint)
            }
          })
        }
      })
      // The light sources themselves
      const torches = []
      const lights = []
      plan.lights.forEach((l) => {
        const look = LIGHT_LOOK[l.kind]
        const cx = (l.i + 0.5) * C
        const cz = (l.j + 0.5) * C
        let lx = cx
        let lz = cz
        let colour = look.colour || type.glow || '#ffffff'
        if (l.kind === 'torch') {
          // A bracket on the wall with a flame on top
          lx = cx + l.dir[0] * (C / 2 - 1.5)
          lz = cz + l.dir[1] * (C / 2 - 1.5)
          rock.cube(lx - 0.8, 24, lz - 0.8, lx + 0.8, look.y - 2, lz + 0.8, new Color('#5a3a1e'))
          torches.push(new Vector3(lx, look.y - 2, lz))
          lx -= l.dir[0] * 4
          lz -= l.dir[1] * 4
        } else if (l.kind === 'crystal') {
          // A cluster of glowing crystals, leaning on the wall
          const bx = cx + l.dir[0] * 2.5
          const bz = cz + l.dir[1] * 2.5
          for (let k = 0; k < 4; k++) {
            const h = hash2(l.i * 3 + k, l.j * 5 - k)
            const px = bx + (hash2(l.i + k, l.j) - 0.5) * 7
            const pz = bz + (hash2(l.i, l.j + k) - 0.5) * 7
            const tall = 6 + h * 14
            const w = 1.2 + h * 1.2
            tint.copy(glowC).offsetHSL(0, 0, (h - 0.5) * 0.25)
            glow.cube(px - w, 0, pz - w, px + w, tall, pz + w, tint)
            glow.cube(px - w * 0.5, tall, pz - w * 0.5, px + w * 0.5, tall + 3, pz + w * 0.5, tint.clone().offsetHSL(0, 0, 0.15))
          }
        } else if (l.kind === 'mushroom') {
          // A clump of glowing mushrooms
          for (let k = 0; k < 4; k++) {
            const h = hash2(l.i * 7 + k, l.j * 3 + k)
            const px = cx + l.dir[0] * 2 + (hash2(l.i + k, l.j * 2) - 0.5) * 8
            const pz = cz + l.dir[1] * 2 + (hash2(l.i * 2, l.j + k) - 0.5) * 8
            const tall = 2 + h * 5
            rock.cube(px - 0.6, 0, pz - 0.6, px + 0.6, tall, pz + 0.6, stemColour)
            const w = 1.5 + h * 1.6
            glow.cube(px - w, tall, pz - w, px + w, tall + 1.4, pz + w, tint.copy(glowC).offsetHSL((h - 0.5) * 0.08, 0, 0))
          }
        }
        lights.push({ x: plan.x0 * C + lx, z: plan.z0 * C + lz, y: look.y, colour: new Color(colour), power: look.power, range: look.range, flicker: !!look.flicker })
      })
      // A wooden ladder up each way out, lit by the daylight (or moonlight)
      // coming down it
      plan.exits.forEach((ex) => {
        const lx = ex.gx - plan.x0 * C
        const lz = ex.gz - plan.z0 * C
        const top = UNDER_WALL + 60
        rock.cube(lx - 8, 0, lz - 1.3, lx - 5.4, top, lz + 1.3, ladderRail)
        rock.cube(lx + 5.4, 0, lz - 1.3, lx + 8, top, lz + 1.3, ladderRail)
        for (let y = 6, k = 0; y < top - 2; y += 8, k++) {
          rock.cube(lx - 5.4, y, lz - 1, lx + 5.4, y + 1.8, lz + 1, tint.copy(ladderRung).offsetHSL(0, 0, ((k * 37) % 7) * 0.012 - 0.03))
        }
        lights.push({ x: ex.gx, z: ex.gz, y: LIGHT_LOOK.shaft.y, colour: new Color(LIGHT_LOOK.shaft.colour), power: LIGHT_LOOK.shaft.power, range: LIGHT_LOOK.shaft.range, shaft: true })
      })
      const group = new Group()
      const meshes = [new Mesh(rock.geometry(), caveRockMat)]
      if (glow.size()) meshes.push(new Mesh(glow.geometry(), caveGlowMat))
      if (lava.size()) meshes.push(new Mesh(lava.geometry(), lavaMat))
      if (water.size()) meshes.push(new Mesh(water.geometry(), type.light === 'pool' ? glowWaterMat : waterMat))
      group.add(...meshes)
      return { group, meshes, torches, lights }
    }

    // Down into a cave system, landing at (x, z): dropping in through a
    // sinkhole, or walking in from a hillside cave
    const goUnder = (plan, x, z, fall) => {
      const s = world.squirrel
      if (!plan) return
      s.caveFor = CAVE_TRIP
      s.caveSwitched = false
      s.caveTrip = { down: true, plan, x, z, fall }
      sound.play(fall ? 'jump' : 'munch')
    }
    // Clear out whatever was about up top (or down below) on the way through
    const clearAround = () => {
      spiders.forEach((sp) => {
        if (sp.boss) return
        sp.alive = false
        sp.deadFor = 0
        sp.respawnIn = rand(1, 3)
      })
      shots.forEach((sh) => woods.remove(sh.mesh))
      shots = []
      world.acorns = []
      clearHearts()
      clearPickups()
    }
    const enterUnder = (plan, x, z, fall) => {
      const s = world.squirrel
      carveUnder(plan)
      const built = buildUnder(plan)
      const C = TERRAIN_CELL
      built.group.position.set(plan.x0 * C - origin.x, 0, plan.z0 * C - origin.z)
      woods.add(built.group)
      under = {
        plan,
        type: plan.type,
        grid: plan.grid,
        x0: plan.x0,
        z0: plan.z0,
        built,
        open: plan.open,
        lights: built.lights.map((l) => ({ ...l, x: l.x - origin.x, z: l.z - origin.z })),
        exits: plan.exits.map((ex) => ({ ...ex, x: ex.gx - origin.x, z: ex.gz - origin.z })),
        pools: plan.pools
          .filter((pool) => pool.kind === 2)
          .map((pool) => ({ x: (plan.x0 + pool.i) * C - origin.x, z: (plan.z0 + pool.j) * C - origin.z, rx: pool.r * C, rz: pool.r * C })),
        void: new Color(plan.type.void),
        near: [],
        pickIn: 0,
        armed: false,
      }
      clearAround()
      indexChunks()
      caveLights.forEach((light) => {
        light.visible = gv('lighting') > 0
      })
      // Landed in the open, wherever it came in
      s.pos.set(x, 0, z)
      if (!underCellAt(x, z)) {
        const spot = openSpot(0, 120)
        if (spot) s.pos.set(spot.x, 0, spot.z)
      }
      s.pos.y = fall ? 55 : 0
      s.caveFall = fall
      s.safeFor = Math.max(s.safeFor, 1)
      s.climb = null
      // Been down here before: just what it left behind
      const kept = caveMemory.get(plan.id)
      if (kept) {
        kept.nuts.forEach((nut) => {
          spawnAcorn(new Vector3(nut.x - origin.x, nut.y, nut.z - origin.z), nut.super)
          const acorn = world.acorns[world.acorns.length - 1]
          Object.assign(acorn, { kind: nut.kind, caveId: nut.id })
          acorn.vel.set(0, 0, 0)
        })
        if (kept.prize) {
          const { kind, x, z, id } = kept.prize
          const mesh = new Mesh(powerGeos[kind], powerMats[kind])
          woods.add(mesh)
          pickups.push({ kind, mesh, pos: new Vector3(x - origin.x, 0, z - origin.z), phase: rand(0, 6), id })
        }
        showToast(plan.type.name)
        return
      }
      // Nuts stashed about the caverns, and a hoard in the one furthest in
      const exits = under.exits
      let deepest = null
      plan.rooms.forEach((room) => {
        const rx = (plan.x0 + room.ci) * C - origin.x
        const rz = (plan.z0 + room.cj) * C - origin.z
        const d = Math.min(...exits.map((ex) => Math.hypot(ex.x - rx, ex.z - rz)))
        if (!deepest || d > deepest.d) deepest = { room, d, rx, rz }
      })
      plan.rooms.forEach((room, r) => {
        const hoard = deepest && deepest.room === room
        const count = hoard ? 8 : 2 + Math.floor(Math.random() * 3)
        for (let n = 0; n < count; n++) {
          const id = `cave:${plan.id}:${r}:${n}`
          if (world.harvested.has(id)) continue
          for (let tries = 0; tries < 12; tries++) {
            const a = Math.random() * Math.PI * 2
            const d = Math.random() * room.r * 0.7 * C
            const nx = (plan.x0 + room.ci) * C - origin.x + Math.cos(a) * d
            const nz = (plan.z0 + room.cj) * C - origin.z + Math.sin(a) * d
            if (underCellAt(nx, nz) !== 1) continue
            spawnAcorn(new Vector3(nx, AC_R + 1, nz), Math.random() < (hoard ? 0.15 : 0.04))
            const acorn = world.acorns[world.acorns.length - 1]
            acorn.vel.set(0, 0, 0)
            acorn.caveId = id
            break
          }
        }
      })
      // and a power-up in the hoard
      const prize = `cave:${plan.id}:prize`
      if (deepest && !world.harvested.has(prize)) {
        const kind = POWER_KINDS[Math.floor(Math.random() * POWER_KINDS.length)]
        const mesh = new Mesh(powerGeos[kind], powerMats[kind])
        woods.add(mesh)
        pickups.push({ kind, mesh, pos: new Vector3(deepest.rx, 0, deepest.rz), phase: rand(0, 6), id: prize })
      }
      showToast(plan.type.name)
    }
    const leaveUnder = () => {
      if (!under) return
      const prize = pickups.find((pk) => pk.id === `cave:${under.plan.id}:prize`)
      caveMemory.set(under.plan.id, {
        exits: under.exits.map((ex) => [ex.gx, ex.gz]),
        nuts: world.acorns
          .filter((a) => a.caveId)
          .map((a) => ({ id: a.caveId, kind: a.kind, super: a.super, x: a.pos.x + origin.x, y: a.pos.y, z: a.pos.z + origin.z })),
        prize: prize && { kind: prize.kind, id: prize.id, x: prize.pos.x + origin.x, z: prize.pos.z + origin.z },
      })
      woods.remove(under.built.group)
      under.built.meshes.forEach((m) => m.geometry.dispose())
      under = null
      caveLights.forEach((light) => {
        light.visible = false
      })
      flames.count = 0
      clearAround()
      indexChunks()
    }
    // Back up into the daylight, out of a sinkhole or a hillside cave
    const surface = (exit) => {
      const s = world.squirrel
      leaveUnder()
      if (exit.kind === 'hole') {
        const f = facing(s)
        const x = exit.x + f.x * 24
        const z = exit.z + f.z * 24
        s.pos.set(x, floorAt(x, z) + 4, z)
        s.vel.set(f.x * 60, 260, f.z * 60)
      } else {
        const [dx, dz] = exit.cave.dir
        const x = exit.cave.gx - origin.x + dx * 10
        const z = exit.cave.gz - origin.z + dz * 10
        s.pos.set(x, floorAt(x, z), z)
        s.heading = Math.atan2(-dz, dx)
        s.vel.set(0, 0, 0)
      }
      s.caveCd = 2
      s.safeFor = Math.max(s.safeFor, 1)
    }
    // A random open spot in the caves between min and max from the squirrel
    const openSpot = (min, max) => {
      if (!under || !under.open.length) return null
      const s = world.squirrel
      for (let k = 0; k < 60; k++) {
        const cell = under.open[Math.floor(Math.random() * under.open.length)]
        const x = (under.x0 + Math.floor(cell / UNDER_REGION) + 0.5) * TERRAIN_CELL - origin.x
        const z = (under.z0 + (cell % UNDER_REGION) + 0.5) * TERRAIN_CELL - origin.z
        const d = Math.hypot(x - s.pos.x, z - s.pos.z)
        if (d >= min && d <= max) return new Vector3(x, 0, z)
      }
      return null
    }
    // Torches flicker, lava glows, the nearest sources light the caves, and
    // daylight falls down the shafts (moonlight by night)
    const forgetCaves = () => {
      const s = world.squirrel
      const far = DROP_RADIUS * gv('view')
      caveMemory.forEach((kept, id) => {
        if (kept.exits.some(([gx, gz]) => Math.hypot(gx - origin.x - s.pos.x, gz - origin.z - s.pos.z) <= far)) return
        caveMemory.delete(id)
        world.harvested.forEach((h) => {
          if (typeof h === 'string' && h.startsWith(`cave:${id}:`)) world.harvested.delete(h)
        })
      })
    }
    const updateUnder = (dt) => {
      if (!under) {
        flames.count = 0
        forgetCaves()
        return
      }
      const s = world.squirrel
      under.pickIn -= dt
      if (under.pickIn <= 0) {
        under.pickIn = 0.25
        under.near = under.lights
          .map((l) => [l, Math.hypot(l.x - s.pos.x, l.z - s.pos.z)])
          .filter(([, d]) => d < 450)
          .sort((a, b) => a[1] - b[1])
          .slice(0, CAVE_LIGHTS)
          .map(([l]) => l)
      }
      caveLights.forEach((light, i) => {
        const l = under.near[i]
        if (!l) {
          light.intensity = 0
          return
        }
        const flick = l.flicker ? 0.8 + 0.2 * Math.sin(clock * 17 + l.x) * Math.sin(clock * 7.3 + l.z) : 1
        light.position.set(l.x, l.y, l.z)
        light.color.copy(l.colour)
        light.distance = l.range
        light.intensity = l.power * flick * view.e * (l.shaft ? 0.35 + 0.65 * daylight : 1)
      })
      let n = 0
      const gp = under.built.group.position
      under.built.torches.forEach((t, i) => {
        if (n >= FLAMES || Math.hypot(t.x + gp.x - s.pos.x, t.z + gp.z - s.pos.z) > 600) return
        bugQuat.identity()
        const f = 0.8 + 0.25 * Math.sin(clock * 19 + i * 1.7) + 0.1 * Math.sin(clock * 31 + i)
        bugScale.set(f, f * (1.1 + 0.2 * Math.sin(clock * 23 + i)), f).multiplyScalar(Math.max(0.001, view.e))
        rainAt.set(t.x + gp.x, t.y, t.z + gp.z)
        bugMatrix.compose(rainAt, bugQuat, bugScale)
        flames.setMatrixAt(n++, bugMatrix)
      })
      flames.count = n
      flames.instanceMatrix.needsUpdate = true
      lavaMat.color.setScalar(0.85 + 0.15 * Math.sin(clock * 2.2))
      // Lava spits up a glob now and then
      if (under.type.lava && !world.paused && Math.random() < dt * 3) {
        const spot = openSpot(0, 260)
        const pool = under.plan.pools[Math.floor(Math.random() * under.plan.pools.length)]
        if (pool && spot) {
          const px = (under.x0 + pool.i) * TERRAIN_CELL - origin.x + rand(-pool.r, pool.r) * 4
          const pz = (under.z0 + pool.j) * TERRAIN_CELL - origin.z + rand(-pool.r, pool.r) * 4
          if (underCellAt(px, pz) === 3) shatter([piece(new Vector3(px, 2, pz), lavaColours[Math.floor(Math.random() * 3)], 2, new Vector3(px + rand(-1, 1), -6, pz + rand(-1, 1)))])
        }
      }
    }

    // Hugging the trunk: W climbs, S climbs down, A, D or Space leaps off
    const climb = (s, dt, input) => {
      const t = s.climb
      const away = new Vector3(s.pos.x - t.x, 0, s.pos.z - t.z)
      if (away.lengthSq() === 0) away.set(0, 0, 1)
      away.normalize()
      if (input.x !== 0) {
        letGo(s)
        return
      }
      s.pos.y = clamp(s.pos.y - input.z * CLIMB_SPEED * dt, t.baseY || 0, t.canopyY)
      if (s.pos.y === t.canopyY && input.z < 0) {
        // Up through the leaves and onto the top
        s.climb = null
        s.pos.set(t.x, treeTopAt(t, t.x, t.z), t.z)
        s.vel.set(0, 0, 0)
        return
      }
      s.pos.x = t.x + away.x * (t.trunkRadius + 1)
      s.pos.z = t.z + away.z * (t.trunkRadius + 1)
      s.heading = Math.atan2(away.z, -away.x)
      s.vel.set(0, 0, 0)
      s.phase += dt * Math.abs(input.z) * 10
      if (s.pos.y === (t.baseY || 0) && input.z > 0) s.climb = null
    }
    const letGo = (s) => {
      const t = s.climb
      s.climb = null
      const away = new Vector3(s.pos.x - t.x, 0, s.pos.z - t.z).normalize()
      s.vel.set(away.x * 140, 180, away.z * 140)
      s.heading = Math.atan2(-away.z, away.x)
    }

    // A short hop forward that lands claws-first
    const attack = () => {
      const s = world.squirrel
      if (s.deadFor > 0 || s.attackCd > 0 || s.climb || !s.grounded) return
      s.attackFor = POUNCE_TIME
      s.attackCd = POUNCE_COOLDOWN
      s.eatFor = 0
      s.struck = new Set()
      sound.play('pounce')
      const f = facing(s)
      s.vel.set(f.x * 110, 190, f.z * 110)
    }

    // ----- Spiders -----
    // Somewhere in the woods around the squirrel, not on top of it
    const placeSpider = (sp) => {
      const s = world.squirrel
      // On the harder levels many turn up in the squirrel's path. Either way
      // they come from out of sight: off screen, or far off in the haze.
      const ahead = flat(s.vel) > 30 && Math.random() < level().ahead
      // Underground, somewhere out of sight in the caverns
      const spot = under ? openSpot(220, 650) || openSpot(120, 900) : null
      if (under) {
        if (spot) sp.pos.copy(spot)
        sp.pos.y = sp.cfg.fly ? 34 : 0
      }
      for (let i = 0; i < 10 && !under; i++) {
        const a = ahead ? Math.atan2(s.vel.z, s.vel.x) + rand(-0.7, 0.7) : rand(0, Math.PI * 2)
        const r = rand(480, 820) * gv('view')
        sp.pos.set(s.pos.x + Math.cos(a) * r, 0, s.pos.z + Math.sin(a) * r)
        sp.pos.y = floorAt(sp.pos.x, sp.pos.z)
        // Bats stream out of a cave mouth if there's one about
        if (sp.cfg.fly) {
          const roost = caves.find((c) => {
            const d = Math.hypot(c.x - s.pos.x, c.z - s.pos.z)
            return d > 200 && d < viewRadius() && Math.random() < 0.7
          })
          if (roost) sp.pos.set(roost.x + rand(-6, 6), roost.floor + 10, roost.z + rand(-6, 6))
          else sp.pos.y += 40
        }
        if (r > 660 * gv('view') || !onScreen(sp.pos.x, 8, sp.pos.z)) break
      }
      sp.hp = sp.cfg.hp + level().hp
      sp.knock.set(0, 0, 0)
      sp.windup = 0
      sp.leapFor = 0
      sp.vy = 0
      sp.pounceCd = 1
      sp.flashFor = 0
      sp.alive = true
      sp.deadFor = 0
      sp.wanderTo = null
      // By day, some are found asleep
      sp.asleep = daylight > 0.5 && Math.random() < level().sleep
      sp.alertFor = 0
    }

    let daylight = 1
    let dayRoll = Math.random()
    let wasNight = false
    const updateSpiders = (dt, night) => {
      const s = world.squirrel
      const active = view.p > 0.5
      const hazards = world.staged && view.p > 0.9 && s.introFor <= 0
      const isNight = night > 0.5
      daylight = 1 - night
      if (wasNight && !isNight) dayRoll = Math.random()
      wasNight = isNight
      // Only the current biome's creatures roam; others slink off
      const enemies = under ? under.type.enemies : [].concat(BIOMES[biome].enemy)
      const want = (sp) => {
        // The caves are close quarters: only a few at a time, 2 on the easier
        // levels up to 4 on Insane, and a bat or two
        if (sp.cfg.fly && under) return level().creatures >= 1.5 ? 2 : 1
        if (under) {
          const k = enemies.indexOf(sp.kind)
          if (k < 0) return 0
          const total = clamp(Math.round(1.5 + level().creatures * 0.9), 2, 4)
          return Math.floor(total / enemies.length) + (k < total % enemies.length ? 1 : 0)
        }
        if (sp.cfg.fly) {
          if (!isNight || (boss && boss.phase === 'fight')) return 0
          return Math.max(1, Math.round(sp.cfg.night * level().creatures))
        }
        if (!enemies.includes(sp.kind)) return 0
        if (boss && boss.phase === 'fight') return 0
        const count = isNight ? sp.cfg.night : sp.cfg.day + (dayRoll < 0.25 ? 1 : 0)
        return Math.max(1, Math.round((count * level().creatures) / enemies.length))
      }
      spiders.forEach((sp) => {
        if (sp.boss) return
        const i = sp.index
        const wanted = want(sp)
        const cfg = sp.cfg
        sp.flashFor = Math.max(0, sp.flashFor - dt)
        sp.chargedFor = Math.max(0, (sp.chargedFor || 0) - dt)
        sp.shotCd = Math.max(0, (sp.shotCd === undefined ? rand(1, 3) : sp.shotCd) - dt)
        if (!sp.alive) {
          if (sp.deadFor > 0) {
            sp.deadFor = Math.max(0, sp.deadFor - dt)
            return
          }
          if (i >= wanted || !active) return
          // and slower to come back underground
          sp.respawnIn -= (dt / level().respawn) * (under ? 0.4 : 1)
          if (sp.respawnIn <= 0) placeSpider(sp)
          return
        }
        if (!active) return
        const toSquirrel = s.pos.clone().sub(sp.pos).setY(0)
        const dist = toSquirrel.length()
        const wasX = sp.pos.x
        const wasZ = sp.pos.z
        if (dist > SPIDER_LEASH * gv('view')) {
          placeSpider(sp)
          return
        }
        // Come daybreak the extra night spiders slink off and vanish into the fog
        const leaving = i >= wanted
        sp.leaving = leaving
        if (leaving && dist > 320 && (dist > 660 * gv('view') || !onScreen(sp.pos.x, 8, sp.pos.z))) {
          sp.alive = false
          sp.respawnIn = rand(2, 6)
          return
        }
        let dir
        let speed = cfg.wander
        sp.pounceCd = Math.max(0, sp.pounceCd - dt)
        const target = toSquirrel.clone().normalize()
        // A super squirrel finishes off any creature it touches, whatever the
        // creature is up to: asleep, winding up, mid-pounce or reeling
        if (s.superFor > 0 && s.deadFor <= 0 && dist < 14 + cfg.shadow[0] / 2 && Math.abs(sp.pos.y - s.pos.y) < 24) {
          sp.alive = false
          sp.deadFor = SPIDER_DEATH
          sp.respawnIn = rand(8, 14)
          sp.chargedFor = 0
          sound.play('kill')
          return
        }
        if (sp.asleep) {
          // Creeping past is fine; running close, a pounce nearby, a hit or
          // nightfall wakes it
          const pace = flat(s.vel)
          const stir = pace > PLAYER_SPEED + 20 ? 110 : pace > 30 ? 40 : 16
          if (isNight || sp.hp < cfg.hp + level().hp || (hazards && (dist < stir || (s.attackFor > 0 && dist < 70)))) {
            sp.asleep = false
            sp.alertFor = 0.6
            if (dist < 300) sound.play('alert')
          }
        }
        if (sp.knock.lengthSq() > 1) {
          // Reeling from a bite
          sp.pos.addScaledVector(sp.knock, dt)
          sp.knock.multiplyScalar(Math.exp(-7 * dt))
          sp.leapFor = 0
          sp.windup = 0
          speed = 0
          dir = target
        } else if (sp.asleep || sp.alertFor > 0) {
          // Dozing, or just woken and getting its bearings
          speed = 0
          dir = sp.asleep ? new Vector3(Math.cos(sp.heading), 0, -Math.sin(sp.heading)) : target
          sp.alertFor = Math.max(0, sp.alertFor - dt)
        } else if (sp.leapFor > 0) {
          // Mid-pounce: only a pounce that lands on the squirrel bites
          sp.leapFor -= dt
          sp.pos.addScaledVector(sp.leap, dt)
          speed = 0
          dir = sp.leap.clone().normalize()
          const reach = sp.pos.distanceTo(s.pos.clone().setY(s.pos.y + SQ_H / 2))
          if (!sp.bit && hazards && s.deadFor <= 0 && s.safeFor <= 0 && reach < 18 * (cfg.big || 1)) {
            sp.bit = true
            hurt(s, 'bite')
          }
        } else if (sp.stingFor > 0) {
          // The tail lashes over: halfway through it lands where it's aimed
          sp.stingFor -= dt
          speed = 0
          dir = sp.stingDir || target
          if (!sp.stung && sp.stingFor < STING_TIME * 0.5) {
            sp.stung = true
            const tip = sp.pos.clone().addScaledVector(dir, 20)
            if (hazards && s.deadFor <= 0 && s.safeFor <= 0 && flat(s.pos.clone().sub(tip)) < 15 && Math.abs(s.pos.y - sp.pos.y) < 20) hurt(s, 'bite')
          }
        } else if (sp.windup > 0) {
          // Crouching to spring, or cocking the tail
          sp.windup -= dt
          speed = 0
          dir = target
          if (sp.windup <= 0 && cfg.sting) {
            sp.stingFor = STING_TIME
            sp.stung = false
            sp.stingDir = target.clone()
            sound.play('pounce')
          } else if (sp.windup <= 0) {
            // Aimed to come down right where the squirrel is, not sail over it
            const airTime = (2 * cfg.lift) / GRAVITY
            // Harder creatures aim ahead of a squirrel on the move
            const aim = s.pos.clone().addScaledVector(s.vel, airTime * level().lead).sub(sp.pos).setY(0)
            const span = aim.length()
            sp.leap = aim.normalize().multiplyScalar(Math.min(cfg.leap * (1 + level().lead * 0.4), span / airTime))
            sp.vy = cfg.lift
            sp.leapFor = cfg.leapTime
            sp.bit = false
          }
        } else if (leaving) {
          dir = target.clone().negate()
          speed = cfg.wander * 1.5
        } else if (hazards && s.deadFor <= 0 && dist < cfg.sight * level().sight * 1.3) {
          dir = target
          speed = cfg.chase
          const reach = cfg.reach * level().reach
          // In the wet they spit from range; charged ones spark, and more often
          const charged = sp.chargedFor > 0
          const shoots = !cfg.fly && (charged || ((wetness() !== 'clear' || level().spitDry) && sp.kind !== 'wolf'))
          if (shoots && sp.shotCd <= 0 && shotsIn <= 0 && dist > reach && s.pos.y - sp.pos.y < 40) {
            shoot(sp, charged)
            sp.shotCd = (charged ? rand(2.5, 4) : rand(4, 7)) * level().shots
            shotsIn = 1.8 * level().shots // and never two in quick succession from the pack
          }
          if (dist > cfg.sight * level().sight) {
            // Only close enough to shoot from; keep closing in
          } else if (dist < reach && sp.pounceCd <= 0 && s.pos.y - sp.pos.y < 12 && !(sp.stingFor > 0)) {
            sp.windup = cfg.windup * level().attack
            sp.pounceCd = cfg.cooldown * level().attack
          } else if (dist < reach * 0.6) {
            // Circles in close rather than walking into it
            speed = 0
          }
        } else {
          if (!sp.wanderTo || flat(sp.wanderTo.clone().sub(sp.pos)) < 6) {
            sp.wanderTo = new Vector3(sp.pos.x + rand(-150, 150), 0, sp.pos.z + rand(-150, 150))
          }
          dir = sp.wanderTo.clone().sub(sp.pos).setY(0).normalize()
        }
        // Charged creatures are quicker, but on any level a running squirrel
        // comfortably gets away: none can top 80% of its running pace
        const boost = sp.chargedFor > 0 ? 1.25 : 1
        sp.pos.addScaledVector(dir, Math.min(speed * level().speed * boost, SPRINT_SPEED * 0.8) * dt)
        // Underground the rock is solid, and only bats cross lava
        if (under) {
          const bad = (x, z) => {
            const c = underCellAt(x, z)
            return !c || (c === 3 && !cfg.fly)
          }
          if (bad(sp.pos.x, sp.pos.z)) {
            if (!bad(wasX, sp.pos.z)) sp.pos.x = wasX
            else if (!bad(sp.pos.x, wasZ)) sp.pos.z = wasZ
            else {
              sp.pos.x = wasX
              sp.pos.z = wasZ
            }
            sp.wanderTo = null
            sp.knock.set(0, 0, 0)
          }
        }
        // Over the hills: they scramble up and drop down the blocks
        const floorC = floorAt(sp.pos.x, sp.pos.z)
        if (cfg.fly) {
          // Bats flit about overhead, swooping down on the squirrel
          const swooping = sp.leapFor > 0
          const height = swooping ? s.pos.y + 6 : floorC + 34 + Math.sin(sp.phase * 0.3) * 5
          sp.pos.y = lerp(sp.pos.y, height, Math.min(1, dt * (swooping ? 9 : 2)))
          sp.vy = 0
        } else if (sp.pos.y > floorC || sp.vy > 0) {
          sp.vy -= GRAVITY * dt
          sp.pos.y = Math.max(floorC, sp.pos.y + sp.vy * dt)
          if (sp.pos.y === floorC) {
            sp.vy = 0
            sp.leapFor = 0
          }
        } else {
          sp.pos.y = floorC
        }
        dodgeTrunks(sp.pos, 8, null)
        sp.heading = Math.atan2(-dir.z, dir.x)
        sp.phase += dt * Math.max(speed, sp.leapFor > 0 ? 120 : 0) * 0.25
        sp.moving = speed > 0

        // A pouncing squirrel bites first: once per pounce, two bites to kill
        if (s.attackFor > 0 && !s.struck.has(sp) && flat(mouthOf(s).sub(sp.pos)) < 22 * (cfg.big || 1) && Math.abs(sp.pos.y - s.pos.y) < 26 * (cfg.big || 1)) {
          s.struck.add(sp)
          sp.hp -= 1
          sp.flashFor = 0.25
          sp.knock.copy(toSquirrel).setY(0).normalize().multiplyScalar(-260)
          sound.play(sp.hp <= 0 ? 'kill' : 'hit')
          if (sp.hp <= 0) {
            // A charged creature leaves a nut behind
            if (sp.chargedFor > 0) spawnAcorn(sp.pos.clone().setY(AC_R + 6), Math.random() < 0.25)
            sp.alive = false
            sp.deadFor = SPIDER_DEATH
            sp.respawnIn = rand(8, 14)
            sp.chargedFor = 0
          }
        }
      })
    }

    // Spit, webs and sparks: lobbed at where the squirrel is, dodged by moving
    const SHOT_LOOK = {
      spider: '#f2f2f2', // web
      frog: '#7dff6a', // spit
      snake: '#c45cff', // venom
      croc: '#6ac8ff', // a spray of water
      scorpion: '#d8ff5a', // venom
      iguana: '#9aff7a',
      wolf: '#9ff4ff',
      bear: '#f4faff', // snowballs
    }
    const shotGeo = keep(new BoxGeometry(3, 3, 3))
    const shotMats = {}
    Object.keys(SHOT_LOOK).forEach((k) => {
      shotMats[k] = new MeshBasicMaterial({ color: SHOT_LOOK[k] })
    })
    const sparkMat = new MeshBasicMaterial({ color: '#bff8ff' })
    let shots = []
    let shotsIn = 0
    const shoot = (sp, charged) => {
      const s = world.squirrel
      const from = sp.pos.clone().setY(sp.pos.y + 8)
      // Not dead-on: a stray shot now and then
      const to = s.pos.clone().setY(s.pos.y + SQ_H / 2).add(new Vector3(rand(-14, 14), 0, rand(-14, 14)))
      // Harder creatures lob it where the squirrel is heading
      to.addScaledVector(new Vector3(s.vel.x, 0, s.vel.z), (from.distanceTo(to) / SHOT_SPEED) * level().lead)
      const time = Math.max(0.35, from.distanceTo(to) / SHOT_SPEED)
      const lift = 260 // a gentle arc
      const vel = to.clone().sub(from).divideScalar(time)
      vel.y += (lift * time) / 2
      const mesh = new Mesh(shotGeo, charged ? sparkMat : shotMats[sp.kind])
      woods.add(mesh)
      shots.push({ pos: from, vel, lift, mesh, life: time + 1, spark: charged })
      sound.play('spit')
    }
    const updateShots = (dt) => {
      const s = world.squirrel
      shotsIn -= dt
      const hazards = world.staged && view.p > 0.9 && s.introFor <= 0
      shots = shots.filter((shot) => {
        shot.life -= dt
        if (shot.orb) {
          if (!boss || boss.phase !== 'fight') shot.life = 0
          steerOrb(shot, dt)
          reflectOrb(shot)
        }
        shot.vel.y -= shot.lift * dt
        shot.pos.addScaledVector(shot.vel, dt)
        const home = shot.orb && orbHome(shot)
        const hit = !shot.back && shot.pos.distanceTo(s.pos.clone().setY(s.pos.y + SQ_H / 2)) < 9 + 3 * (shot.size || 1)
        if (hit && hazards && s.deadFor <= 0 && s.safeFor <= 0) hurt(s, 'bite')
        if (home || hit || (!shot.orb && shot.pos.y < floorAt(shot.pos.x, shot.pos.z)) || shot.life <= 0 || !world.staged) {
          woods.remove(shot.mesh)
          return false
        }
        shot.mesh.position.copy(shot.pos)
        const wobble = shot.spark ? 0.6 + Math.random() * 0.8 : shot.orb ? 1 + Math.sin(clock * 12) * 0.12 : 1
        shot.mesh.scale.setScalar(Math.max(0.001, view.e * wobble * (shot.size || 1)))
        shot.mesh.rotation.set(shot.life * 7, shot.life * 5, 0)
        return true
      })
    }

    // ----- Sleepy creatures -----
    // By day some creatures doze where they are. Creep past quietly; running
    // close by, pouncing near them or hitting them wakes them up.
    const zGeo = keep(buildSleepZ())
    const zMat = new MeshBasicMaterial({ color: '#f2f6ff', transparent: true })
    const sleepZs = new InstancedMesh(zGeo, zMat, 40)
    sleepZs.frustumCulled = false
    sleepZs.count = 0
    woods.add(sleepZs)
    const updateSleepZs = () => {
      let n = 0
      spiders.forEach((sp) => {
        if (!sp.alive || !sp.asleep || n >= 40) return
        // Each sleeper puffs out a "z" that drifts up and fades, again and again
        const t = (clock * 0.5 + sp.phase) % 1
        bugEuler.set(0, 0.6, 0.2)
        bugQuat.setFromEuler(bugEuler)
        bugScale.setScalar(Math.max(0.001, (0.6 + t * 0.6) * (1 - t) * 1.4 * view.e))
        rainAt.set(sp.pos.x + t * 6, sp.pos.y + 14 + t * 14, sp.pos.z)
        bugMatrix.compose(rainAt, bugQuat, bugScale)
        sleepZs.setMatrixAt(n++, bugMatrix)
      })
      sleepZs.count = n
      sleepZs.instanceMatrix.needsUpdate = true
    }

    // ----- Power-ups -----
    // Every so often a power-up turns up a little way ahead
    const POWERS = {
      magnet: { name: 'Golden nut', time: 12 },
      pepper: { name: 'Pepper', time: 5 },
      feather: { name: 'Feather', time: 20 },
      shield: { name: 'Shield acorn', time: 10 },
    }
    const POWER_KINDS = Object.keys(POWERS)
    const powerGeos = {
      magnet: acornGeo,
      pepper: keep(buildPepper()),
      feather: keep(buildFeather()),
      shield: acornGeo,
    }
    const powerMats = {
      magnet: [0, 1, 2].map(() => new MeshLambertMaterial({ color: '#ffd23f', emissive: '#a07000' })),
      pepper: [new MeshLambertMaterial({ color: '#e8251f', emissive: '#5a0a08' }), lambert('#3fa03a')],
      feather: [new MeshLambertMaterial({ color: '#ffffff', emissive: '#555555' }), lambert('#3a7bff')],
      shield: [0, 1, 2].map(() => new MeshLambertMaterial({ color: '#6ac8ff', emissive: '#1a4a7a' })),
    }
    // A soft bubble around the squirrel while it has a shield
    const bubble = new Mesh(
      keep(new BoxGeometry(SQ_W * 1.25, SQ_H * 1.3, SQ_D * 2.2)),
      new MeshBasicMaterial({ color: '#8fd6ff', transparent: true, opacity: 0.25, depthWrite: false }),
    )
    bubble.visible = false
    woods.add(bubble)
    let pickups = []
    let powerIn = rand(18, 30)
    // Out at the edge of view, where the woods grow in, ahead of the squirrel
    // (or the way the camera looks while it's still), so a power-up is
    // already there in the haze rather than popping up close by
    const distantSpot = () => {
      const s = world.squirrel
      const ahead = flat(s.vel) > 30 ? new Vector3(s.vel.x, 0, s.vel.z) : s.pos.clone().sub(camera.position).setY(0)
      const a = Math.atan2(ahead.z, ahead.x) + rand(-0.6, 0.6)
      const r = viewRadius() * rand(0.72, 0.9)
      return new Vector3(s.pos.x + Math.cos(a) * r, 0, s.pos.z + Math.sin(a) * r)
    }
    const clearPickups = () => {
      pickups.forEach((pk) => woods.remove(pk.mesh))
      pickups = []
    }
    const updatePowers = (dt) => {
      const s = world.squirrel
      world.acorns.forEach((a) => {
        a.magnet = false
      })
      const playing = world.staged && !world.paused && view.p > 0.5
      if (!s.powers) s.powers = { magnet: 0, pepper: 0, feather: 0, shield: 0 }
      Object.keys(s.powers).forEach((k) => {
        s.powers[k] = Math.max(0, s.powers[k] - dt)
      })
      // A golden nut on each lookout peak that hasn't been climbed yet
      const bossFight = boss && boss.phase === 'fight'
      peaks.forEach((pe) => {
        if (bossFight || world.harvested.has(pe.id) || pickups.some((pk) => pk.id === pe.id)) return
        const mesh = new Mesh(powerGeos.magnet, powerMats.magnet)
        woods.add(mesh)
        pickups.push({ kind: 'magnet', mesh, pos: new Vector3(pe.x, 0, pe.z), phase: rand(0, 6), id: pe.id })
      })
      if (playing && s.introFor <= 0) {
        // Low on lives, they turn up more often (barely more on Insane), often as a shield
        const low = world.lives <= Math.max(1, Math.floor(level().lives / 2))
        powerIn -= dt * (1 + (low ? level().lowPowers : 0))
        if (powerIn <= 0 && pickups.filter((pk) => !pk.id).length < 2) {
          powerIn = rand(22, 38)
          const fighting = boss && boss.phase === 'fight'
          // No golden nuts in a boss fight
          const kinds = fighting ? POWER_KINDS.filter((k) => k !== 'magnet') : POWER_KINDS
          const kind = low && Math.random() < 0.5 ? 'shield' : kinds[Math.floor(Math.random() * kinds.length)]
          const mesh = new Mesh(powerGeos[kind], powerMats[kind])
          woods.add(mesh)
          const pos = fighting ? nearSquirrel(140) : (under && openSpot(80, 260)) || distantSpot()
          pickups.push({ kind, mesh, pos, phase: rand(0, 6) })
        }
      }
      pickups = pickups.filter((pk) => {
        const centre = s.pos.clone().setY(s.pos.y + SQ_H / 2)
        if (pk.floor === undefined) pk.floor = floorAt(pk.pos.x, pk.pos.z)
        const at = pk.pos.clone().setY(pk.floor + 10 + Math.sin(clock * 3 + pk.phase) * 3)
        if (playing && s.deadFor <= 0 && at.distanceTo(centre) < 18) {
          s.powers[pk.kind] = POWERS[pk.kind].time
          if (pk.id) world.harvested.add(pk.id)
          popup(POWERS[pk.kind].name, 'power')
          sound.play('power')
          woods.remove(pk.mesh)
          return false
        }
        if (!world.staged || flat(pk.pos.clone().sub(s.pos)) > Math.max(800, viewRadius())) {
          woods.remove(pk.mesh)
          return false
        }
        pk.mesh.position.copy(at)
        pk.mesh.rotation.set(0, clock * 2 + pk.phase, 0)
        pk.mesh.scale.setScalar(Math.max(0.001, view.e * (pk.kind === 'magnet' || pk.kind === 'shield' ? 1.6 : 1.3) * (1 + Math.sin(clock * 6) * 0.08)))
        return true
      })

      // Golden nut: nuts nearby are drawn in to the squirrel
      if (s.powers.magnet > 0 && playing) {
        world.acorns.forEach((a) => {
          const to = s.pos.clone().setY(s.pos.y + AC_R).sub(a.pos)
          const d = to.length()
          if (d > GOLDEN_REACH) return
          a.hang = null
          a.magnet = true
          // Faster than the squirrel can run, and carried along with it, so
          // they catch it up from behind and are eaten on arrival
          a.vel.copy(to.setLength(Math.max(420, d * 5))).add(s.vel)
          if (d < 20 && s.deadFor <= 0) eat(s, a, true)
        })
      }
      // Pepper: bowl creatures over just by running into them
      if (s.powers.pepper > 0 && playing) {
        spiders.forEach((sp) => {
          if (!sp.alive || sp.boss || (sp.pepperedFor || 0) > 0) return
          if (flat(sp.pos.clone().sub(s.pos)) > 22 || Math.abs(sp.pos.y - s.pos.y) > 16) return
          sp.pepperedFor = 0.6
          sp.asleep = false
          sp.hp -= 1
          sp.flashFor = 0.25
          sp.knock.copy(sp.pos.clone().sub(s.pos).setY(0).setLength(320))
          sound.play(sp.hp <= 0 ? 'kill' : 'hit')
          if (sp.hp <= 0) {
            sp.alive = false
            sp.deadFor = SPIDER_DEATH
            sp.respawnIn = rand(8, 14)
          }
        })
      }
      spiders.forEach((sp) => {
        sp.pepperedFor = Math.max(0, (sp.pepperedFor || 0) - dt)
      })
      // The bubble flickers as the shield runs out
      const shieldLeft = s.powers.shield || 0
      bubble.visible = shieldLeft > 0 && world.staged && view.p > 0.5 && (shieldLeft > 2 || Math.sin(clock * 20) > 0)
      if (bubble.visible) {
        bubble.position.set(s.pos.x, s.pos.y + SQ_H / 2, s.pos.z)
        bubble.rotation.y = s.heading
        bubble.material.opacity = 0.18 + Math.sin(clock * 5) * 0.06
      }
      showPowers()
    }
    // Badges for what's active, after the hearts
    const showPowers = () => {
      const s = world.squirrel
      const active = []
      if (s.powers) {
        Object.keys(s.powers).forEach((k) => {
          if (s.powers[k] > 0) active.push(`${POWERS[k].name} ${Math.ceil(s.powers[k])}`)
        })
      }
      const text = active.join(' · ')
      if (powersEl.textContent !== text) powersEl.textContent = text
    }

    // ----- Storm hazards -----
    // A warning ring flickers on the ground, then lightning strikes there
    const warnMat = new MeshBasicMaterial({ color: '#ff3b3b', transparent: true, opacity: 0.8, side: DoubleSide, depthWrite: false })
    const warnRing = new Mesh(keep(new RingGeometry(18, 24, 32).rotateX(-Math.PI / 2)), warnMat)
    const warnFill = new Mesh(keep(new CircleGeometry(18, 32).rotateX(-Math.PI / 2)), new MeshBasicMaterial({ color: '#ff3b3b', transparent: true, opacity: 0.2, depthWrite: false }))
    warnRing.visible = false
    warnFill.visible = false
    woods.add(warnRing, warnFill)
    const STRIKE_RADIUS = 24
    let strikeIn = rand(8, 14)
    let warnFor = 0
    const strikeAt = new Vector3()
    // Storm wind: a gusting push that slowly swings round, felt while gliding
    const wind = new Vector3()
    let windAngle = Math.random() * Math.PI * 2
    const updateWind = (dt) => {
      windAngle += dt * 0.07
      const gust = 0.7 + 0.3 * Math.sin(clock * 0.9) + 0.15 * Math.sin(clock * 2.3)
      const strength = (75 * clamp(atmo.storm, 0, 1) + 120 * blizzard) * gust
      wind.set(Math.cos(windAngle) * strength, 0, Math.sin(windAngle) * strength)
    }

    const updateStrikes = (dt) => {
      const s = world.squirrel
      const on = world.staged && !world.paused && view.p > 0.5 && atmo.storm > 0.5 && s.introFor <= 0
      if (warnFor > 0 && s.sheltered) warnFor = 0.0001 // it fizzles out over the cave
      if (warnFor > 0) {
        warnFor -= dt
        const blink = Math.sin(clock * 30) > 0
        warnRing.visible = blink
        warnFill.visible = true
        warnFill.material.opacity = 0.15 + (1 - warnFor / 1.3) * 0.3
        if (warnFor <= 0) {
          // Strike: a bolt straight down onto the spot
          warnRing.visible = false
          warnFill.visible = false
          flash = 1
          const points = []
          let x = strikeAt.x
          for (let y = 600; y >= 0; y -= 60) {
            points.push(x, y, strikeAt.z)
            x = strikeAt.x + rand(-14, 14)
          }
          points.push(strikeAt.x, strikeAt.y, strikeAt.z)
          bolt.geometry.setAttribute('position', new Float32BufferAttribute(points, 3))
          sound.play('thunder', 0.02)
          if (flat(s.pos.clone().sub(strikeAt)) < STRIKE_RADIUS && s.pos.y - strikeAt.y < 30 && s.deadFor <= 0 && s.safeFor <= 0) hurt(s, 'bite')
          spiders.forEach((sp) => {
            if (sp.alive && flat(sp.pos.clone().sub(strikeAt)) < STRIKE_RADIUS * 1.5) sp.chargedFor = CHARGED_TIME
          })
        }
        return
      }
      if (!on || s.sheltered) return
      strikeIn -= dt
      if (strikeIn > 0) return
      strikeIn = rand(8, 15)
      warnFor = 1.3
      // Near enough that standing still is a gamble
      strikeAt.set(s.pos.x + rand(-26, 26), 0, s.pos.z + rand(-26, 26))
      strikeAt.y = floorAt(strikeAt.x, strikeAt.z) + 0.4
      warnRing.position.copy(strikeAt)
      warnFill.position.copy(strikeAt)
      sound.play('warn')
    }

    // Rain: slippery puddles on the ground
    const puddleMat = new MeshLambertMaterial({ color: '#7d98b2', emissive: '#22303e', transparent: true, opacity: 0, depthWrite: false })
    const puddles = Array.from({ length: 16 }, () => {
      const mesh = new Mesh(keep(new CircleGeometry(1, 20).rotateX(-Math.PI / 2)), puddleMat)
      mesh.visible = false
      woods.add(mesh)
      return { mesh, pos: null, r: 0 }
    })
    let puddleAmount = 0
    const updatePuddles = (dt) => {
      const s = world.squirrel
      const wetNow = wetness()
      const want = wetNow === 'storm' ? 1 : wetNow === 'rain' ? 0.75 : 0
      puddleAmount = lerp(puddleAmount, want, 1 - Math.exp(-0.5 * dt))
      puddleMat.opacity = 0.55 * puddleAmount * view.p
      const count = Math.round(puddles.length * Math.min(1, puddleAmount * 1.4))
      puddles.forEach((pd, i) => {
        pd.mesh.visible = i < count && view.p > 0.5
        if (!pd.mesh.visible) return
        if (!pd.pos || flat(pd.pos.clone().sub(s.pos)) > 360) {
          const a = rand(0, Math.PI * 2)
          const r = pd.pos ? rand(200, 340) : rand(30, 340)
          pd.pos = new Vector3(s.pos.x + Math.cos(a) * r, 0.35, s.pos.z + Math.sin(a) * r)
          pd.r = rand(14, 30)
          // Rain pools in the low ground, not on the hills
          if (floorAt(pd.pos.x, pd.pos.z) > 0) pd.r = 0.001
        }
        pd.mesh.position.copy(pd.pos)
        pd.mesh.scale.set(pd.r * view.e, 1, pd.r * 0.75 * view.e)
      })
    }
    const onPuddle = (s) =>
      s.pos.y < 1 &&
      puddles.some((pd) => pd.mesh.visible && pd.pos && Math.hypot((s.pos.x - pd.pos.x) / pd.r, (s.pos.z - pd.pos.z) / (pd.r * 0.75)) < 1)

    // ----- Bosses -----
    // Now and then a giant of the biome's creature waits up ahead. Come close
    // and a ring of brambles closes in: no leaving until it's beaten. It can't
    // be hurt while it's on the prowl; dodge its big attack and pounce while
    // it's dazed. Three hits does it. Each kind has its own big attack.
    const BOSS_RADIUS = 210
    const BOSS_HITS = 3
    const BOSS_SIZE = 3
    const BOSS_TITLES = {
      spider: ['Broodmother', 'Silkfang', 'the Weaver'],
      wolf: ['Alpha', 'Greymaw', 'the Howler'],
      frog: ['Bog King', 'Croaker', 'Old Bulge'],
      snake: ['Coilqueen', 'the Hisser', 'Stormfang'],
      croc: ['Old Snapper', 'Mudjaw', 'the Mire King'],
      scorpion: ['Stingtail', 'the Sand King', 'Deathclaw'],
      iguana: ['the Basker', 'Old Crest', 'Scaleback'],
      bear: ['Frostmaw', 'Old Whitefang', 'the Icehide'],
    }
    const BOSS_NAMES = ['Grim', 'Thorn', 'Gloom', 'Mossback', 'Rook', 'Ash', 'Bramble', 'Hollow', 'Iron', 'Mad', 'Gnarl', 'Rot']
    const BOSS_EPITHETS = ['Terror', 'Bane', 'Warden', 'Tyrant', 'Scourge', 'Dread']
    const BOSS_PLACES = { meadow: 'Meadow', fog: 'Mist', rain: 'Mire', storm: 'Storm', jungle: 'Canopy', desert: 'Dunes', tropical: 'Shore', snow: 'Peaks' }
    // Each kind's big attack, after which it's dazed:
    //  leap: the frog bounds onto the squirrel and lands with a shockwave
    //  charge: the wolf charges again and again, dazed when the last one hits the brambles
    //  orbs: the spider sends slow glowing orbs drifting after the squirrel;
    //   pounce on one to knock it back, and one that hits the spider dazes it.
    //   If none come back it just goes back to prowling.
    //  venom: the snake sprays rings of venom outward; jump them or slip through a gap
    //  spikes: the scorpion stabs the ground and spikes burst up where the ground flashes
    //  rockfall: the iguana stomps and rocks drop where their shadows flash
    //  ambush: the croc dives under, a ripple follows the squirrel, then it bursts up in a geyser
    //  icefall: the bear roars and icicles fall in a ring round the squirrel, closing in; run out through the gap
    const BOSS_MOVES = { frog: 'leap', wolf: 'charge', spider: 'orbs', snake: 'venom', scorpion: 'spikes', iguana: 'rockfall', croc: 'ambush', bear: 'icefall' }
    const pickOne = (list) => list[Math.floor(Math.random() * list.length)]
    const bossName = (kind) =>
      `${pickOne(BOSS_NAMES)} ${pickOne(BOSS_TITLES[kind])}, ${pickOne(BOSS_EPITHETS)} of the ${BOSS_PLACES[biome]}`

    // The bramble ring
    const WALLS = 56
    const wallMat = lambert('#4a3a24')
    const walls = new InstancedMesh(keep(new BoxGeometry(12, 1, 12).translate(0, 0.5, 0)), wallMat, WALLS)
    walls.frustumCulled = false
    walls.visible = false
    woods.add(walls)
    const thornMat = lambert('#2f5a26')
    const thorns = new InstancedMesh(keep(new BoxGeometry(5, 5, 5)), thornMat, WALLS * 2)
    thorns.frustumCulled = false
    thorns.visible = false
    woods.add(thorns)
    // Shockwave and dizzy stars
    const quakeMat = new MeshBasicMaterial({ color: '#fff2c4', transparent: true, opacity: 0.8, side: DoubleSide, depthWrite: false })
    const quake = new Mesh(keep(new RingGeometry(0.9, 1, 48).rotateX(-Math.PI / 2)), quakeMat)
    quake.visible = false
    woods.add(quake)
    const starMeshes = [0, 1, 2].map(() => {
      const m = new Mesh(keep(new BoxGeometry(3, 3, 3)), new MeshBasicMaterial({ color: '#ffe14a' }))
      m.visible = false
      woods.add(m)
      return m
    })
    const dazeMat = new MeshBasicMaterial({ color: '#ffe14a' })
    const angryMat = new MeshBasicMaterial({ color: '#ff7a2a' })

    // Telegraphed traps: a marker flashes on the ground, quicker and quicker,
    // then spikes burst up, a rock lands or a geyser erupts there
    const TRAPS = 48
    const TRAP_LOOK = {
      spike: { r: 13, up: 0.9, tall: 28 },
      rock: { r: 15, up: 0.7, tall: 18 },
      geyser: { r: 16, up: 0.9, tall: 70 },
      icicle: { r: 11, up: 0.8, tall: 24 },
    }
    const markMat = new MeshBasicMaterial({ color: '#ff3b3b', transparent: true, opacity: 0.55, side: DoubleSide, depthWrite: false })
    const marks = new InstancedMesh(keep(new CircleGeometry(1, 20).rotateX(-Math.PI / 2)), markMat, TRAPS)
    const spikeMat = lambert('#e3d2ad')
    const spikes = new InstancedMesh(keep(new ConeGeometry(1, 1, 4).translate(0, 0.5, 0)), spikeMat, TRAPS * 3)
    const rockMat = lambert('#7d7468')
    const rocks = new InstancedMesh(keep(new BoxGeometry(1, 1, 1)), rockMat, TRAPS)
    const geyserMat = new MeshBasicMaterial({ color: '#a9e8ff', transparent: true, opacity: 0.75, depthWrite: false })
    const geysers = new InstancedMesh(keep(new BoxGeometry(1, 1, 1).translate(0, 0.5, 0)), geyserMat, TRAPS)
    // Icicles point down as they fall, and stand jagged in the snow
    const icicleMat = new MeshLambertMaterial({ color: '#cfefff', emissive: '#3a6a8a' })
    const icicles = new InstancedMesh(keep(new ConeGeometry(1, 1, 5).rotateX(Math.PI).translate(0, 0.5, 0)), icicleMat, TRAPS)
    ;[marks, spikes, rocks, geysers, icicles].forEach((m) => {
      m.frustumCulled = false
      m.count = 0
      woods.add(m)
    })
    let traps = []
    let trapSound = 0
    // warn: seconds of flashing before it goes off; follow: seconds before
    // that spent tracking the squirrel; delay: seconds before it appears
    const addTrap = (kind, x, z, warn, delay = 0, follow = 0) => {
      if (traps.length >= TRAPS) return null
      const trap = { kind, pos: new Vector3(x, floorAt(x, z), z), warn, delay, up: 0, hit: false, ...TRAP_LOOK[kind] }
      trap.left = warn + follow
      traps.push(trap)
      return trap
    }
    // Somewhere near the squirrel, inside the ring
    const nearSquirrel = (spread) => {
      const s = world.squirrel
      const at = new Vector3(s.pos.x + rand(-spread, spread), 0, s.pos.z + rand(-spread, spread))
      const out = at.clone().sub(wallCentre).setY(0)
      if (out.length() > BOSS_RADIUS - 20) out.setLength(BOSS_RADIUS - 20)
      return wallCentre.clone().add(out)
    }
    const placeTrap = (mesh, n, x, y, z, sx, sy, sz, spin = 0) => {
      bugEuler.set(0, spin, 0)
      bugQuat.setFromEuler(bugEuler)
      rainAt.set(x, y, z)
      bugScale.set(Math.max(0.001, sx), Math.max(0.001, sy), Math.max(0.001, sz))
      bugMatrix.compose(rainAt, bugQuat, bugScale)
      mesh.setMatrixAt(n, bugMatrix)
    }
    const updateTraps = (dt) => {
      const s = world.squirrel
      const hazards = world.staged && view.p > 0.9 && s.introFor <= 0
      trapSound -= dt
      let nMark = 0
      let nSpike = 0
      let nRock = 0
      let nGeyser = 0
      let nIce = 0
      traps = traps.filter((t) => {
        if (t.delay > 0) {
          t.delay -= dt
          return true
        }
        if (t.left > 0) {
          t.left -= dt
          // A ripple that tracks the squirrel before it settles
          if (t.left > t.warn) {
            t.pos.lerp(new Vector3(s.pos.x, 0, s.pos.z), Math.min(1, dt * 3.5))
            t.pos.y = floorAt(t.pos.x, t.pos.z)
          }
          const urgency = 1 - clamp(t.left / t.warn, 0, 1)
          const blink = t.left > t.warn || Math.sin(clock * (10 + 40 * urgency)) > -0.3
          if (blink && nMark < TRAPS) {
            const grow = t.kind === 'rock' || t.kind === 'icicle' ? 0.5 + 0.5 * urgency : 1
            placeTrap(marks, nMark++, t.pos.x, t.pos.y + 0.5, t.pos.z, t.r * grow, 1, t.r * grow)
          }
          // A rock is seen falling for its last half second
          if (t.kind === 'rock' && t.left < 0.5 && nRock < TRAPS) {
            placeTrap(rocks, nRock++, t.pos.x, t.pos.y + t.r * 0.5 + 400 * t.left * t.left, t.pos.z, t.r, t.r, t.r, t.left * 6)
          }
          if (t.kind === 'icicle' && t.left < 0.5 && nIce < TRAPS) {
            placeTrap(icicles, nIce++, t.pos.x, t.pos.y + 450 * t.left * t.left, t.pos.z, t.r * 0.6, t.tall * 1.4, t.r * 0.6)
          }
          if (t.left <= 0) {
            t.up = TRAP_LOOK[t.kind].up
            bossSmash(t.pos, t.r)
            if (trapSound <= 0) {
              sound.play(t.kind === 'rock' ? 'crash' : t.kind === 'spike' || t.kind === 'icicle' ? 'smash' : 'spit')
              trapSound = 0.12
            }
          }
          return true
        }
        t.up -= dt
        if (t.up <= 0) return false
        const life = TRAP_LOOK[t.kind].up
        // Bursts up fast, sinks back at the end
        const rise = ease(clamp((life - t.up) / 0.1, 0, 1)) * clamp(t.up / 0.25, 0, 1)
        const near = flat(s.pos.clone().sub(t.pos)) < t.r
        if (!t.hit && near && s.pos.y - t.pos.y < t.tall * rise && hazards && s.deadFor <= 0 && s.safeFor <= 0) {
          t.hit = true
          hurt(s, 'bite')
        }
        if (t.kind === 'spike' && nSpike < TRAPS * 3 - 2) {
          placeTrap(spikes, nSpike++, t.pos.x, t.pos.y, t.pos.z, t.r * 0.45, t.tall * rise, t.r * 0.45, 0.4)
          placeTrap(spikes, nSpike++, t.pos.x + t.r * 0.5, t.pos.y, t.pos.z + t.r * 0.2, t.r * 0.3, t.tall * 0.6 * rise, t.r * 0.3, 1.1)
          placeTrap(spikes, nSpike++, t.pos.x - t.r * 0.4, t.pos.y, t.pos.z - t.r * 0.4, t.r * 0.3, t.tall * 0.7 * rise, t.r * 0.3, 2)
        } else if (t.kind === 'rock' && nRock < TRAPS) {
          placeTrap(rocks, nRock++, t.pos.x, t.pos.y + t.r * 0.5 * rise, t.pos.z, t.r * rise, t.r * rise, t.r * rise, 0.3)
        } else if (t.kind === 'icicle' && nIce < TRAPS) {
          // Stuck point down in the ground, then shatters away
          const shrink = clamp(t.up / 0.25, 0, 1)
          placeTrap(icicles, nIce++, t.pos.x, t.pos.y - t.tall * 0.3, t.pos.z, t.r * 0.6 * shrink, t.tall * 1.3 * shrink, t.r * 0.6 * shrink)
        } else if (t.kind === 'geyser' && nGeyser < TRAPS) {
          const wobble = 1 + Math.sin(clock * 30 + t.pos.x) * 0.12
          placeTrap(geysers, nGeyser++, t.pos.x, t.pos.y, t.pos.z, t.r * 1.2 * wobble, t.tall * rise, t.r * 1.2 * wobble)
        }
        return true
      })
      const shown = view.p > 0.5
      marks.count = shown ? nMark : 0
      spikes.count = shown ? nSpike : 0
      rocks.count = shown ? nRock : 0
      geysers.count = shown ? nGeyser : 0
      icicles.count = shown ? nIce : 0
      ;[marks, spikes, rocks, geysers, icicles].forEach((m) => {
        m.instanceMatrix.needsUpdate = true
      })
    }

    // Bosses flatten trees and mushrooms in their way
    const bossSmash = (pos, radius) => {
      trees
        .filter((t) => Math.hypot(t.x - pos.x, t.z - pos.z) < radius + t.trunkRadius)
        .forEach((t) => {
          while (t.hits < TREE_HITS) hitTree(t)
        })
      mushrooms.filter((m) => Math.hypot(m.x - pos.x, m.z - pos.z) < radius).forEach(smashMushroom)
    }

    // Boss shots: lobbed (lift) or flat (no lift), a little bigger than the pack's
    const bossShot = (from, to, mat, time, lift) => {
      const vel = to.clone().sub(from).divideScalar(time)
      vel.y += (lift * time) / 2
      const mesh = new Mesh(shotGeo, mat)
      woods.add(mesh)
      shots.push({ pos: from.clone(), vel, lift, mesh, life: time + 0.5, spark: false, size: 1.6 })
    }

    // The spider's orbs: slow, drifting after the squirrel, and turned back
    // gold by a pounce
    const orbMat = new MeshBasicMaterial({ color: '#d59bff' })
    const orbBackMat = new MeshBasicMaterial({ color: '#ffe14a' })
    const ORB_SIZE = 2.4
    const bossOrb = () => {
      const sp = boss.sp
      const from = sp.pos.clone().setY(sp.pos.y + 16)
      const aim = world.squirrel.pos.clone().setY(from.y).sub(from).setLength(1)
      const speed = 85 / Math.sqrt(level().bossPace)
      const mesh = new Mesh(shotGeo, orbMat)
      woods.add(mesh)
      shots.push({ pos: from, vel: aim.multiplyScalar(speed), lift: 0, mesh, life: 7, spark: false, size: ORB_SIZE, orb: true, speed })
    }
    const orbsOut = () => shots.some((sh) => sh.orb)
    // Steers an orb: after the squirrel (gently, so it can be outrun or
    // sidestepped), or once knocked back, straight for the spider
    const steerOrb = (shot, dt) => {
      const s = world.squirrel
      const sp = boss && boss.sp
      if (!sp) return
      const goal = shot.back ? sp.pos.clone().setY(sp.pos.y + 16) : s.pos.clone().setY(s.pos.y + SQ_H / 2)
      const want = goal.sub(shot.pos)
      want.y *= 0.5
      want.setLength(shot.speed)
      shot.vel.lerp(want, Math.min(1, dt * (shot.back ? 6 : 0.9)))
      // Floats along over the ground
      const floor = floorAt(shot.pos.x, shot.pos.z)
      if (shot.pos.y < floor + 8) {
        shot.pos.y = floor + 8
        if (shot.vel.y < 0) shot.vel.y = 0
      }
    }
    // A pounce that reaches an orb sends it back where it came from
    const reflectOrb = (shot) => {
      const s = world.squirrel
      if (shot.back || !(s.attackFor > 0)) return false
      const mouth = mouthOf(s).setY(s.pos.y + SQ_H / 2)
      if (shot.pos.distanceTo(mouth) > 24) return false
      shot.back = true
      shot.speed *= 3
      shot.life = 4
      shot.mesh.material = orbBackMat
      sound.play('clank')
      return true
    }
    // A knocked-back orb that gets home leaves the spider dazed
    const orbHome = (shot) => {
      const sp = boss && boss.sp
      if (!shot.back || !sp || boss.phase !== 'fight') return false
      const reach = Math.max(30, sp.cfg.shadow[0] * BOSS_SIZE * 0.45)
      if (flat(shot.pos.clone().sub(sp.pos)) > reach || Math.abs(shot.pos.y - sp.pos.y - 16) > 30) return false
      sound.play('crash')
      sp.flashFor = 0.3
      shots.forEach((sh) => {
        if (sh.orb && sh !== shot) sh.life = 0
      })
      boss.mode = 'dazed'
      boss.t = 2.8 * level().bossDaze
      boss.dazedLength = boss.t
      boss.left = 0
      return true
    }

    // One round of the boss's big attack
    const bossVolley = () => {
      const s = world.squirrel
      const sp = boss.sp
      const pace = level().bossPace
      const count = level().bossCount
      const aim = s.pos.clone().sub(sp.pos).setY(0)
      const dist = aim.length() || 1
      if (boss.move === 'orbs') {
        // One orb at a time, a couple in the air at once at most
        bossOrb()
        sound.play('spit')
        boss.gap = 1.6 * pace
      } else if (boss.move === 'venom') {
        // A ring of venom spreading out flat, each ring turned half a step
        const n = Math.round(12 * count)
        const turn = boss.left % 2 ? Math.PI / n : 0
        for (let i = 0; i < n; i++) {
          const a = (i / n) * Math.PI * 2 + turn
          const to = sp.pos.clone().add(new Vector3(Math.cos(a), 0, Math.sin(a)).multiplyScalar(400)).setY(6)
          bossShot(sp.pos.clone().setY(6), to, shotMats.snake, 400 / (130 + 40 / pace), 0)
        }
        sound.play('spit')
        boss.gap = 0.9 * pace
      } else if (boss.move === 'spikes') {
        // Right under the squirrel, scattered around it, and a line out from the boss
        const warn = Math.max(0.65, 1.1 * pace)
        addTrap('spike', s.pos.x, s.pos.z, warn)
        for (let i = 0; i < Math.round(4 * count); i++) {
          const at = nearSquirrel(90)
          addTrap('spike', at.x, at.z, warn)
        }
        const step = aim.clone().divideScalar(dist)
        for (let d = 40; d < dist + 30; d += 26) addTrap('spike', sp.pos.x + step.x * d, sp.pos.z + step.z * d, warn, d / 400)
        sound.play('smash')
        boss.gap = warn + 0.4
      } else if (boss.move === 'rockfall') {
        // Rocks drop where the squirrel is (or is heading) and nearby
        const warn = Math.max(0.75, 1.2 * pace)
        const lead = new Vector3(s.vel.x, 0, s.vel.z).multiplyScalar(warn * 0.5 * level().lead)
        addTrap('rock', s.pos.x + lead.x, s.pos.z + lead.z, warn)
        for (let i = 0; i < Math.round(2 * count) - 1; i++) {
          const at = nearSquirrel(80)
          addTrap('rock', at.x, at.z, warn, rand(0, 0.3))
        }
        boss.gap = 0.45 * pace
      } else if (boss.move === 'icefall') {
        // A ring of icicles round the squirrel with one gap, smaller each
        // round, and one right on top of it
        const warn = Math.max(0.8, 1.25 * pace)
        const round = boss.rounds - boss.left
        const radius = Math.max(34, 78 - round * 16)
        const n = Math.round(clamp(radius / 7, 6, 12) * Math.min(1.3, count))
        const gap = rand(0, Math.PI * 2)
        for (let i = 0; i < n; i++) {
          const a = gap + ((i + 1) / (n + 1)) * Math.PI * 2
          addTrap('icicle', s.pos.x + Math.cos(a) * radius, s.pos.z + Math.sin(a) * radius, warn, i * 0.02)
        }
        addTrap('icicle', s.pos.x, s.pos.z, warn)
        sound.play('roar')
        boss.gap = warn + 0.35 * pace
      }
    }

    let boss = null
    let bossIn = rand(120, 200)
    let wallRise = 0
    const wallCentre = new Vector3()

    // Everything growing inside the ring shrinks away as the fight starts:
    // trees, mushrooms, flowers and ferns, and the grass (rocks, logs and
    // stepping stones stay). Trees and mushrooms are gone for the run.
    let withering = []
    const inRing = (x, z) => Math.hypot(x - wallCentre.x, z - wallCentre.z) < BOSS_RADIUS + 10
    const wither = (step, done, instant) => withering.push({ t: instant ? 1 : 0, step, done })
    const clearArena = (only, instant = false) => {
      ;(only ? [only] : [...chunks.values()]).forEach((c) => {
        const { x: ox, z: oz } = c.group.position
        c.trees = c.trees.filter((t) => {
          if (NOT_TREES.includes(t.kind) || !inRing(ox + t.lx, oz + t.lz)) return true
          world.felled.set(t.id, TREE_HITS)
          world.acorns.forEach((a) => {
            if (a.hang === t) a.hang = null
          })
          if (world.squirrel.climb === t) letGo(world.squirrel)
          const base = t.mesh.scale.clone()
          wither(
            (k) => t.mesh.scale.copy(base).multiplyScalar(k),
            () => {
              c.group.remove(t.mesh)
              t.mesh.geometry.dispose()
              t.mats.forEach((m) => m.dispose())
            },
            instant,
          )
          return false
        })
        c.mushrooms = c.mushrooms.filter((mu) => {
          if (!inRing(ox + mu.lx, oz + mu.lz)) return true
          world.eaten.add(mu.id)
          wither(
            (k) => mu.mesh.scale.setScalar(k),
            () => {
              c.group.remove(mu.mesh)
              mu.mesh.geometry.dispose()
            },
            instant,
          )
          return false
        })
        // Grass tufts shrink where they stand
        if (c.grass) {
          const tufts = []
          const m = new Matrix4()
          for (let i = 0; i < c.grass.count; i++) {
            c.grass.getMatrixAt(i, m)
            if (m.elements[5] > 0.01 && inRing(ox + m.elements[12], oz + m.elements[14])) tufts.push([i, m.elements[0], m.elements[5], m.elements[10]])
          }
          if (tufts.length) {
            const grass = c.grass
            const step = (k) => {
              tufts.forEach(([i, sx, sy, sz]) => {
                grass.getMatrixAt(i, m)
                m.elements[0] = sx * k
                m.elements[5] = sy * k
                m.elements[10] = sz * k
                grass.setMatrixAt(i, m)
              })
              grass.instanceMatrix.needsUpdate = true
            }
            wither(step, () => step(0.001), instant)
          }
        }
        // Flowers, ferns and the like: split off into a mesh of their own to shrink
        if (c.decor && c.decorVoxels) {
          const stay = new Map()
          const go = new Map()
          c.decorVoxels.forEach((v, k) => {
            const [vx, , vz] = k.split(',').map(Number)
            ;(inRing(ox + vx * VOXEL, oz + vz * VOXEL) ? go : stay).set(k, v)
          })
          if (go.size) {
            c.group.remove(c.decor)
            c.decor.geometry.dispose()
            c.decorVoxels = stay
            c.decor = stay.size ? new Mesh(meshDecor(stay), decorMats) : null
            if (c.decor) c.group.add(c.decor)
            const gone = new Mesh(meshDecor(go), decorMats)
            c.group.add(gone)
            wither(
              (k) => gone.scale.set(1, k, 1),
              () => {
                c.group.remove(gone)
                gone.geometry.dispose()
              },
              instant,
            )
          }
        }
      })
      indexChunks()
    }
    const updateWithering = (dt) => {
      withering = withering.filter((w) => {
        w.t = Math.min(1, w.t + dt / 0.9)
        w.step(Math.max(0.001, 1 - ease(w.t)))
        if (w.t < 1) return true
        w.done()
        return false
      })
    }

    const clearBoss = () => {
      if (boss) {
        woods.remove(boss.sp.group, boss.sp.shadow)
        const i = spiders.indexOf(boss.sp)
        if (i >= 0) spiders.splice(i, 1)
      }
      boss = null
      fightOn = false
      traps = []
      wallRise = 0
      walls.visible = false
      thorns.visible = false
      quake.visible = false
      starMeshes.forEach((m) => {
        m.visible = false
      })
      bossBar.classList.remove('show', 'exposed')
    }
    const spawnBoss = () => {
      const s = world.squirrel
      const kind = pickOne([].concat(BIOMES[biome].enemy))
      const sp = makeEnemy(kind)
      sp.boss = true
      sp.size = BOSS_SIZE
      sp.alive = true
      sp.deadFor = 0
      sp.hp = BOSS_HITS
      sp.pos.set(s.pos.x + rand(-60, 60), 0, s.pos.z - rand(240, 300))
      sp.pos.y = floorAt(sp.pos.x, sp.pos.z)
      sp.vy = 0
      sp.leapFor = 0
      spiders.push(sp)
      boss = { sp, kind, move: BOSS_MOVES[kind], name: bossName(kind), phase: 'waiting', mode: 'stalk', t: 0, hits: 0, ring: 0, left: 0, gap: 0, dir: new Vector3() }
    }
    const showBossBar = () => {
      bossNameEl.textContent = boss.name
      bossFill.style.width = `${(100 * (BOSS_HITS - boss.hits)) / BOSS_HITS}%`
      bossBar.classList.toggle('exposed', boss.mode === 'dazed')
    }
    const startFight = () => {
      boss.phase = 'fight'
      fightOn = true
      boss.mode = 'stalk'
      boss.t = 1.6 * level().bossPace
      // Centred between the two, so the squirrel starts well inside
      wallCentre.copy(boss.sp.pos).lerp(world.squirrel.pos, 0.5).setY(0)
      clearArena()
      // Keep the squirrel inside, even if it was right at the edge
      showBossBar()
      bossBar.classList.add('show')
      sound.play('roar')
    }
    const winBoss = () => {
      const s = world.squirrel
      const sp = boss.sp
      boss.phase = 'won'
      fightOn = false
      sp.alive = false
      sp.deadFor = SPIDER_DEATH * 1.6
      sp.hidden = false
      traps = []
      bossBar.classList.remove('exposed')
      sound.play('victory')
      // Rewards: a super nut, an extra heart slot and a full heal
      spawnAcorn(sp.pos.clone().setY(AC_R + 30), true)
      world.bonusLives = Math.min(2, (world.bonusLives || 0) + 1)
      world.lives = maxLives()
      showScore()
      popup('+1', 'life')
      showToast(`${boss.name.split(',')[0]} defeated! Extra heart`)
      bossIn = rand(160, 260)
      s.safeFor = 1
    }

    // Inside the bramble ring until the boss is beaten
    const holdInArena = (s) => {
      if (!boss || boss.phase !== 'fight') return
      const out = s.pos.clone().sub(wallCentre).setY(0)
      const d = out.length()
      const limit = BOSS_RADIUS - 16
      if (d <= limit) return
      out.setLength(limit)
      s.pos.x = wallCentre.x + out.x
      s.pos.z = wallCentre.z + out.z
      const n = out.normalize()
      const outward = s.vel.x * n.x + s.vel.z * n.z
      if (outward > 0) {
        s.vel.x -= n.x * outward
        s.vel.z -= n.z * outward
      }
      if (s.climb) letGo(s)
    }

    const updateBoss = (dt) => {
      const s = world.squirrel
      updateWithering(dt)
      if (!world.staged) {
        if (boss) clearBoss()
        return
      }
      if (under) return
      const hazards = view.p > 0.9 && s.introFor <= 0
      if (!boss) {
        if (!world.paused && hazards && s.deadFor <= 0) {
          bossIn -= dt
          if (bossIn <= 0) spawnBoss()
        }
        return
      }
      const sp = boss.sp
      // Walls rise as the fight starts and sink once it's won
      wallRise = clamp(wallRise + (boss.phase === 'fight' ? dt : -dt) * 1.4, 0, 1)
      walls.visible = wallRise > 0 && view.p > 0.5
      thorns.visible = walls.visible
      if (walls.visible) {
        // The ring stays whole, even between the camera and the squirrel, so
        // it never looks like there's a way out
        for (let i = 0; i < WALLS; i++) {
          const a = (i / WALLS) * Math.PI * 2
          const h = (34 + ((i * 37) % 13)) * ease(wallRise)
          bugEuler.set(0, a, 0)
          bugQuat.setFromEuler(bugEuler)
          const wallX = wallCentre.x + Math.cos(a) * BOSS_RADIUS
          const wallZ = wallCentre.z + Math.sin(a) * BOSS_RADIUS
          const wallY = floorAt(wallX, wallZ)
          rainAt.set(wallX, wallY, wallZ)
          bugScale.set(1, Math.max(0.001, h), 1)
          bugMatrix.compose(rainAt, bugQuat, bugScale)
          walls.setMatrixAt(i, bugMatrix)
          for (let k = 0; k < 2; k++) {
            rainAt.set(
              wallCentre.x + Math.cos(a + (k ? 0.03 : -0.03)) * (BOSS_RADIUS + (k ? 6 : -6)),
              wallY + h * (k ? 0.45 : 0.75),
              wallCentre.z + Math.sin(a + (k ? 0.03 : -0.03)) * (BOSS_RADIUS + (k ? 6 : -6)),
            )
            bugScale.setScalar(Math.max(0.001, ease(wallRise)))
            bugMatrix.compose(rainAt, bugQuat, bugScale)
            thorns.setMatrixAt(i * 2 + k, bugMatrix)
          }
        }
        walls.instanceMatrix.needsUpdate = true
        thorns.instanceMatrix.needsUpdate = true
      }
      if (boss.phase === 'won') {
        sp.deadFor = Math.max(0, sp.deadFor - dt)
        starMeshes.forEach((m) => {
          m.visible = false
        })
        if (sp.deadFor <= 0 && wallRise <= 0) {
          bossBar.classList.remove('show')
          clearBoss()
        }
        return
      }
      if (world.paused) return
      const to = s.pos.clone().sub(sp.pos).setY(0)
      const dist = to.length()
      const target = dist > 0 ? to.clone().divideScalar(dist) : new Vector3(1, 0, 0)
      if (boss.phase === 'waiting') {
        // Prowls about on the spot, waiting for the squirrel to come close
        sp.heading = Math.atan2(-target.z, target.x)
        sp.phase += dt * 4
        sp.moving = true
        if (dist < 190 && hazards && s.deadFor <= 0) startFight()
        if (dist > 900) {
          clearBoss()
          bossIn = rand(40, 80)
        }
        return
      }

      // The fight. Harder levels wait less, warn for less and stay dazed for less.
      const pace = level().bossPace
      const count = level().bossCount
      const dazedFor = (t) => {
        boss.mode = 'dazed'
        boss.t = t * level().bossDaze
        boss.dazedLength = boss.t
        sp.hidden = false
      }
      const angry = 1 + boss.hits * 0.25
      boss.t -= dt
      sp.flashFor = Math.max(0, sp.flashFor - dt)
      let speed = 0
      let dir = target
      if (boss.mode === 'stalk') {
        // Prowling closer, untouchable; in its last stand it spits too
        speed = 70 * angry
        if (boss.hits >= level().bossSpit) {
          sp.shotCd = (sp.shotCd || 0) - dt
          if (sp.shotCd <= 0 && dist > 60) {
            shoot(sp, false)
            sp.shotCd = 2.2 * pace
          }
        }
        if (boss.t <= 0) {
          boss.mode = 'windup'
          boss.t = Math.max(0.35, (0.9 / angry + 0.3) * pace)
          // How many leaps, charges or rounds this attack has
          boss.left = {
            leap: Math.round(count),
            charge: Math.round(1 + count),
            orbs: Math.round(2 + count),
            venom: Math.round(2 * count),
            spikes: Math.round(2 * count),
            rockfall: Math.round(5 * count),
            ambush: 1,
            icefall: Math.round(2 + count),
          }[boss.move]
          boss.rounds = boss.left
        }
      } else if (boss.mode === 'windup') {
        // Rears up and glows: the attack is coming
        speed = 0
        if (boss.t <= 0) {
          if (boss.move === 'leap') {
            boss.mode = 'leap'
            const air = 1.1 * Math.sqrt(pace)
            sp.leap = to.clone().divideScalar(air)
            sp.vy = (GRAVITY * air) / 2
            sp.pos.y += 0.1
            sp.leapFor = air
          } else if (boss.move === 'charge') {
            boss.mode = 'charge'
            boss.dir.copy(target)
            boss.t = 2.4
          } else if (boss.move === 'ambush') {
            // Dives under; a ripple tracks the squirrel, settles, then it bursts up
            boss.mode = 'under'
            sp.hidden = true
            sound.play('quake')
            const follow = 1.4 * pace
            const warn = Math.max(0.55, 0.85 * pace)
            boss.trap = addTrap('geyser', s.pos.x, s.pos.z, warn, 0, follow)
            for (let i = 0; i < Math.round(3 * count); i++) {
              const at = nearSquirrel(110)
              addTrap('geyser', at.x, at.z, warn, follow)
            }
            boss.t = follow + warn
          } else {
            if (boss.move === 'rockfall' || boss.move === 'icefall') sound.play('quake')
            boss.mode = 'volley'
            boss.t = 0
          }
        }
      } else if (boss.mode === 'charge') {
        // Straight at where the squirrel was, until it slams into the brambles
        dir = boss.dir
        speed = (330 + boss.hits * 30) / Math.sqrt(pace)
        const out = sp.pos.clone().add(boss.dir.clone().multiplyScalar(speed * dt)).sub(wallCentre).setY(0)
        if (out.length() > BOSS_RADIUS - 24 || boss.t <= 0) {
          sound.play('crash')
          speed = 0
          boss.left -= 1
          if (boss.left > 0) {
            // Shakes it off, turns and goes again
            boss.mode = 'windup'
            boss.t = Math.max(0.3, 0.55 * pace)
          } else {
            dazedFor(2.6)
          }
        }
      } else if (boss.mode === 'leap') {
        dir = sp.leap.clone().normalize()
        sp.pos.addScaledVector(sp.leap, dt)
        sp.vy -= GRAVITY * dt
        const floorB = floorAt(sp.pos.x, sp.pos.z)
        sp.pos.y = Math.max(floorB, sp.pos.y + sp.vy * dt)
        if (sp.pos.y <= floorB) {
          // Lands with a shockwave; jump over the ring as it rolls out
          boss.left -= 1
          if (boss.left > 0) {
            boss.mode = 'windup'
            boss.t = Math.max(0.3, 0.5 * pace)
          } else {
            dazedFor(2.4)
          }
          boss.ring = 1
          boss.ringAt = sp.pos.clone()
          sp.leapFor = 0
          sp.vy = 0
          sound.play('quake')
          bossSmash(sp.pos, 60)
        }
      } else if (boss.mode === 'volley') {
        // Webs, venom, spikes or rocks, round after round, then worn out
        speed = 0
        if (boss.t <= 0) {
          if (boss.left > 0) {
            bossVolley()
            boss.left -= 1
            boss.t = boss.gap
          } else if (boss.move === 'orbs') {
            // None came back: it shrugs it off and prowls again
            if (!orbsOut()) {
              boss.mode = 'stalk'
              boss.t = (rand(1.2, 2) / angry) * pace
            }
          } else {
            dazedFor(2.6)
          }
        }
      } else if (boss.mode === 'under') {
        // Underground, following the ripple
        speed = 0
        if (boss.trap) sp.pos.set(boss.trap.pos.x, 0, boss.trap.pos.z)
        if (boss.t <= 0) {
          boss.mode = 'erupt'
          sp.hidden = false
          sp.pos.y = floorAt(sp.pos.x, sp.pos.z) + 0.1
          sp.vy = 240
          boss.trap = null
        }
      } else if (boss.mode === 'erupt') {
        // Bursts up out of the geyser and flops down dazed
        speed = 0
        sp.vy -= GRAVITY * dt
        const floorE = floorAt(sp.pos.x, sp.pos.z)
        sp.pos.y = Math.max(floorE, sp.pos.y + sp.vy * dt)
        if (sp.pos.y <= floorE) {
          sp.vy = 0
          sound.play('crash')
          bossSmash(sp.pos, 45)
          dazedFor(2.6)
        }
      } else if (boss.mode === 'dazed') {
        // Dizzy: open to a pounce
        speed = 0
        if (boss.t <= 0) {
          boss.mode = 'stalk'
          boss.t = (rand(1.4, 2.4) / angry) * pace
        }
      } else if (boss.mode === 'recover') {
        // Hurt and furious: bounds back, untouchable for a moment
        speed = -120
        if (boss.t <= 0) {
          boss.mode = 'stalk'
          boss.t = (rand(1.2, 2) / angry) * pace
        }
      }
      sp.pos.addScaledVector(dir, speed * dt)
      if (boss.mode !== 'leap' && boss.mode !== 'erupt') sp.pos.y = floorAt(sp.pos.x, sp.pos.z)
      // Kept inside its own ring
      const fromCentre = sp.pos.clone().sub(wallCentre).setY(0)
      if (fromCentre.length() > BOSS_RADIUS - 24) {
        fromCentre.setLength(BOSS_RADIUS - 24)
        sp.pos.x = wallCentre.x + fromCentre.x
        sp.pos.z = wallCentre.z + fromCentre.z
      }
      if (boss.mode !== 'charge' && boss.mode !== 'recover') sp.heading = Math.atan2(-target.z, target.x)
      else if (boss.mode === 'charge') sp.heading = Math.atan2(-boss.dir.z, boss.dir.x)
      sp.phase += dt * Math.max(Math.abs(speed), boss.mode === 'leap' ? 200 : boss.mode === 'dazed' ? 250 : 0) * 0.12
      sp.moving = Math.abs(speed) > 0
      // Tramples whatever it moves through
      const body = Math.max(30, sp.cfg.shadow[0] * BOSS_SIZE * 0.45)
      if (!sp.hidden && sp.pos.y - floorAt(sp.pos.x, sp.pos.z) < 20 && boss.mode !== 'dazed') bossSmash(sp.pos, body * 0.8)
      sp.windup = boss.mode === 'windup' ? 1 : 0

      // The shockwave ring
      if (boss.ring > 0) {
        const radius = (1 - boss.ring) * 230 + 10
        boss.ring = Math.max(0, boss.ring - dt / 1.05)
        quake.visible = boss.ring > 0 && view.p > 0.5
        quake.position.set(boss.ringAt.x, boss.ringAt.y + 0.6, boss.ringAt.z)
        quake.scale.set(radius, 1, radius)
        quakeMat.opacity = 0.85 * boss.ring
        const fromRing = Math.abs(flat(s.pos.clone().sub(boss.ringAt)) - radius)
        if (fromRing < 9 && s.pos.y - floorAt(s.pos.x, s.pos.z) < 4 && s.deadFor <= 0 && s.safeFor <= 0) hurt(s, 'bite')
      } else {
        quake.visible = false
      }

      // Brushing against it is harmless; being charged down, landed on or
      // burst up under is what hurts
      const reach = Math.max(30, sp.cfg.shadow[0] * BOSS_SIZE * 0.45)
      const close = flat(s.pos.clone().sub(sp.pos)) < reach && Math.abs(s.pos.y - sp.pos.y) < 30
      const attacking = boss.mode === 'charge' || boss.mode === 'leap' || boss.mode === 'erupt'
      if (close && attacking && !sp.hidden && s.deadFor <= 0 && s.safeFor <= 0) hurt(s, 'bite')
      // Pounces: a hit while it's dazed, a clank off it otherwise
      if (!sp.hidden && s.attackFor > 0 && s.struck && !s.struck.has(sp) && flat(mouthOf(s).sub(sp.pos)) < reach + 6) {
        s.struck.add(sp)
        if (boss.mode === 'dazed') {
          boss.hits += 1
          sp.flashFor = 0.3
          sound.play('kill')
          if (boss.hits >= BOSS_HITS) {
            showBossBar()
            winBoss()
            return
          }
          boss.mode = 'recover'
          boss.t = 1.1
        } else {
          // Untouchable: the pounce clanks off and it's thrown back
          sound.play('clank')
          const away = s.pos.clone().sub(sp.pos).setY(0).setLength(220)
          s.vel.set(away.x, 160, away.z)
          s.attackFor = 0
        }
      }
      showBossBar()

      // Dizzy stars circling its head
      const dazed = boss.mode === 'dazed'
      starMeshes.forEach((m, i) => {
        m.visible = dazed && view.p > 0.5
        if (!m.visible) return
        const a = clock * 4 + (i * Math.PI * 2) / 3
        m.position.set(sp.pos.x + Math.cos(a) * 18, sp.pos.y + 30 * (BOSS_SIZE / 2) + Math.sin(clock * 8 + i) * 2, sp.pos.z + Math.sin(a) * 18)
        m.rotation.set(clock * 3, clock * 5, 0)
      })
    }

    // ----- Smashing things -----
    // Knocked-off voxels hang a moment, top first, then tumble and dissolve away
    const DEBRIS = 900
    const debrisMesh = new InstancedMesh(keep(new BoxGeometry(1, 1, 1)), lambert('#ffffff'), DEBRIS)
    debrisMesh.frustumCulled = false
    // Colours first: the colour buffer is sized from the count when it's made
    for (let i = 0; i < DEBRIS; i++) debrisMesh.setColorAt(i, new Color('#ffffff'))
    debrisMesh.count = 0
    woods.add(debrisMesh)
    let debris = []
    const debrisEuler = new Euler()
    const squirrelGlow = new PointLight('#ffe6c0', 0, 120, 2)
    woods.add(squirrelGlow)

    // Little puffs of dust kicked up where its paws land
    const DUST = 80
    const dustMesh = new InstancedMesh(keep(new BoxGeometry(1, 1, 1)), lambert('#c9b48a'), DUST)
    dustMesh.frustumCulled = false
    dustMesh.count = 0
    woods.add(dustMesh)
    let dust = []
    const kickDust = (s) => {
      const back = facing(s).multiplyScalar(-SQ_W * 0.3)
      for (let i = 0; i < Math.max(1, Math.round(4 * gv('particles'))); i++) {
        dust.push({
          pos: new Vector3(s.pos.x + back.x + rand(-4, 4), s.pos.y + 1, s.pos.z + back.z + rand(-4, 4)),
          vel: new Vector3(back.x * 0.6 + rand(-12, 12), rand(8, 22), back.z * 0.6 + rand(-12, 12)),
          spin: rand(-3, 3),
          size: rand(1.8, 3),
          age: 0,
          life: rand(0.35, 0.6),
        })
      }
      dust = dust.slice(-DUST)
    }
    // Water thrown up where its paws land in a pool or puddle
    const DROPS = 120
    const dropMat = new MeshBasicMaterial({ color: '#d6f1ff', transparent: true, opacity: 0.85 })
    const dropMesh = new InstancedMesh(keep(new BoxGeometry(1, 1, 1)), dropMat, DROPS)
    dropMesh.frustumCulled = false
    dropMesh.count = 0
    woods.add(dropMesh)
    let splashDrops = []
    const kickSplash = (s, count) => {
      for (let i = 0; i < Math.max(2, Math.round(count * gv('particles'))); i++) {
        const a = rand(0, Math.PI * 2)
        const out = rand(10, 30) * (count > 8 ? 1.6 : 1)
        splashDrops.push({
          pos: new Vector3(s.pos.x + Math.cos(a) * 3, s.pos.y + 1, s.pos.z + Math.sin(a) * 3),
          vel: new Vector3(Math.cos(a) * out + s.vel.x * 0.2, rand(40, 90) * (count > 8 ? 1.3 : 1), Math.sin(a) * out + s.vel.z * 0.2),
          size: rand(1.2, 2.2),
          age: 0,
        })
      }
      splashDrops = splashDrops.slice(-DROPS)
    }
    const inWater = (s) =>
      s.pos.y < 1 && (waters.some((w) => Math.hypot((s.pos.x - w.x) / w.rx, (s.pos.z - w.z) / w.rz) < 1) || onPuddle(s))
    const updateDrops = (dt) => {
      splashDrops = splashDrops.filter((d) => {
        d.age += dt
        d.vel.y -= GRAVITY * 0.8 * dt
        d.pos.addScaledVector(d.vel, dt)
        return d.pos.y > floorAt(d.pos.x, d.pos.z) && d.age < 1.2
      })
      splashDrops.forEach((d, i) => {
        bugQuat.identity()
        bugScale.set(d.size, d.size * 1.4, d.size).multiplyScalar(Math.max(0.001, view.e))
        bugMatrix.compose(d.pos, bugQuat, bugScale)
        dropMesh.setMatrixAt(i, bugMatrix)
      })
      dropMesh.count = splashDrops.length
      dropMesh.instanceMatrix.needsUpdate = true
    }

    // Streaks running down each waterfall near the squirrel, spray at the foot,
    // and now and then a nut carried over the edge
    const FALL_STREAKS = 90
    const streakMesh = new InstancedMesh(keep(new BoxGeometry(1.2, 6, 0.6)), new MeshBasicMaterial({ color: '#f2fbff' }), FALL_STREAKS)
    streakMesh.frustumCulled = false
    streakMesh.count = 0
    woods.add(streakMesh)
    let fallNutIn = 8
    const updateFalls = (dt) => {
      const s = world.squirrel
      let n = 0
      const near = falls.filter((f) => Math.hypot(f.x - s.pos.x, f.z - s.pos.z) < 600)
      near.forEach((f) => {
        for (let k = 0; k < 10 && n < FALL_STREAKS; k++) {
          const t = (clock * 0.9 + k * 0.137 + f.x * 0.01) % 1
          const across = ((k * 0.37) % 1 - 0.5) * TERRAIN_CELL * 0.8
          rainAt.set(f.x + f.dx * 0.8 + (f.dz ? across : 0), f.base + (f.h - f.base) * (1 - t), f.z + f.dz * 0.8 + (f.dx ? across : 0))
          bugQuat.identity()
          bugScale.setScalar(Math.max(0.001, view.e))
          bugMatrix.compose(rainAt, bugQuat, bugScale)
          streakMesh.setMatrixAt(n++, bugMatrix)
        }
      })
      streakMesh.count = view.p > 0.5 ? n : 0
      streakMesh.instanceMatrix.needsUpdate = true
      if (!world.staged || world.paused) return
      // Spray where it lands, if it's close
      const close = near.find((f) => Math.hypot(f.x - s.pos.x, f.z - s.pos.z) < 300)
      if (close && Math.random() < dt * 6) {
        splashDrops.push({
          pos: new Vector3(close.x + close.dx * 6 + rand(-4, 4), close.base + 1, close.z + close.dz * 6 + rand(-4, 4)),
          vel: new Vector3(close.dx * rand(10, 40) + rand(-15, 15), rand(30, 70), close.dz * rand(10, 40) + rand(-15, 15)),
          size: rand(1.2, 2),
          age: 0,
        })
      }
      // A nut over the top, to bob about in the pool below
      fallNutIn -= dt
      if (fallNutIn <= 0) {
        fallNutIn = rand(14, 26)
        const f = near.find((ff) => Math.hypot(ff.x - s.pos.x, ff.z - s.pos.z) < 450)
        if (f) {
          spawnAcorn(new Vector3(f.x - f.dx * 4, f.h + AC_R + 2, f.z - f.dz * 4), Math.random() < superChance())
          const a = world.acorns[world.acorns.length - 1]
          a.vel.set(f.dx * 45, 30, f.dz * 45)
        }
      }
    }

    const updateDust = (dt) => {
      updateDrops(dt)
      updateFalls(dt)
      dust = dust.filter((d) => {
        d.age += dt
        d.pos.addScaledVector(d.vel, dt)
        d.vel.multiplyScalar(Math.exp(-4 * dt))
        return d.age < d.life
      })
      dust.forEach((d, i) => {
        const t = d.age / d.life
        // Billows out, then thins away
        const size = d.size * (0.6 + t * 1.2) * (1 - t * t)
        debrisEuler.set(0, d.spin * d.age, 0)
        bugQuat.setFromEuler(debrisEuler)
        bugScale.setScalar(Math.max(0.001, size * view.e))
        bugMatrix.compose(d.pos, bugQuat, bugScale)
        dustMesh.setMatrixAt(i, bugMatrix)
      })
      dustMesh.count = dust.length
      dustMesh.instanceMatrix.needsUpdate = true
    }

    const shatter = (pieces) => {
      debris = debris.concat(pieces).slice(-DEBRIS)
    }
    const updateDebris = (dt) => {
      debris = debris.filter((d) => {
        if (d.delay > 0) {
          d.delay -= dt
          return true
        }
        d.age += dt
        d.vel.y -= GRAVITY * 0.35 * dt
        d.pos.addScaledVector(d.vel, dt)
        if (d.pos.y < d.size / 2) {
          d.pos.y = d.size / 2
          d.vel.multiplyScalar(0.4)
          d.vel.y = Math.abs(d.vel.y) * 0.3
        }
        return d.age < d.life
      })
      debris.forEach((d, i) => {
        const left = 1 - d.age / d.life
        debrisEuler.set(d.spin.x * d.age, d.spin.y * d.age, d.spin.z * d.age)
        bugQuat.setFromEuler(debrisEuler)
        bugScale.setScalar(Math.max(0.001, d.size * Math.min(1, left * 1.6) * view.e))
        bugMatrix.compose(d.pos, bugQuat, bugScale)
        debrisMesh.setMatrixAt(i, bugMatrix)
        debrisMesh.setColorAt(i, d.colour)
      })
      debrisMesh.count = debris.length
      debrisMesh.instanceMatrix.needsUpdate = true
      if (debrisMesh.instanceColor) debrisMesh.instanceColor.needsUpdate = true
    }
    const piece = (pos, colour, size, from, delay = 0) => ({
      pos,
      vel: pos.clone().sub(from).setY(0).normalize().multiplyScalar(rand(20, 70)).setY(rand(30, 110)),
      spin: new Vector3(rand(-6, 6), rand(-6, 6), rand(-6, 6)),
      colour,
      size,
      delay,
      age: 0,
      life: rand(0.7, 1.3),
    })

    // A pounced mushroom bursts into red and white bits and stays gone
    const smashMushroom = (shroom) => {
      sound.play('smash')
      world.eaten.add(shroom.id)
      shroom.chunk.group.remove(shroom.mesh)
      shroom.mesh.geometry.dispose()
      shroom.chunk.mushrooms = shroom.chunk.mushrooms.filter((m) => m !== shroom)
      indexChunks()
      const centre = new Vector3(shroom.x, 4, shroom.z)
      shatter(
        Array.from({ length: 24 }, (_, i) =>
          piece(
            new Vector3(shroom.x + rand(-5, 5), rand(2, 10), shroom.z + rand(-5, 5)),
            mushroomMats[i % 3 === 0 ? 1 : 0].color,
            VOXEL,
            centre,
          ),
        ),
      )
    }

    // Three pounces fell a tree: the top of the crown, the rest of it, then
    // the trunk. A nut tree that hasn't grown its acorn yet may shake it loose.
    const NUT_TREES = ['oak', 'wide', 'willow', 'kapok', 'palm', 'spruce']
    const hitTree = (t) => {
      t.hits += 1
      world.felled.set(t.id, t.hits)
      if (NOT_TREES.includes(t.kind)) sound.play(t.hits >= TREE_HITS ? 'crash' : 'smash')
      else sound.play(t.hits >= TREE_HITS ? 'fell' : 'chop')
      const { voxels, removed } = damageTree(t.voxels, t.hits)
      t.voxels = voxels
      const cos = Math.cos(t.rot)
      const sin = Math.sin(t.rot)
      const top = Math.max(0, ...removed.map((v) => v[1]))
      const centre = new Vector3(t.x, t.canopyY, t.z)
      const most = 220 * gv('particles')
      const sample = removed.length > most ? removed.filter(() => Math.random() < most / removed.length) : removed
      shatter(
        sample.map(([x, y, z, mat]) => {
          const lx = (x + 0.5 + t.offset[0]) * TREE_VOXEL
          const lz = (z + 0.5 + t.offset[2]) * TREE_VOXEL
          const pos = new Vector3(t.x + cos * lx + sin * lz, (y + 0.5) * TREE_VOXEL, t.z - sin * lx + cos * lz)
          return piece(pos, t.mats[mat].color, TREE_VOXEL * 0.9, centre, (top - y) * 0.03 + rand(0, 0.08))
        }),
      )
      hearts.forEach((h) => {
        if (h.tree !== t || h.loose) return
        h.loose = true
        h.vy = 0
      })
      if (NUT_TREES.includes(t.kind) && !world.harvested.has(t.id) && Math.random() < 0.45) {
        world.harvested.add(t.id)
        const a = rand(0, Math.PI * 2)
        spawnAcorn(
          new Vector3(t.x + Math.cos(a) * (t.trunkRadius + 6), t.canopyY * 0.8, t.z + Math.sin(a) * (t.trunkRadius + 6)),
          Math.random() < superChance(),
        )
      }
      t.topY = undefined
      t.mesh.geometry.dispose()
      if (t.hits >= TREE_HITS) {
        t.chunk.group.remove(t.mesh)
        t.mats.forEach((m) => m.dispose())
        t.chunk.trees = t.chunk.trees.filter((other) => other !== t)
        indexChunks()
        return
      }
      const rebuilt = meshTree(voxels, t.offset)
      t.mesh.geometry = rebuilt.geometry
      t.geometry = rebuilt.geometry
      t.tops = rebuilt.tops
      if (t.hits >= 2 || NOT_TREES.includes(t.kind)) t.canopyY = Math.min(t.canopyY, (t.baseY || 0) + Math.max(0, ...t.tops.values()))
    }

    // What a pounce lands on besides spiders: mushrooms and tree trunks
    const strikeScenery = () => {
      const s = world.squirrel
      if (!world.staged || !(s.attackFor > 0) || !s.struck) return
      const mouth = mouthOf(s)
      if (s.pos.y - floorAt(s.pos.x, s.pos.z) < 12) {
        const shroom = mushrooms.find((m) => !s.struck.has(m) && Math.hypot(m.x - mouth.x, m.z - mouth.z) < 14)
        if (shroom) {
          s.struck.add(shroom)
          smashMushroom(shroom)
        }
      }
      const tree = trees.find(
        (t) =>
          !s.struck.has(t) &&
          s.pos.y < t.canopyY &&
          Math.hypot(t.x - mouth.x, t.z - mouth.z) < t.trunkRadius + 10,
      )
      if (tree) {
        s.struck.add(tree)
        hitTree(tree)
      }
    }

    // Now and then, while lives are down, a heart ripens in a nearby tree.
    // Climbing to the top of that tree picks it.
    const updateHearts = (dt, now) => {
      const s = world.squirrel
      hearts = hearts.filter((h) => {
        if (h.loose ? flat(h.pos.clone().sub(s.pos)) < viewRadius() : trees.includes(h.tree)) return true
        woods.remove(h.mesh)
        return false
      })
      if (!world.staged) return
      heartIn -= dt
      if (world.lives < maxLives() && hearts.length < MAX_HEARTS && heartIn <= 0) {
        heartIn = rand(8, 16) * level().hearts
        const near = trees.filter((t) => {
          const d = Math.hypot(t.x - s.pos.x, t.z - s.pos.z)
          return d > 80 && d < 450 && !t.hits && !NOT_TREES.includes(t.kind) && !hearts.some((h) => h.tree === t)
        })
        const tree = near[Math.floor(Math.random() * near.length)]
        if (tree) {
          // Among the leaves, at the side of the crown a little below its top
          const a = rand(0, Math.PI * 2)
          const mesh = new Mesh(heartGeo, heartMat)
          const crownTop = Math.max(...tree.tops.values()) + (tree.baseY || 0)
          const pos = new Vector3(
            tree.x + Math.cos(a) * tree.canopyRadius * 0.95,
            Math.max(tree.canopyY - 10, crownTop - rand(8, 16)),
            tree.z + Math.sin(a) * tree.canopyRadius * 0.95,
          )
          woods.add(mesh)
          hearts.push({ tree, mesh, pos })
        }
      }
      hearts.forEach((h) => {
        const floorH = h.loose ? floorAt(h.pos.x, h.pos.z) + 8 : 0
        if (h.loose && h.pos.y > floorH) {
          h.vy -= GRAVITY * 0.5 * dt
          h.pos.y = Math.max(floorH, h.pos.y + h.vy * dt)
        }
        h.mesh.position.set(h.pos.x, h.pos.y + Math.sin(now / 400) * 1.5, h.pos.z)
        h.mesh.rotation.y += dt * 1.5
        h.mesh.scale.setScalar(Math.max(0.001, view.e))
      })
      const centre = s.pos.clone().setY(s.pos.y + SQ_H / 2)
      const ripe = hearts.find(
        (h) =>
          (!h.loose && s.climb === h.tree && s.pos.y >= h.tree.canopyY - 6) ||
          (!h.loose && s.perch === h.tree) ||
          h.pos.distanceTo(centre) < 20,
      )
      if (ripe && world.lives < maxLives()) {
        world.lives += 1
        showScore()
        popup('+1', 'life')
        sound.play('heart')
        woods.remove(ripe.mesh)
        hearts = hearts.filter((h) => h !== ripe)
      }
    }

    // ----- Drawing -----
    // Footprint-sized shadow that shrinks as its owner leaves the ground
    const placeShadow = (mesh, x, z, width, depth, height, heading = 0) => {
      const floor = view.p > 0.5 ? floorAt(x, z) : 0
      const fade = clamp(1 - (height - floor) / 120, 0.3, 1)
      mesh.position.set(x, floor + 0.3, z)
      mesh.rotation.y = heading
      mesh.scale.set(width * fade, 1, depth * fade)
    }

    const sky = new Color()
    // Phone screens are too dim for a rainy night, so there the rain and
    // storms bring daylight back with them
    let wetDay = 0
    const dayCycle = (dt = 0) => {
      const angle = world.time * Math.PI * 2
      const height = Math.sin(angle)
      wetDay = clamp(wetDay + (touchOnly && wetness() !== 'clear' ? dt : -dt) / 3, 0, 1)
      const day = Math.max(clamp((height + 0.15) / 0.4, 0, 1), wetDay)
      const dusk = 1 - Math.min(1, Math.abs(height) / 0.25)
      sky.copy(SKY_NIGHT).lerp(SKY_DAY, day).lerp(SKY_DUSK, dusk * 0.55)
      return { angle, day }
    }

    const sync = ({ angle, day }, dt) => {
      const { p, e } = view
      const s = world.squirrel

      // Flat theme colours at rest, natural colours lit by the sun in nut mode
      tinted.forEach((m) => {
        m.color.copy(m.userData.role === 'bg' ? themeBg : themeMain).lerp(m.userData.natural, p)
        m.emissive.copy(m.color)
        m.emissiveIntensity = 1 - p
      })
      // The skin from the nut store: glowing, see-through or cycling the rainbow
      const skin = skinOf()
      squirrelMats.forEach((m, i) => {
        if (skin.rainbow) {
          skinTint.setHSL((clock * 0.35 + i * 0.25) % 1, 0.85, 0.58)
          m.color.lerp(skinTint, p)
          m.emissive.copy(m.color)
        }
        const glow = skin.glow ? skin.glow[i] : 0
        if (glow) m.emissiveIntensity = Math.max(1 - p, glow * p * (skin.id === 'molten' ? 0.8 + 0.2 * Math.sin(clock * 9 + i) : 1))
        if (skin.ghost) m.opacity = lerp(1, 0.55 + 0.1 * Math.sin(clock * 3), p)
      })
      hat.visible = p > 0.5
      superMats.forEach((m) => {
        m.color.setHSL((clock * 0.6 + m.userData.offset) % 1, 1, 0.6)
        m.emissive.copy(m.color)
        m.emissiveIntensity = 0.6 * p + (1 - p)
      })
      // Super: the squirrel cycles the rainbow too, and the whole view trips out
      if (s.superFor > 0) {
        squirrelMats.forEach((m, i) => {
          m.color.setHSL((clock * 0.8 + i * 0.2) % 1, 0.9, 0.6)
          m.emissive.copy(m.color)
          m.emissiveIntensity = 0.45 * p
        })
      }
      const tripping = s.superFor > 0 && p > 0.01
      canvas.style.filter = tripping
        ? `hue-rotate(${Math.round((clock * 140) % 360)}deg) saturate(1.7)`
        : ''
      counter.classList.toggle('super', tripping)
      // Poisoned: a pulsing green glow that fades as it wears off
      const poison = s.poisonFor > 0 ? Math.min(1, s.poisonFor / 0.6) * (0.65 + 0.35 * Math.sin(clock * 14)) : 0
      if (poison > 0) {
        squirrelMats.forEach((m) => {
          m.color.lerp(POISON_GREEN, 0.5 * poison)
          m.emissive.copy(POISON_GREEN)
          m.emissiveIntensity = Math.max(m.emissiveIntensity, 0.55 * poison * p)
        })
      }
      // Ease the weather towards the biome the squirrel is in
      const here = world.staged ? biome : 'meadow'
      const look = BIOMES[here]
      // Passing rain or storms darken the biome's sky and bring the rain
      const wet = world.staged && weather !== 'clear' && here !== 'snow' ? BIOMES[weather] : null
      const settle = 1 - Math.exp(-0.8 * dt)
      atmo.sky.lerp(biomeSky[wet && wet.skyMix > look.skyMix ? weather : here], settle)
      atmo.ground.lerp(biomeGround[here], settle)
      const want = {
        skyMix: wet ? Math.max(look.skyMix, wet.skyMix) : look.skyMix,
        light: wet ? Math.min(look.light, wet.light) : look.light,
        fog: wet ? Math.min(look.fog, wet.fog) : look.fog,
        rain: wet ? Math.max(look.rain, wet.rain) : look.rain,
        storm: wet ? Math.max(look.storm, wet.storm) : look.storm,
      }
      // A blizzard whites out the view
      want.fog = lerp(want.fog, 0.16, blizzard)
      want.skyMix = lerp(want.skyMix, 0.95, blizzard)
      want.light *= 1 - 0.25 * blizzard
      ;['skyMix', 'light', 'fog', 'rain', 'storm'].forEach((k) => {
        atmo[k] = lerp(atmo[k], want[k], settle)
      })
      // Overcast skies take on the weather's colour, darker still at night
      sky.lerp(atmo.sky.clone().lerp(SKY_NIGHT, (1 - day) * 0.85), atmo.skyMix)
      groundMat.color.copy(atmo.ground).lerp(snowColour, 0.75 * winterNow)
      waterMat.emissiveIntensity = 0.7 + Math.sin(clock * 2) * 0.25
      // Lightning lights up everything for a moment
      const strike = flash * (0.7 + 0.3 * Math.sin(clock * 70))
      sky.lerp(BOLT_WHITE, strike * 0.55)
      hemi.intensity = ((0.5 + 0.3 * day) * atmo.light + strike * 1.4) * p
      hemi.color.copy(sky).lerp(SUN_NIGHT, 1 - day)
      sun.intensity = (0.35 + 0.55 * day) * atmo.light * atmo.light * p
      sun.color.copy(SUN_NIGHT).lerp(SUN_DAY, day)
      // Sun by day, moon by night, both sweeping across the sky
      const arc = day > 0.5 ? angle : angle + Math.PI
      sun.position.set(
        s.pos.x + Math.cos(arc) * 300,
        Math.max(0.25, Math.abs(Math.sin(arc))) * 400,
        s.pos.z + 150,
      )
      sun.target.position.copy(s.pos)
      const horizon = (body, a, lift) => {
        body.position.set(s.pos.x + Math.cos(a) * 750, 20 + Math.sin(a) * lift, s.pos.z - 1700)
        body.visible = Math.sin(a) > -0.1
      }
      horizon(sunMesh, angle, 100)
      horizon(moonMesh, angle + Math.PI, 90)
      // Hidden behind the clouds in bad weather
      if (atmo.skyMix > 0.5) {
        sunMesh.visible = false
        moonMesh.visible = false
      }
      scene.fog.color.copy(sky)
      shadowMat.opacity = 0.3 * p
      starMat.opacity = (1 - day) * p * (1 - atmo.skyMix)

      woods.visible = p > 0.01
      chunks.forEach((c) => {
        c.group.scale.y = Math.max(0.001, e * ease(c.grow))
        c.group.visible = !under
      })
      ground.visible = !under
      if (under) {
        under.built.group.scale.y = Math.max(0.001, e)
        // Dark as a cave, lit by its own lights (dimly by itself without them)
        sky.copy(under.void)
        scene.fog.color.copy(sky)
        hemi.intensity = (gv('lighting') > 0 ? 0.3 : 0.7) * p
        hemi.color.copy(under.void).lerp(new Color('#ffffff'), 0.5)
        sun.intensity = 0.1 * p
        sunMesh.visible = false
        moonMesh.visible = false
        starMat.opacity = 0
      }
      ground.position.set(s.pos.x, 0, s.pos.z)
      stars.position.set(s.pos.x, 0, s.pos.z)
      // Fade trees standing between the camera and the squirrel, but never the
      // one it's on, climbing or jumping onto, nor ones the view clears
      const cam = camera.position
      const seg = new Vector3(s.pos.x - cam.x, 0, s.pos.z - cam.z)
      const segLen = seg.lengthSq()
      trees.forEach((t) => {
        if (t.topY === undefined) t.topY = (t.baseY || 0) + Math.max(0, ...t.tops.values())
        const along = clamp(((t.x - cam.x) * seg.x + (t.z - cam.z) * seg.z) / (segLen || 1), 0, 1)
        const miss = Math.hypot(cam.x + seg.x * along - t.x, cam.z + seg.z * along - t.z)
        const sightY = cam.y + (s.pos.y - cam.y) * along
        const underfoot =
          t === s.perch ||
          t === s.climb ||
          Math.hypot(s.pos.x - t.x, s.pos.z - t.z) < t.canopyRadius + TREE_VOXEL * 3
        const blocks = !underfoot && along < 0.97 && miss < t.canopyRadius + 6 && t.topY > sightY
        t.fade = clamp(t.fade + (blocks ? -dt : dt) * 4, 0.15, 1)
        t.mats.forEach((m) => {
          m.opacity = t.fade
          m.depthWrite = t.fade > 0.99
        })
      })

      const running = flat(s.vel) > 5
      let step = running ? Math.floor(s.phase) % 2 : 1
      // Bounding gait while running, nibbling bob while eating
      let bob = 0
      let hop = 0
      let pitch = 0
      let stretch = 0
      if (p < 0.5) {
        // Flat view: snapped to whole pixels like the sprite it came from
        if (running) bob = Math.round(Math.abs(Math.sin(s.phase * Math.PI)) * 2) * VOXEL
        else if (s.eatFor > 0) bob = -(Math.floor(s.phase) % 2) * VOXEL
      } else {
        // Woods: a squirrel's bound. Hind legs push off nose-up, it arcs
        // through the air stretched out, lands nose-down on its front paws
        // and gathers itself (squashed) for the next push. Eases in and out
        // of it as it speeds up and slows down.
        const want = s.grounded && !s.climb && s.attackFor <= 0 ? clamp(flat(s.vel) / PLAYER_SPEED, 0, 1) : 0
        s.gait = lerp(s.gait || 0, want, 1 - Math.exp(-10 * dt))
        const c = (s.phase / 3) % 1
        const AIR = 0.6 // share of each bound spent in the air
        const air = c < AIR ? c / AIR : 0
        const ground = c < AIR ? 0 : (c - AIR) / (1 - AIR)
        hop = 4 * air * (1 - air) * 7 * s.gait
        pitch =
          (c < AIR ? Math.cos(Math.PI * air) : Math.cos(Math.PI * (1 - ground))) * 0.2 * s.gait
        stretch = (c < AIR ? 0.1 * Math.sin(Math.PI * air) : -0.1 * Math.sin(Math.PI * ground)) * s.gait
        if (s.gait > 0.05) step = c < AIR ? 0 : 1
        // Paws hit the ground: a puff of dust behind it
        const landed = s.lastBound !== undefined && s.lastBound < AIR && c >= AIR
        if (landed && s.gait > 0.4 && s.grounded) {
          if (inWater(s)) kickSplash(s, 5)
          else kickDust(s)
        }
        s.lastBound = c
        if (s.eatFor > 0 && !running) bob = -Math.abs(Math.sin(s.phase * Math.PI)) * VOXEL * 0.8
      }
      // A big splash on stepping or dropping into the water
      const inPool = world.staged && p > 0.5 && inWater(s)
      if (inPool && !s.wasWet) {
        kickSplash(s, 16)
        sound.play('spit')
      }
      s.wasWet = inPool
      // A soft glow so it can be picked out in the dark and the gloom
      const dark = under ? 1 : clamp(1 - daylight * atmo.light, 0, 1)
      squirrelGlow.visible = gv('lighting') > 0
      squirrelGlow.intensity = world.staged ? (0.25 + 1.3 * dark) * clamp((p - 0.5) * 2, 0, 1) : 0
      squirrelGlow.position.set(s.pos.x, s.pos.y + SQ_H + 10, s.pos.z)
      squirrelGroup.position.copy(s.pos)
      squirrelGroup.position.y += hop
      // Nose up while climbing
      squirrelGroup.rotation.set(s.tilt, s.heading, s.climb ? Math.PI / 2 : 0)
      // Blinks while untouchable after coming back
      // and flashes slowly, then faster, as super power runs out
      const left = s.superFor || 0
      const flashRate = left > 0 && left < SUPER_WARNING ? lerp(3, 14, 1 - left / SUPER_WARNING) : 0
      squirrelGroup.visible =
        fpBlend * view.e < 0.6 &&
        (s.safeFor <= 0 || left > 0 || Math.floor(s.safeFor * 10) % 2 === 0) &&
        (flashRate === 0 || Math.floor(clock * flashRate) % 2 === 0)
      squirrelBody.position.y = bob
      // Pounce: rear up, then dive nose-first
      const pounce = s.attackFor > 0 ? 1 - s.attackFor / POUNCE_TIME : 0
      squirrelBody.rotation.z = 0.55 * Math.sin(pounce * Math.PI * 2) + pitch
      squirrelBody.scale.set(1 + 0.18 * Math.sin(pounce * Math.PI) + stretch, 1 - stretch * 0.8, 1)
      squirrelLegs.forEach((legs, i) => {
        legs.visible = i === step && !s.gliding
        legs.rotation.z = pitch
      })
      // Gliding: spread flat and wide like a flying squirrel
      if (s.gliding && p > 0.5) {
        squirrelBody.scale.set(1.15, 0.7, 1.8)
        squirrelBody.rotation.z = -0.1
      }
      // Playing as a creature: it moves just as the squirrel's body would
      const asCreature = !!charRig && p > 0.5
      squirrelBody.visible = !asCreature
      charGroup.visible = asCreature
      if (asCreature) {
        squirrelLegs.forEach((legs) => {
          legs.visible = false
        })
        charGroup.position.y = squirrelBody.position.y
        charGroup.rotation.z = squirrelBody.rotation.z
        charGroup.scale.copy(squirrelBody.scale)
        stepRig(charRig, flat(s.vel) > 8 && s.grounded ? 1 : 0, clock * 11)
        styleRig(charRig, s)
      }
      squirrelShadow.visible = p > 0.01
      const trip = s.caveFor > 0 ? 1 - Math.abs(s.caveFor - CAVE_TRIP / 2) / (CAVE_TRIP / 2) : 0
      const fadeTo = trip > 0.001 ? String(Math.min(1, trip * 1.4)) : ''
      if (fadeEl.style.opacity !== fadeTo) fadeEl.style.opacity = fadeTo
      placeShadow(squirrelShadow, s.pos.x, s.pos.z, SQ_W * 0.75, SQ_D, s.pos.y, s.heading)

      acornMeshes.forEach((m, a) => {
        if (world.acorns.includes(a)) return
        scene.remove(m.body, m.shadow)
        acornMeshes.delete(a)
      })
      world.acorns.forEach((a) => {
        const m = meshFor(a)
        m.body.position.copy(a.pos)
        m.body.quaternion.copy(a.quat)
        m.shadow.visible = p > 0.01
        placeShadow(m.shadow, a.pos.x, a.pos.z, AC_R * 1.6, AC_R * 1.6, a.pos.y - AC_R)
      })

      spiders.forEach((sp) => {
        const shown = (sp.alive || sp.deadFor > 0) && !sp.hidden && !(under && sp.boss)
        sp.group.visible = shown
        sp.shadow.visible = shown
        if (!shown) return
        // Hurt spiders flash red; dead ones roll onto their backs, then shrink away
        const dying = sp.alive ? 0 : 1 - sp.deadFor / SPIDER_DEATH
        const roll = ease(clamp(dying / 0.4, 0, 1))
        const fade = clamp((1 - dying) / 0.4, 0, 1)
        const red = sp.flashFor > 0 || (!sp.alive && dying < 0.6)
        const glow = sp.alive && sp.chargedFor > 0 && Math.sin(clock * 30 + sp.phase) > -0.3
        // Bosses flicker yellow while dazed and orange as they wind up
        let bossLook = null
        if (sp.boss && boss && sp.alive) {
          if (boss.mode === 'dazed' && Math.sin(clock * 12) > 0) bossLook = dazeMat
          else if (boss.mode === 'windup' && Math.sin(clock * 30) > 0) bossLook = angryMat
        }
        sp.parts.forEach(([mesh, mat]) => {
          mesh.material = red ? hurtMat : bossLook || (glow ? glowMat : mat)
        })
        // In thick fog, creatures loom out of it only when close
        const foggy = clamp((0.6 - atmo.fog) / 0.3, 0, 1)
        const near = flat(sp.pos.clone().sub(s.pos))
        const hide = sp.boss ? 1 : lerp(1, clamp((170 - near) / 50, 0, 1), foggy)
        // Frogs get about in hops
        const hop = sp.kind === 'frog' && sp.alive && sp.moving && sp.leapFor <= 0 ? Math.abs(Math.sin(sp.phase * 0.35)) * 6 : 0
        // A dazed boss flips onto its back with its legs flailing, except the
        // wolf, which keels over onto its side, and the snake, which spins
        let flip = 0
        let tip = 0
        let spin = 0
        if (sp.boss && boss && sp.alive && boss.mode === 'dazed') {
          const since = (boss.dazedLength || 1) - boss.t
          const k = ease(clamp(since / 0.35, 0, 1)) * ease(clamp(boss.t / 0.35, 0, 1))
          if (sp.kind === 'wolf') tip = k
          else if (sp.kind === 'snake') spin = k
          else flip = k
        }
        const flail = flip > 0 || tip > 0
        sp.group.position.set(sp.pos.x, sp.pos.y + hop + roll * 3 * VOXEL + flip * 3 * VOXEL * (sp.size || 1), sp.pos.z)
        // Crouches before springing, nose up through the leap
        const pitch = sp.alive && sp.leapFor > 0 ? clamp(sp.vy / 400, -0.5, 0.5) : 0
        sp.group.rotation.set(Math.max(roll, flip) * Math.PI + tip * Math.PI * 0.5, sp.heading + spin * Math.sin(clock * 9) * 1.6, pitch)
        sp.group.scale.setScalar(Math.max(0.001, e * fade * (sp.size || 1) * hide))
        if (sp.asleep && sp.alive) sp.group.scale.y *= 0.8
        if (sp.alive && sp.windup > 0) sp.group.scale.y *= 0.75
        if (sp.tail) {
          let cock = 0
          if (sp.alive && sp.windup > 0) cock = 0.4
          if (sp.alive && sp.stingFor > 0) {
            const k = 1 - sp.stingFor / STING_TIME
            cock = k < 0.5 ? lerp(0.4, -1.3, ease(k * 2)) : lerp(-1.3, 0, ease((k - 0.5) * 2))
          }
          sp.tail.rotation.z = cock + (sp.alive ? Math.sin(sp.phase * 0.3) * 0.05 : 0)
        }
        if (sp.kind === 'spider' || sp.kind === 'scorpion') {
          sp.legs.forEach((leg, i) => {
            // Legs curl and twitch once it's over
            leg.rotation.y = sp.alive ? Math.sin(sp.phase + (i % 2) * Math.PI) * 0.45 : 0
            leg.rotation.z = sp.alive ? 0 : 0.6 + Math.sin(dying * 40 + i) * 0.15
          })
        } else if (sp.kind === 'croc' || sp.kind === 'iguana') {
          // A low sprawling waddle, swinging side to side
          sp.legs.forEach((leg, i) => {
            const pair = i === 0 || i === 3 ? 0 : Math.PI
            leg.rotation.z = sp.alive ? Math.sin(sp.phase * 0.5 + pair) * 0.6 * (sp.moving || flail ? 1 : 0) : 1.2
          })
          sp.body.rotation.y = sp.alive && sp.moving ? Math.sin(sp.phase * 0.5) * 0.12 : 0
        } else if (sp.kind === 'wolf' || sp.kind === 'bear') {
          // Diagonal pairs swing together at a lope; stiff once beaten
          sp.legs.forEach((leg, i) => {
            const pair = i === 0 || i === 3 ? 0 : Math.PI
            leg.rotation.z = sp.alive ? Math.sin(sp.phase * 0.6 + pair) * 0.7 * (sp.moving || flail ? 1 : 0) : 0.9
          })
        } else if (sp.kind === 'bat') {
          // Wings beat fast; folded flat as it dives; still once beaten
          const beat = sp.alive ? (sp.leapFor > 0 ? 0.2 : 0.35 + Math.sin(clock * 20 + sp.phase) * 0.75) : 1.2
          sp.legs.forEach((wing, i) => {
            wing.rotation.x = (i ? 1 : -1) * beat
          })
        } else if (sp.kind === 'snake') {
          // A wave runs back along the body from the head
          const wave = sp.alive ? 2.5 : 0.4
          sp.segs.forEach((seg, i) => {
            const along = -(i + 1) * SNAKE_SEGMENT * VOXEL * 0.9
            seg.position.set(along, 0, Math.sin(sp.phase * 0.4 - i * 0.9) * wave * (i + 1) * 0.5)
            seg.rotation.y = Math.cos(sp.phase * 0.4 - i * 0.9) * 0.5 * (sp.alive ? 1 : 0)
          })
        }
        const [sw, sd] = sp.cfg.shadow
        const big = (sp.size || 1) * hide
        placeShadow(sp.shadow, sp.pos.x, sp.pos.z, sw * fade * big, sd * fade * big, sp.pos.y, sp.heading)
        sp.shadow.visible = hide > 0.01
      })

      while (crumbMeshes.length < world.crumbs.length) {
        const m = new Mesh(crumbGeo, acornMats[0])
        scene.add(m)
        crumbMeshes.push(m)
      }
      crumbMeshes.forEach((m, i) => {
        const c = world.crumbs[i]
        m.visible = !!c
        if (c) m.position.copy(c.pos)
      })
    }

    const render = () => {
      const W = window.innerWidth
      const strip = clamp(Math.round(view.ground), 0, window.innerHeight)
      // Wipe everything, then draw the sky and scene in the strip above the nav
      renderer.setScissorTest(false)
      renderer.setClearColor(0x000000, 0)
      renderer.clear()
      renderer.setScissorTest(true)
      renderer.setScissor(0, window.innerHeight - strip, W, strip)
      renderer.setClearColor(sky, view.p)
      renderer.clear()
      renderer.render(scene, camera)
    }

    const tick = (now) => {
      // rAF timestamps can trail performance.now() on the first frame;
      // a paused game keeps drawing but stops the clock
      const frameDt = Math.max(0, Math.min(0.05, (now - last) / 1000))
      const dt = world.paused ? 0 : frameDt
      if (fpsEl.classList.contains('show')) {
        fpsFrames++
        fpsTime += Math.max(0, (now - last) / 1000)
        if (fpsTime >= 0.5) {
          fpsEl.textContent = `${Math.round(fpsFrames / fpsTime)} FPS`
          fpsFrames = 0
          fpsTime = 0
        }
      }
      last = now

      if (!devFreeze) world.time = (world.time + dt / DAY_LENGTH) % 1
      sound.setPlaying(world.staged && !world.paused && overlay.dataset.kind !== 'over')
      const fighting = boss && boss.phase === 'fight'
      // A super nut's music outlasts everything: caves, the way back up, boss fights
      const superOn = world.staged && world.squirrel && world.squirrel.superFor > 0
      sound.setTrack(superOn ? 'super' : fighting ? `boss:${boss.kind}:${biome}` : under ? 'cave' : wetness() === 'clear' ? (world.staged ? biome : 'meadow') : wetness())
      const inWoods = view.p > 0.5
      world.spawnIn -= dt
      if (world.spawnIn <= 0) {
        const s = world.squirrel
        const about = world.staged && s ? world.acorns.filter((a) => flat(a.pos.clone().sub(s.pos)) < 600).length : world.acorns.length
        if (about < (inWoods ? MAX_TREE_ACORNS : MAX_ACORNS)) dropAcorn()
        // More nuts in autumn, few in winter
        const plenty = SEASONS.reduce((sum, name) => sum + SEASON_NUTS[name] * seasonWeight(name), 0)
        // and quicker still while there are hardly any nuts about
        world.spawnIn = inWoods ? (about < 6 ? rand(0.2, 0.45) : rand(0.5, 1.3)) / plenty : rand(2.5, 6)
      }

      measure()
      if (world.staged) updateChunks(dt)
      const cycle = dayCycle(dt)
      fpBlend = clamp(fpBlend + (firstPerson && world.staged ? 1 : -1) * frameDt * 3, 0, 1)
      // The camera keeps settling while paused, so it's centred on the squirrel
      // rather than left trailing behind wherever it was running
      updateCamera(frameDt)
      updateAcorns(dt)
      updateCrumbs(dt)
      updateSquirrel(dt)
      updateSpiders(dt, 1 - cycle.day)
      updateHearts(dt, clock * 1000)
      strikeScenery()
      updateDebris(dt)
      updateDust(dt)
      updateUnder(dt)
      updateCritters(dt, cycle.day)
      if (world.staged && world.squirrel) {
        const here = biomeAt(world.squirrel.pos.x + origin.x, world.squirrel.pos.z + origin.z)
        biome = here
      }
      updateWeather(dt)
      updateBlizzard(dt)
      updateRain(dt)
      updateLightning(dt)
      updateShots(dt)
      if (world.squirrel) {
        updatePowers(dt)
        updateStrikes(dt)
        updatePuddles(dt)
        updateBoss(dt)
        updateTraps(dt)
        updateWind(dt)
        updateSeasons(dt)
        updateSleepZs()
        const sq = world.squirrel
        if (sq.streak && clock - sq.lastEat > COMBO_TIME) {
          sq.streak = 0
          showCombo()
        }
        comboEl.classList.toggle('ending', !!sq.streak && clock - sq.lastEat > COMBO_TIME - 1)
      }
      sound.setRain(world.staged && view.p > 0.5 && !world.paused && !under ? Math.max(atmo.rain, blizzard * 1.3) : 0)
      updateBubbles(dt)
      sync(cycle, frameDt)
      render()
      frame = requestAnimationFrame(tick)
    }

    // ----- Input -----
    const track = (e) => {
      // Captured, in first person: the mouse looks around
      if (document.pointerLockElement && firstPerson && world.staged && !world.paused) {
        lookYaw -= (e.movementX || 0) * 0.0025
        lookPitch = clamp(lookPitch - (e.movementY || 0) * 0.0025, -0.7, 0.55)
        return
      }
      mouse.x = e.clientX
      mouse.y = e.clientY
      updateHover()
    }
    const forget = () => {
      mouse.x = -9999
      mouse.y = -9999
      updateHover()
    }
    // Click the strip to enter nut mode; once in, clicks attack
    const onDown = (e) => {
      if (e.button !== 0) return
      const touch = e.pointerType === 'touch'
      if (!touch) track(e)
      const on = (selector) => e.target.closest && e.target.closest(selector)
      // On touch screens a tap anywhere puts the instructions away
      if (touch && world.staged && prompt.classList.contains('show') && !prompt.classList.contains('centered')) showPrompt(false)
      if (on('.colour-toggle') || on('.nut-exit') || on('.nut-sound') || on('.nut-overlay__levels') || on('.nut-touch') || on('.nut-settings')) return
      if (!world.staged) {
        if (e.clientY < view.ground) setStaged(true)
      } else if (overlay.dataset.kind === 'over') {
        restart()
      } else if (world.paused) {
        // Click anywhere to carry on
        setPaused(false)
      } else if (view.p > 0.5 && !world.paused && !touch) {
        attack()
      }
    }
    const exit = () => setStaged(false)
    // Browsers only let audio start (or restart, after the page was left)
    // inside a tap or key press
    const wakeSound = () => sound.wake()
    // Quick taps on the attack button, or two thumbs down at once, mustn't
    // zoom the page while playing: Safari doesn't always heed touch-action
    let lastTouchEnd = 0
    // Taps while playing never start a text selection (Safari doesn't
    // always heed user-select)
    const blockSelect = (e) => {
      if (world.staged && !(e.target && e.target.tagName === 'INPUT')) e.preventDefault()
    }
    const blockZoom = (e) => {
      if (world.staged && e.cancelable) e.preventDefault()
    }
    const onTouchEnd = (e) => {
      const now = performance.now()
      // (Buttons answering to clicks are left alone so they still get them)
      const clicks = e.target.closest && e.target.closest('button:not(.nut-touch__btn), a')
      if (world.staged && !clicks && now - lastTouchEnd < 350 && e.cancelable) e.preventDefault()
      lastTouchEnd = now
    }
    const showSound = () => {
      soundButton.classList.toggle('on', sound.on)
      soundButton.setAttribute('aria-pressed', sound.on)
    }
    showSound()
    const toggleSound = () => {
      store(SOUND_KEY, sound.toggle() ? 'on' : 'off')
      showSound()
    }
    const onContextMenu = (e) => {
      if (world.staged) e.preventDefault()
    }

    // Floating +1 / -1 by the squirrel's head that drifts up and fades
    function popup(text, kind) {
      if (!world.staged || view.p < 0.5) return
      const s = world.squirrel
      const at = new Vector3(s.pos.x, s.pos.y + SQ_H + 6, s.pos.z).project(camera)
      const el = document.createElement('span')
      el.className = `nut-pop nut-pop--${kind}`
      el.textContent = text
      // With an icon of what changed: a heart for lives, an acorn for nuts
      const icon = document.createElementNS(SVG_NS, 'svg')
      icon.setAttribute('class', 'nut-pop__icon')
      icon.setAttribute('shape-rendering', 'crispEdges')
      const shapes = kind === 'life' ? [[HEART, 1]] : [[ACORN_CAP, 0.6], [ACORN_NUT, 1]]
      icon.setAttribute('viewBox', kind === 'life' ? '0 0 7 6' : '0 0 7 7')
      shapes.forEach(([d, opacity]) => {
        const path = document.createElementNS(SVG_NS, 'path')
        path.setAttribute('d', d)
        path.setAttribute('opacity', opacity)
        icon.appendChild(path)
      })
      if (kind !== 'power') el.appendChild(icon)
      el.style.left = `${((at.x + 1) / 2) * window.innerWidth + rand(-12, 12)}px`
      el.style.top = `${((1 - at.y) / 2) * window.innerHeight}px`
      el.addEventListener('animationend', () => el.remove())
      popups.appendChild(el)
    }

    // ----- Pause, reset and game over -----
    function showOverlay(kind) {
      overlay.dataset.kind = kind || ''
      overlay.classList.toggle('show', !!kind)
      if (kind === 'over') releasePointer()
      showLevel()
      if (kind === 'over') {
        overlay.querySelector('.nut-overlay__title').textContent = 'Game over'
        overlay.querySelector('.nut-overlay__text').textContent =
          `Nuts ${world.score} / Best ${best}. ${touchOnly ? 'Tap' : 'Click'} to restart`
      } else if (kind === 'paused') {
        overlay.querySelector('.nut-overlay__title').textContent = 'Paused'
        overlay.querySelector('.nut-overlay__text').textContent = touchOnly ? 'Tap to resume' : 'Press Space or P, or click, to resume'
      }
    }
    // Paused with the pause card, or (info) with the controls card instead
    const setPaused = (paused, info = false) => {
      world.paused = paused
      keys.clear()
      if (paused) releasePointer()
      else lockPointer()
      const panel = paused && typeof info === 'string' ? info : null
      settingsPanel.classList.toggle('show', panel === 'settings')
      storePanel.classList.toggle('show', panel === 'store')
      bagPanel.classList.toggle('show', panel === 'bag')
      devPanel.classList.toggle('show', panel === 'dev')
      if (panel !== 'store') storePop.classList.remove('show')
      if (panel) {
        showOverlay(null)
        prompt.classList.remove('show', 'centered')
      } else if (paused && info) {
        showOverlay(null)
        clearTimeout(promptTimer)
        prompt.classList.add('show', 'centered')
      } else if (paused) {
        prompt.classList.remove('show', 'centered')
        showOverlay('paused')
      } else {
        if (overlay.dataset.kind === 'paused') showOverlay(null)
        if (prompt.classList.contains('centered')) prompt.classList.remove('show', 'centered')
      }
      pauseButton.classList.toggle('paused', paused)
      pauseButton.setAttribute('aria-label', paused ? 'Resume' : 'Pause')
    }
    // Difficulty buttons: top right cycles through them, the card picks one.
    // A new difficulty starts a new run.
    function showLevel() {
      levelButton.textContent = level().name
      levelButton.classList.toggle('insane', world.difficulty === 'insane')
      levelChoices.forEach((b) => b.classList.toggle('on', b.dataset.level === world.difficulty))
    }
    const setDifficulty = (next) => {
      if (!DIFFICULTIES[next]) return
      const changed = next !== world.difficulty
      world.difficulty = next
      store(DIFFICULTY_KEY, next)
      best = readBest(next)
      showLevel()
      showScore()
      if (changed && world.staged) restart()
    }
    const cycleDifficulty = () => {
      setDifficulty(DIFFICULTY_LEVELS[(DIFFICULTY_LEVELS.indexOf(world.difficulty) + 1) % DIFFICULTY_LEVELS.length])
    }
    const chooseDifficulty = (e) => {
      const choice = e.target.closest('button')
      if (!choice) return
      if (choice.dataset.level === world.difficulty) {
        if (world.paused) setPaused(false)
        else if (overlay.dataset.kind === 'over') restart()
      } else {
        setDifficulty(choice.dataset.level)
      }
    }
    showLevel()
    // Graphics: the gear opens the panel (pausing); a preset sets everything,
    // and any one change makes it Custom. Saved for next time.
    const showGraphics = () => {
      settingsPanel.querySelectorAll('button[data-key]').forEach((b) => {
        const { key } = b.dataset
        let current = gfx[key]
        if (key === 'camera') current = firstPerson ? 'first' : 'third'
        if (key === 'fps') current = fpsEl.classList.contains('show') ? 'on' : 'off'
        b.classList.toggle('on', current === b.dataset.level)
      })
      cameraButton.classList.toggle('on', firstPerson)
      cameraButton.setAttribute('aria-pressed', firstPerson)
    }
    // First or third person: C, the eye at the top right, or the panel
    const setCamera = (first) => {
      if (first && !firstPerson) {
        lookYaw = 0
        lookPitch = 0
      }
      firstPerson = first
      store(CAMERA_KEY, first ? 'first' : 'third')
      showGraphics()
    }
    const toggleCamera = () => setCamera(!firstPerson)
    // The frames per second counter: O, or the panel; remembered
    const setFps = (on) => {
      fpsEl.classList.toggle('show', on)
      store(FPS_KEY, on ? 'on' : 'off')
      fpsFrames = 0
      fpsTime = 0
      fpsEl.textContent = '-- FPS'
      showGraphics()
    }
    const applyGraphics = (before) => {
      store(GRAPHICS_KEY, JSON.stringify(gfx))
      showGraphics()
      if (gfx.resolution !== before.resolution) resize()
      // Grass and plants are baked into each chunk: grow them again
      if (gfx.foliage !== before.foliage && world.staged) clearChunks()
    }
    const chooseGraphics = (e) => {
      const choice = e.target.closest('button')
      if (!choice) return
      if (choice.classList.contains('nut-settings__done')) {
        setPaused(false)
        return
      }
      const { key, level } = choice.dataset
      if (!key) return
      if (key === 'camera') {
        setCamera(level === 'first')
        return
      }
      if (key === 'fps') {
        setFps(level === 'on')
        return
      }
      const before = { ...gfx }
      if (key === 'preset') gfx = presetSettings(level)
      else gfx = { ...gfx, [key]: level, preset: 'custom' }
      applyGraphics(before)
    }
    const toggleSettings = () => {
      if (!world.staged || overlay.dataset.kind === 'over') return
      if (world.paused && settingsPanel.classList.contains('show')) setPaused(false)
      else setPaused(true, 'settings')
    }
    showGraphics()

    // ----- The nut store -----
    // Nuts eaten are banked; spend them on skins (kept once bought) and on
    // power-ups to summon whenever (one of each held at once). Every card has
    // a picture of its own. With a mouse, hovering a card shows it turning in
    // 3D beside the store and a click buys (or wears) it; on touch screens a
    // tap opens its own page, with a turntable to spin by hand and a button
    // back to the store at the top left.
    const owns = (skin) => skin.price === 0 || shop.owned.includes(skin.id)
    let picked = `skin:${shop.skin}`
    const ownsChar = (ch) => ch.price === 0 || shop.chars.includes(ch.id)
    const thingOf = (key) => {
      const [kind, id] = key.split(':')
      if (kind === 'char') return { char: CHARACTERS.find((c) => c.id === id) }
      return kind === 'skin' ? { skin: SKINS.find((k) => k.id === id) } : { item: SHOP_ITEMS.find((it) => it.id === id) }
    }
    const keyOf = (b) => (b.dataset.char ? `char:${b.dataset.char}` : b.dataset.skin ? `skin:${b.dataset.skin}` : `item:${b.dataset.item}`)
    const storePop = storePopRef.current
    const popView = storePop.querySelector('.nut-store__view')
    const detailView = storePanel.querySelector('.nut-store__page--detail .nut-store__view')

    // One renderer off the page draws every picture: the cards' stills, and
    // the turning model into the hover card or the item's page
    let preview = null
    const ensurePreview = () => {
      if (preview) return preview
      let renderer
      try {
        renderer = new WebGLRenderer({ canvas: document.createElement('canvas'), alpha: true, antialias: true, preserveDrawingBuffer: true })
      } catch (e) {
        return null // No WebGL to spare: the store works without pictures
      }
      renderer.setPixelRatio(1)
      const stage3d = new Scene()
      stage3d.add(new HemisphereLight(0xffffff, 0x445533, 1.1))
      const key = new DirectionalLight(0xffffff, 0.8)
      key.position.set(40, 80, 70)
      stage3d.add(key)
      const cam = new PerspectiveCamera(30, 4 / 3, 1, 1000)
      cam.position.set(0, 10, 74)
      cam.lookAt(0, 0, 0)
      const stand = new Group()
      stage3d.add(stand)
      preview = { renderer, scene: stage3d, cam, stand, yaw: 0.5, tilt: 0.2, drag: null, model: null, modelKey: null, raf: 0, last: 0, w: 0, h: 0 }
      return preview
    }
    // The model on the turntable: the chosen character in a skin, a
    // character in its own colours, or the power-up
    const buildModel = (key) => {
      const [kind, id] = key.split(':')
      if (kind === 'skin' || kind === 'char') {
        const rig = kind === 'skin' ? charModel(shop.char, SKINS.find((k) => k.id === id) || SKINS[0]) : charModel(id, SKINS[0])
        rig.group.position.y = -10
        return rig
      }
      const group = new Group()
      const nut = new Mesh(acornGeo, id === 'golden' ? powerMats.magnet : superMats)
      nut.scale.setScalar(3.2)
      group.add(nut)
      return { group, mats: [], legs: [] }
    }
    const setModel = (key) => {
      if (!preview || preview.modelKey === key) return
      if (preview.model) {
        preview.stand.remove(preview.model.group)
        preview.model.mats.forEach((m) => m.dispose())
        preview.model = null
      }
      preview.modelKey = key
      if (!key) return
      preview.model = buildModel(key)
      preview.stand.add(preview.model.group)
    }
    // Render the stand and copy it into a canvas on the page
    const paint = (target) => {
      const ratio = Math.min(2, window.devicePixelRatio || 1)
      const w = Math.max(1, Math.round((target.clientWidth || 160) * ratio))
      const h = Math.max(1, Math.round((target.clientHeight || 120) * ratio))
      if (target.width !== w) target.width = w
      if (target.height !== h) target.height = h
      const { renderer, cam } = preview
      if (preview.w !== w || preview.h !== h) {
        renderer.setSize(w, h, false)
        cam.aspect = w / h
        cam.updateProjectionMatrix()
        preview.w = w
        preview.h = h
      }
      renderer.render(preview.scene, cam)
      const ctx = target.getContext('2d')
      ctx.clearRect(0, 0, w, h)
      ctx.drawImage(renderer.domElement, 0, 0, w, h)
    }
    // Each card's still, drawn the first time its panel opens
    const drawThumbs = () => {
      if (!ensurePreview()) return
      const turning = preview.modelKey
      setModel(null)
      preview.stand.rotation.set(0.22, 0.55, 0)
      document.querySelectorAll('canvas[data-thumb]').forEach((thumb) => {
        if (thumb.dataset.drawn) return
        setModel(thumb.dataset.thumb)
        if (preview.model.colours) styleRig(preview.model, null)
        paint(thumb)
        thumb.dataset.drawn = '1'
      })
      setModel(turning)
    }
    const previewTick = (now) => {
      if (!preview || !storePanel.classList.contains('show')) {
        storePop.classList.remove('show')
        if (preview) preview.raf = 0
        return
      }
      const dt = Math.min(0.05, Math.max(0, (now - (preview.last || now)) / 1000))
      preview.last = now
      const target = storePanel.dataset.page === 'detail' ? detailView : storePop.classList.contains('show') ? popView : null
      if (target) {
        setModel(picked)
        if (!preview.drag) preview.yaw += dt * 0.8
        preview.stand.rotation.set(preview.tilt, preview.yaw, 0)
        const model = preview.model
        if (model && model.colours) {
          styleRig(model, null)
          stepRig(model, 1, now / 90)
        }
        paint(target)
      }
      preview.raf = requestAnimationFrame(previewTick)
    }
    // On the item's page the turntable can be spun by hand; let go and it
    // carries on by itself
    const onPreviewDown = (e) => {
      if (!preview) return
      e.preventDefault()
      detailView.setPointerCapture(e.pointerId)
      preview.drag = { id: e.pointerId, x: e.clientX, y: e.clientY, yaw: preview.yaw, tilt: preview.tilt }
    }
    const onPreviewMove = (e) => {
      const drag = preview && preview.drag
      if (!drag || drag.id !== e.pointerId) return
      preview.yaw = drag.yaw + (e.clientX - drag.x) * 0.012
      preview.tilt = clamp(drag.tilt + (e.clientY - drag.y) * 0.008, -0.6, 1)
    }
    const onPreviewUp = (e) => {
      if (preview && preview.drag && preview.drag.id === e.pointerId) preview.drag = null
    }

    // What the hover card and the item's page say about the picked one
    const showPick = () => {
      const { skin, item, char } = thingOf(picked)
      const thing = skin || item || char
      let status = `Buy for ${thing.price.toLocaleString()} nuts`
      let can = shop.bank >= thing.price
      if (char && shop.char === char.id) {
        status = 'Playing as'
        can = false
      } else if (char && ownsChar(char)) {
        status = 'Play as'
        can = true
      } else if (skin && shop.skin === skin.id) {
        status = 'Wearing'
        can = false
      } else if (skin && owns(skin)) {
        status = 'Wear'
        can = true
      } else if (item && shop.items[item.id]) {
        status = 'In your bag'
        can = false
      } else if (!can) status = `${(thing.price - shop.bank).toLocaleString()} more nuts needed`
      ;[storePop, storePanel.querySelector('.nut-store__page--detail')].forEach((el) => {
        el.querySelector('.nut-store__title').textContent = thing.name
        el.querySelector('.nut-store__desc').textContent = SHOP_ABOUT[thing.id]
      })
      storePop.querySelector('.nut-store__status').textContent = can ? `Click to ${status.toLowerCase()}` : status
      const act = storePanel.querySelector('.nut-store__act')
      act.textContent = status
      act.classList.toggle('cant', !can)
    }
    const showShop = () => {
      storePanel.querySelector('.nut-store__bank').textContent = `${shop.bank} ${shop.bank === 1 ? 'nut' : 'nuts'} to spend`
      storePanel.querySelectorAll('[data-skin]').forEach((b) => {
        const skin = SKINS.find((k) => k.id === b.dataset.skin)
        b.classList.toggle('on', skin.id === shop.skin)
        b.classList.toggle('cant', !owns(skin) && shop.bank < skin.price)
        b.querySelector('.nut-store__price').textContent = skin.id === shop.skin ? 'Wearing' : owns(skin) ? 'Owned' : `${skin.price.toLocaleString()} nuts`
      })
      storePanel.querySelectorAll('[data-char]').forEach((b) => {
        const ch = CHARACTERS.find((c) => c.id === b.dataset.char)
        b.classList.toggle('on', ch.id === shop.char)
        b.classList.toggle('cant', !ownsChar(ch) && shop.bank < ch.price)
        b.querySelector('.nut-store__price').textContent = ch.id === shop.char ? 'Playing as' : ownsChar(ch) ? 'Owned' : `${ch.price.toLocaleString()} nuts`
      })
      storePanel.querySelectorAll('[data-tab]').forEach((b) => b.classList.toggle('on', b.dataset.tab === storePanel.dataset.tab))
      storePanel.querySelectorAll('[data-item]').forEach((b) => {
        const item = SHOP_ITEMS.find((it) => it.id === b.dataset.item)
        const held = shop.items[item.id]
        b.classList.toggle('on', !!held)
        b.classList.toggle('cant', !held && shop.bank < item.price)
        b.querySelector('.nut-store__price').textContent = held ? 'In your bag' : `${item.price} nuts`
      })
      bagPanel.querySelectorAll('[data-use]').forEach((b) => {
        const id = b.dataset.use
        const held = shop.items[id]
        const blocked = id === 'super' && fightOn
        b.classList.toggle('cant', !held || blocked)
        b.querySelector('.nut-store__price').textContent = !held ? 'Buy in store' : blocked ? 'Not in boss fights' : 'Use'
      })
      bagButton.classList.toggle('has', SHOP_ITEMS.some((it) => shop.items[it.id]))
      showPick()
    }
    showShop()
    const runPreview = () => {
      if (!preview || preview.raf) return
      preview.last = 0
      preview.raf = requestAnimationFrame(previewTick)
    }
    const openStore = () => {
      storePanel.dataset.page = 'list'
      showShop()
      setPaused(true, 'store')
      drawThumbs()
      runPreview()
    }
    // Buy (or wear) the picked one
    const buyPicked = () => {
      const { skin, item, char } = thingOf(picked)
      if (char) {
        if (!ownsChar(char)) {
          if (shop.bank < char.price) {
            sound.play('clank')
            return
          }
          shop.bank -= char.price
          shop.chars.push(char.id)
          sound.play('power')
        } else if (shop.char === char.id) return
        shop.char = char.id
        applyCharacter()
        // The skins' pictures are of the character being played
        document.querySelectorAll('canvas[data-thumb^="skin:"]').forEach((thumb) => {
          delete thumb.dataset.drawn
        })
        drawThumbs()
      } else if (skin) {
        if (!owns(skin)) {
          if (shop.bank < skin.price) {
            sound.play('clank')
            return
          }
          shop.bank -= skin.price
          shop.owned.push(skin.id)
          sound.play('power')
        } else if (shop.skin === skin.id) return
        shop.skin = skin.id
        applyLook()
      } else {
        if (shop.items[item.id] || shop.bank < item.price) {
          sound.play('clank')
          return
        }
        shop.bank -= item.price
        shop.items[item.id] = 1
        sound.play('power')
      }
      saveShop()
      showShop()
    }
    // The hover card, beside the store, level with the card under the mouse
    let hoverCard = null
    const placePop = (card) => {
      const at = card.getBoundingClientRect()
      const box = storePanel.getBoundingClientRect()
      const w = storePop.offsetWidth
      const h = storePop.offsetHeight
      let left = box.right + 12
      if (left + w > window.innerWidth - 8) left = box.left - w - 12
      if (left < 8) left = clamp(at.right + 8, 8, window.innerWidth - w - 8)
      storePop.style.left = `${left}px`
      storePop.style.top = `${clamp(at.top + at.height / 2 - h / 2, 8, window.innerHeight - h - 8)}px`
    }
    const onCardOver = (e) => {
      if (e.pointerType !== 'mouse') return
      const card = e.target.closest('[data-skin], [data-item], [data-char]')
      if (!card || card === hoverCard) return
      hoverCard = card
      picked = keyOf(card)
      if (preview) {
        preview.yaw = 0.5
        preview.tilt = 0.2
      }
      showPick()
      storePop.classList.add('show')
      placePop(card)
      runPreview()
    }
    const onCardOut = (e) => {
      const card = e.target.closest('[data-skin], [data-item], [data-char]')
      if (!card || card.contains(e.relatedTarget)) return
      hoverCard = null
      storePop.classList.remove('show')
    }
    const chooseStore = (e) => {
      const b = e.target.closest('button')
      if (!b) return
      if (b.classList.contains('nut-settings__done')) setPaused(false)
      else if (b.classList.contains('nut-store__back')) {
        storePanel.dataset.page = 'list'
        if (preview) preview.drag = null
      } else if (b.classList.contains('nut-store__act')) buyPicked()
      else if (b.dataset.tab) {
        storePanel.dataset.tab = b.dataset.tab
        storePop.classList.remove('show')
        hoverCard = null
        showShop()
      } else if (b.dataset.skin || b.dataset.item || b.dataset.char) {
        picked = keyOf(b)
        if (touchOnly) {
          // Its own page, with the turntable
          storePanel.dataset.page = 'detail'
          storePanel.scrollTop = 0
          if (preview) {
            preview.yaw = 0.5
            preview.tilt = 0.2
          }
          showPick()
          runPreview()
        } else buyPicked()
      }
    }
    // Summon a power-up from the bag: a golden nut any time, a super nut
    // any time but a boss fight
    const summonItem = (id) => {
      const s = world.squirrel
      if (!world.staged || !s || s.deadFor > 0 || overlay.dataset.kind === 'over' || view.p < 0.5) return
      if (!shop.items[id]) {
        showToast('None in your bag')
        return
      }
      if (id === 'super' && fightOn) {
        showToast('No super nuts in a boss fight')
        sound.play('clank')
        return
      }
      shop.items[id] = 0
      saveShop()
      showShop()
      if (id === 'golden') {
        if (!s.powers) s.powers = { magnet: 0, pepper: 0, feather: 0, shield: 0 }
        s.powers.magnet = POWERS.magnet.time
        popup(POWERS.magnet.name, 'power')
        sound.play('power')
      } else {
        s.superFor = SUPER_TIME
        s.poisonFor = 0
        showToast('Super nut!')
        sound.play('super')
      }
    }
    const chooseBag = (e) => {
      const b = e.target.closest('button')
      if (!b) return
      if (b.classList.contains('nut-settings__done')) setPaused(false)
      else if (b.classList.contains('nut-store__open')) openStore()
      else if (b.dataset.use) {
        const id = b.dataset.use
        if (!shop.items[id]) {
          picked = `item:${id}`
          storePanel.dataset.tab = 'power'
          openStore()
          if (touchOnly) {
            storePanel.dataset.page = 'detail'
            showPick()
          }
        } else if (id === 'super' && fightOn) sound.play('clank')
        else {
          setPaused(false)
          summonItem(id)
        }
      }
    }
    const togglePanel = (name) => {
      if (!world.staged || overlay.dataset.kind === 'over') return
      const el = { store: storePanel, bag: bagPanel, dev: devPanel }[name]
      if (world.paused && el.classList.contains('show')) setPaused(false)
      else if (name === 'store') openStore()
      else {
        showShop()
        if (name === 'dev') showDev()
        setPaused(true, name)
        if (name === 'bag') drawThumbs()
      }
    }
    const toggleStore = () => togglePanel('store')
    const toggleBag = () => togglePanel('bag')
    const toggleDev = () => togglePanel('dev')
    const panelOpen = () => [settingsPanel, storePanel, bagPanel, devPanel].some((el) => el.classList.contains('show'))

    // ----- Dev menu -----
    // Typing !dev shows (or hides) a wrench at the top right; it opens a
    // panel for testing: biomes, weather, seasons, the time of day, nuts and
    // a few cheats
    let typed = ''
    const showDevButton = () => {
      devButton.classList.toggle('show', devOn && world.staged)
      document.documentElement.classList.toggle('nut-dev-on', devOn)
    }
    const clockText = () => {
      const minutes = Math.round(((6 + world.time * 24) % 24) * 60) % 1440
      return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
    }
    const showDev = () => {
      devPanel.querySelectorAll('button[data-dev]').forEach((b) => {
        const { dev, value } = b.dataset
        let on = false
        if (dev === 'biome') on = biome === value
        else if (dev === 'weather') on = value === 'blizzard' ? blizzardOn : weather === value && !blizzardOn
        else if (dev === 'season') on = seasonState().now === value
        else if (dev === 'god') on = devGod
        else if (dev === 'freeze') on = devFreeze
        b.classList.toggle('on', on)
      })
      devPanel.querySelector('input[data-dev="time"]').value = world.time
      devPanel.querySelector('.nut-dev__clock').textContent = clockText()
      devPanel.querySelector('input[data-dev="bank"]').value = shop.bank
      devPanel.querySelector('input[data-dev="score"]').value = world.score
    }
    // Straight into a biome: new woods grow round the squirrel, in it
    const devBiome = (kind) => {
      const s = world.squirrel
      clearBoss()
      clearChunks()
      startRun(kind)
      clearHearts()
      clearPickups()
      updateChunks(0)
      s.caveFor = 0
      s.climb = null
      s.vel.set(0, 0, 0)
      s.pos.y = floorAt(s.pos.x, s.pos.z)
    }
    const numberIn = (key) => Math.max(0, Math.floor(Number(devPanel.querySelector(`input[data-dev="${key}"]`).value)) || 0)
    const chooseDev = (e) => {
      const b = e.target.closest('button')
      if (!b) return
      const s = world.squirrel
      const { dev, value } = b.dataset
      if (b.classList.contains('nut-settings__done')) {
        setPaused(false)
        return
      }
      if (dev === 'biome') devBiome(value)
      else if (dev === 'weather') {
        blizzardOn = value === 'blizzard'
        blizzardFor = 60
        if (value !== 'blizzard') {
          weather = value
          weatherFor = 600
        }
      } else if (dev === 'season') {
        seasonClock = SEASONS.indexOf(value) * SEASON_LENGTH + 1
        leafTimer = 0
      } else if (dev === 'freeze') devFreeze = !devFreeze
      else if (dev === 'god') devGod = !devGod
      else if (dev === 'setBank' || dev === 'addBank') {
        shop.bank = dev === 'addBank' ? shop.bank + 100 : numberIn('bank')
        saveShop()
        showShop()
      } else if (dev === 'setScore') {
        world.score = numberIn('score')
        showScore()
      } else if (dev === 'hearts') {
        world.lives = maxLives()
        showScore()
      } else if (dev === 'unlock') {
        shop.owned = SKINS.map((k) => k.id)
        shop.chars = CHARACTERS.map((c) => c.id)
        saveShop()
        showShop()
      } else if (dev === 'resetStore') {
        Object.assign(shop, { bank: 0, owned: [], skin: 'classic', char: 'squirrel', chars: [], items: { golden: 0, super: 0 } })
        applyLook()
        document.querySelectorAll('canvas[data-thumb^="skin:"]').forEach((thumb) => {
          delete thumb.dataset.drawn
        })
        saveShop()
        showShop()
      } else if (dev === 'boss' || dev === 'power' || dev === 'super') {
        // These happen in the woods, so it carries on playing
        setPaused(false)
        if (dev === 'boss') summonBoss()
        else if (dev === 'super') {
          s.superFor = SUPER_TIME
          s.poisonFor = 0
          sound.play('super')
        } else {
          if (!s.powers) s.powers = { magnet: 0, pepper: 0, feather: 0, shield: 0 }
          s.powers[value] = POWERS[value].time
          popup(POWERS[value].name, 'power')
          sound.play('power')
        }
        return
      }
      showDev()
    }
    const slideDev = (e) => {
      if (e.target.dataset.dev !== 'time') return
      world.time = Number(e.target.value)
      devPanel.querySelector('.nut-dev__clock').textContent = clockText()
    }

    // A boss, right here and now: / then B
    let slashAt = -9999
    const summonBoss = () => {
      const s = world.squirrel
      if (!world.staged || under || !s || s.deadFor > 0 || overlay.dataset.kind === 'over' || view.p < 0.5) return
      if (boss) clearBoss()
      spawnBoss()
      const f = facing(s)
      boss.sp.pos.set(s.pos.x + f.x * 110, 0, s.pos.z + f.z * 110)
      boss.sp.pos.y = floorAt(boss.sp.pos.x, boss.sp.pos.z)
      s.introFor = 0
      s.climb = null
      startFight()
    }
    const toggleInfo = () => {
      if (!world.staged || overlay.dataset.kind === 'over') return
      if (world.paused && prompt.classList.contains('centered')) setPaused(false)
      else setPaused(true, true)
    }
    // Fullscreen: L or the button at the top right
    const toggleFullscreen = () => {
      const el = document.documentElement
      if (!el.requestFullscreen) return
      switchedFullAt = Date.now()
      const request = document.fullscreenElement ? document.exitFullscreen() : el.requestFullscreen()
      if (request && request.catch) request.catch(() => {})
    }
    const onFullscreen = () => {
      const full = !!document.fullscreenElement
      fullButton.classList.toggle('on', full)
      fullButton.setAttribute('aria-label', full ? 'Exit fullscreen' : 'Fullscreen')
      if (Date.now() - switchedFullAt < 1500 && world.staged && !world.paused) lockPointer()
    }
    const togglePause = () => {
      if (world.staged && overlay.dataset.kind !== 'over') setPaused(!world.paused)
    }
    // A fresh run in new woods
    const restart = () => {
      const s = world.squirrel
      setPaused(false)
      showOverlay(null)
      toast.classList.remove('show')
      s.gameOver = true
      s.superFor = 0
      respawn(s)
      s.climb = null
      s.pos.y = floorAt(s.pos.x, s.pos.z)
    }

    // Space or the touch jump button: off the ground, off a trunk, or with a
    // feather, a second time in mid-air
    const jump = (repeat = false) => {
      const s = world.squirrel
      if (s.climb) letGo(s)
      else if (s.grounded && s.deadFor <= 0) {
        s.vel.y = JUMP_SPEED
        sound.play('jump')
      } else if (!repeat && s.deadFor <= 0 && s.powers && s.powers.feather > 0 && !s.doubled) {
        // The feather's double jump
        s.doubled = true
        s.vel.y = JUMP_SPEED * 0.95
        sound.play('jump')
      }
      s.introFor = 0
    }

    // ----- Touch controls -----
    // A joystick on the left, jump and pounce buttons on the right
    const STICK_REACH = 46 // px the knob travels
    let stickTouch = null
    const moveStick = (e) => {
      const box = stickEl.getBoundingClientRect()
      let dx = e.clientX - (box.left + box.width / 2)
      let dy = e.clientY - (box.top + box.height / 2)
      const d = Math.hypot(dx, dy)
      if (d > STICK_REACH) {
        dx = (dx / d) * STICK_REACH
        dy = (dy / d) * STICK_REACH
      }
      stick.x = dx / STICK_REACH
      stick.y = dy / STICK_REACH
      knobEl.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`
    }
    const onStickDown = (e) => {
      e.preventDefault()
      stickTouch = e.pointerId
      stickEl.setPointerCapture(e.pointerId)
      stickEl.classList.add('held')
      moveStick(e)
      world.squirrel.introFor = 0
    }
    const onStickMove = (e) => {
      if (e.pointerId === stickTouch) moveStick(e)
    }
    const onStickUp = (e) => {
      if (e.pointerId !== stickTouch) return
      stickTouch = null
      stick.x = 0
      stick.y = 0
      stickEl.classList.remove('held')
      knobEl.style.transform = ''
    }
    const onJumpDown = (e) => {
      e.preventDefault()
      jumpEl.setPointerCapture(e.pointerId)
      spaceHeld = true // held to glide
      if (world.staged && view.p > 0.5 && !world.paused) jump()
    }
    const onJumpUp = () => {
      spaceHeld = false
    }
    const onAttackDown = (e) => {
      e.preventDefault()
      if (world.staged && view.p > 0.5 && !world.paused && overlay.dataset.kind !== 'over') attack()
    }

    const typing = (e) => /^(input|textarea|select)$/i.test(e.target.tagName) || e.target.isContentEditable
    const onKeyDown = (e) => {
      if (typing(e) || e.metaKey || e.ctrlKey || e.altKey) return
      const k = e.key.toLowerCase()
      // !dev shows or hides the dev menu's wrench
      typed = (typed + (e.key.length === 1 ? k : '')).slice(-4)
      if (typed === '!dev') {
        typed = ''
        devOn = !devOn
        store(DEV_KEY, devOn ? 'on' : 'off')
        showDevButton()
        if (world.staged) showToast(devOn ? 'Dev menu on' : 'Dev menu off')
        return
      }
      if (k === 'shift') sprinting = true
      if (k === 'q' || k === 'e') turning.add(k)
      if (k === ' ') spaceHeld = true
      // Space carries on from the pause card (or the controls card)
      if (k === ' ' && world.staged && world.paused && overlay.dataset.kind !== 'over' && !panelOpen()) {
        e.preventDefault()
        if (document.activeElement && document.activeElement.blur) document.activeElement.blur()
        setPaused(false)
        return
      }
      if (k === '/' && world.staged) {
        e.preventDefault()
        slashAt = performance.now()
        return
      }
      if (k === 'b' && performance.now() - slashAt < 1500) {
        slashAt = -9999
        if (!world.paused) summonBoss()
        return
      }
      if (world.staged && !world.paused && !e.repeat && (k === '1' || k === '2')) {
        summonItem(k === '1' ? 'golden' : 'super')
        return
      }
      if (k === 'n') {
        // Toggle nut mode from anywhere
        setStaged(!world.staged)
      } else if (k === 'escape' && world.staged) {
        setStaged(false)
      } else if (k === 'p') {
        togglePause()
      } else if (k === 'r' && world.staged) {
        restart()
      } else if (k === 'm') {
        toggleSound()
      } else if (k === 'l' && world.staged) {
        toggleFullscreen()
      } else if (k === 'g') {
        toggleSettings()
      } else if (k === 'c' && world.staged) {
        setCamera(!firstPerson)
      } else if (k === 'o') {
        setFps(!fpsEl.classList.contains('show'))
      } else if (world.staged && !world.paused && KEYS[k]) {
        // Moving straight away skips the squirrel's own little walk
        e.preventDefault()
        keys.add(k)
        world.squirrel.introFor = 0
      } else if (world.staged && view.p > 0.5 && !world.paused) {
        if (k === 'f' && !e.repeat && overlay.dataset.kind !== 'over') {
          attack()
        } else if (k === ' ') {
          e.preventDefault()
          jump(e.repeat)
        }
      }
    }
    const onKeyUp = (e) => {
      const k = e.key.toLowerCase()
      keys.delete(k)
      turning.delete(k)
      if (k === 'shift') sprinting = false
      if (k === ' ') spaceHeld = false
    }
    const onBlur = () => {
      keys.clear()
      turning.clear()
      sprinting = false
      spaceHeld = false
      // Leaving the window or tab pauses the game
      if (world.staged && !world.paused && overlay.dataset.kind !== 'over') setPaused(true)
    }
    const onVisibility = () => {
      if (!document.hidden) return
      onBlur()
      sound.sleep()
    }

    // Re-read theme colours whenever the toggles update the root variables
    const observer = new MutationObserver(readColours)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] })

    window.addEventListener('resize', resize)
    window.addEventListener('pointermove', track)
    window.addEventListener('pointerdown', onDown)
    window.addEventListener('touchend', wakeSound)
    window.addEventListener('click', wakeSound)
    document.addEventListener('touchend', onTouchEnd, { passive: false })
    document.addEventListener('dblclick', blockZoom, { passive: false })
    document.addEventListener('selectstart', blockSelect)
    document.addEventListener('gesturestart', blockZoom, { passive: false })
    exitBar.addEventListener('click', exit)
    soundButton.addEventListener('click', toggleSound)
    pauseButton.addEventListener('click', togglePause)
    levelButton.addEventListener('click', cycleDifficulty)
    infoButton.addEventListener('click', toggleInfo)
    fullButton.addEventListener('click', toggleFullscreen)
    gearButton.addEventListener('click', toggleSettings)
    cameraButton.addEventListener('click', toggleCamera)
    settingsPanel.addEventListener('click', chooseGraphics)
    shopButton.addEventListener('click', toggleStore)
    bagButton.addEventListener('click', toggleBag)
    storePanel.addEventListener('click', chooseStore)
    bagPanel.addEventListener('click', chooseBag)
    devButton.addEventListener('click', toggleDev)
    devPanel.addEventListener('click', chooseDev)
    devPanel.addEventListener('input', slideDev)
    detailView.addEventListener('pointerdown', onPreviewDown)
    detailView.addEventListener('pointermove', onPreviewMove)
    detailView.addEventListener('pointerup', onPreviewUp)
    detailView.addEventListener('pointercancel', onPreviewUp)
    storePanel.addEventListener('pointerover', onCardOver)
    storePanel.addEventListener('pointerout', onCardOut)
    stickEl.addEventListener('pointerdown', onStickDown)
    stickEl.addEventListener('pointermove', onStickMove)
    stickEl.addEventListener('pointerup', onStickUp)
    stickEl.addEventListener('pointercancel', onStickUp)
    jumpEl.addEventListener('pointerdown', onJumpDown)
    jumpEl.addEventListener('pointerup', onJumpUp)
    jumpEl.addEventListener('pointercancel', onJumpUp)
    attackEl.addEventListener('pointerdown', onAttackDown)
    document.addEventListener('fullscreenchange', onFullscreen)
    levelChoices.forEach((b) => b.addEventListener('click', chooseDifficulty))
    window.addEventListener('contextmenu', onContextMenu)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keydown', wakeSound)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    document.addEventListener('visibilitychange', onVisibility)
    document.addEventListener('pointerlockchange', onLockChange)
    document.addEventListener('mouseleave', forget)
    frame = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(promptTimer)
      clearTimeout(toastTimer)
      observer.disconnect()
      if (cursorEl) cursorEl.classList.remove('in-sky')
      window.removeEventListener('resize', resize)
      window.removeEventListener('pointermove', track)
      window.removeEventListener('pointerdown', onDown)
      window.removeEventListener('touchend', wakeSound)
      window.removeEventListener('click', wakeSound)
      document.removeEventListener('touchend', onTouchEnd)
      document.removeEventListener('dblclick', blockZoom)
      document.removeEventListener('selectstart', blockSelect)
      document.removeEventListener('gesturestart', blockZoom)
      exitBar.removeEventListener('click', exit)
      soundButton.removeEventListener('click', toggleSound)
      pauseButton.removeEventListener('click', togglePause)
      levelButton.removeEventListener('click', cycleDifficulty)
      infoButton.removeEventListener('click', toggleInfo)
      fullButton.removeEventListener('click', toggleFullscreen)
      gearButton.removeEventListener('click', toggleSettings)
      cameraButton.removeEventListener('click', toggleCamera)
      settingsPanel.removeEventListener('click', chooseGraphics)
      shopButton.removeEventListener('click', toggleStore)
      bagButton.removeEventListener('click', toggleBag)
      storePanel.removeEventListener('click', chooseStore)
      bagPanel.removeEventListener('click', chooseBag)
      devButton.removeEventListener('click', toggleDev)
      devPanel.removeEventListener('click', chooseDev)
      devPanel.removeEventListener('input', slideDev)
      detailView.removeEventListener('pointerdown', onPreviewDown)
      detailView.removeEventListener('pointermove', onPreviewMove)
      detailView.removeEventListener('pointerup', onPreviewUp)
      detailView.removeEventListener('pointercancel', onPreviewUp)
      storePanel.removeEventListener('pointerover', onCardOver)
      storePanel.removeEventListener('pointerout', onCardOut)
      if (charRig) charRig.mats.forEach((m) => m.dispose())
      if (preview) {
        cancelAnimationFrame(preview.raf)
        if (preview.model) preview.model.mats.forEach((m) => m.dispose())
        preview.renderer.dispose()
      }
      document.documentElement.classList.remove('nut-dev-on')
      hatMats.forEach((m) => m.dispose())
      stickEl.removeEventListener('pointerdown', onStickDown)
      stickEl.removeEventListener('pointermove', onStickMove)
      stickEl.removeEventListener('pointerup', onStickUp)
      stickEl.removeEventListener('pointercancel', onStickUp)
      jumpEl.removeEventListener('pointerdown', onJumpDown)
      jumpEl.removeEventListener('pointerup', onJumpUp)
      jumpEl.removeEventListener('pointercancel', onJumpUp)
      attackEl.removeEventListener('pointerdown', onAttackDown)
      document.documentElement.classList.remove('nut-touch-device')
      document.removeEventListener('fullscreenchange', onFullscreen)
      levelChoices.forEach((b) => b.removeEventListener('click', chooseDifficulty))
      window.removeEventListener('contextmenu', onContextMenu)
      document.documentElement.classList.remove('nut-mode')
      sound.dispose()
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keydown', wakeSound)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
      document.removeEventListener('visibilitychange', onVisibility)
      document.removeEventListener('pointerlockchange', onLockChange)
      releasePointer()
      document.documentElement.classList.remove('nut-locked')
      document.removeEventListener('mouseleave', forget)
      clearChunks()
      clearHearts()
      heartMat.dispose()
      ;[...scorpionMats, ...iguanaMats, waterMat, sandMat].forEach((m) => m.dispose())
      ;[...batMats, ...wolfMats, ...frogMats, ...snakeMats, ...crocMats, ...decorMats, rainMat, boltMat, glowMat, sparkMat].forEach((m) => m.dispose())
      Object.values(shotMats).forEach((m) => m.dispose())
      orbMat.dispose()
      orbBackMat.dispose()
      ;[terrainMat, bankMat, fallMat, dropMat, markMat, spikeMat, rockMat, geyserMat, zMat, warnMat, warnFill.material, puddleMat, wallMat, thornMat, quakeMat, dazeMat, angryMat, bubble.material, ...starMeshes.map((m) => m.material)].forEach((m) => m.dispose())
      Object.values(powerMats).forEach((mats) => mats.forEach((m) => m.dispose()))
      Object.values(nutMats).forEach((mats) => mats && mats.forEach((m) => m.dispose()))
      Object.values(nutGeos).forEach((g) => g && g.dispose())
      ;[beeMat, dragonflyMat, flyerMat, wingMat, leafMat, mistMat, splashMat].forEach((m) => m.dispose())
      ;[bubbleMat, fireflyMat, beetleMat, butterflyMat, craterMat, sunMesh.material, sunGlow.material, moonMesh.material].forEach(
        (m) => m.dispose(),
      )
      geometries.forEach((g) => g.dispose())
      allMats.forEach((m) => m.dispose())
      renderer.dispose()
    }
  }, [])

  return (
    <>
      <canvas ref={canvasRef} className="squirrel-canvas" aria-hidden="true" />
      <div ref={fadeRef} className="nut-fade" aria-hidden="true"></div>
      <div ref={counterRef} className="nuts-eaten">
        <span className="nuts-eaten__name">Nuts</span>
        <svg className="nuts-eaten__icon" viewBox="0 0 7 7" shapeRendering="crispEdges" aria-label="Nuts">
          <path d={ACORN_CAP} />
          <path d={ACORN_NUT} opacity="0.7" />
        </svg>
        <span className="nuts-eaten__count">0</span>
        <span className="nuts-eaten__combo"></span>
        <span className="nuts-eaten__label">Best</span>
        <svg className="nuts-eaten__icon nuts-eaten__icon--best" viewBox="0 0 8 7" shapeRendering="crispEdges" aria-label="Best">
          <path d={TROPHY} />
        </svg>
        <span className="nuts-eaten__best">0</span>
        <span className="nuts-eaten__row">
        <span className="nuts-eaten__lives" aria-label="Lives">
          {Array.from({ length: MAX_LIVES }, (_, i) => (
            <svg key={i} className="nuts-eaten__life" viewBox="0 0 7 6" shapeRendering="crispEdges">
              <path d={HEART} />
            </svg>
          ))}
        </span>
        <span className="nuts-eaten__powers"></span>
        </span>
      </div>
      <div ref={bossRef} className="boss-bar" aria-live="polite">
        <p className="boss-bar__name">
          <svg className="boss-bar__skull" viewBox="0 0 8 8" shapeRendering="crispEdges" aria-hidden="true">
            <path d="M1 0h6v1H1zM0 1h8v1H0zM0 2h1v2H0zM3 2h2v2H3zM7 2h1v2H7zM0 4h8v1H0zM1 5h2v1H1zM5 5h2v1H5zM1 6h1v1H1zM3 6h2v1H3zM6 6h1v1H6zM2 7h4v1H2z" />
          </svg>
          <span className="boss-bar__title"></span>
        </p>
        <div className="boss-bar__track">
          <div className="boss-bar__fill"></div>
        </div>
      </div>
      <div ref={hintRef} className="nuts-eaten nut-hint">
        Click to enter nut mode
      </div>
      <button ref={exitRef} className="nut-exit">
        <span className="for-keys">Press P to access pause menu, Escape to exit</span>
        <span className="for-touch">Exit</span>
      </button>
      <div ref={fpsRef} className="nut-fps" aria-hidden="true"></div>
      <div ref={touchRef} className="nut-touch">
        <div className="nut-stick" aria-hidden="true">
          <div className="nut-stick__knob"></div>
        </div>
        <button className="nut-touch__btn nut-touch__jump" aria-label="Jump">
          <svg viewBox="0 0 10 10" shapeRendering="crispEdges" aria-hidden="true">
            <path d={pixelPath(JUMP_ICON)} />
          </svg>
        </button>
        <button className="nut-touch__btn nut-touch__attack" aria-label="Pounce">
          <svg viewBox="0 0 10 10" shapeRendering="crispEdges" aria-hidden="true">
            <path d={pixelPath(SWORD_ICON)} />
          </svg>
        </button>
      </div>
      <button ref={levelRef} className="nut-sound nut-level" aria-label="Difficulty">
        Easy
      </button>
      <button ref={fullRef} className="nut-sound nut-full" aria-label="Fullscreen">
        <svg viewBox="0 0 10 10" width="15" height="15" shapeRendering="crispEdges" aria-hidden="true">
          <path className="nut-full__enter" d="M0 0h4v1H1v3H0zM6 0h4v4H9V1H6zM0 6h1v3h3v1H0zM9 6h1v4H6V9h3z" />
          <path className="nut-full__exit" d="M3 0h1v4H0V3h3zM6 0h1v3h3v1H6zM0 6h4v4H3V7H0zM6 6h4v1H7v3H6z" />
        </svg>
      </button>
      <button ref={cameraRef} className="nut-sound nut-camera" aria-label="First person view" aria-pressed="false">
        <svg viewBox="0 0 10 10" width="15" height="15" shapeRendering="crispEdges" aria-hidden="true">
          <path d={pixelPath(EYE_ICON)} />
        </svg>
      </button>
      <button ref={gearRef} className="nut-sound nut-gear" aria-label="Graphics settings">
        <svg viewBox="0 0 10 10" width="15" height="15" shapeRendering="crispEdges" aria-hidden="true">
          <path d={pixelPath(GEAR_ICON)} />
        </svg>
      </button>
      <div ref={settingsRef} className="squirrel-prompt centered nut-settings" role="dialog" aria-label="Graphics settings">
        <p className="squirrel-prompt__title">Graphics</p>
        <div className="nut-settings__row">
          <span className="nut-settings__name">Preset</span>
          <div className="nut-settings__choices">
            {PRESETS.map((level) => (
              <button key={level} type="button" data-key="preset" data-level={level}>
                {LEVEL_NAMES[level]}
              </button>
            ))}
          </div>
        </div>
        {Object.keys(GRAPHICS).map((key) => (
          <div key={key} className="nut-settings__row">
            <span className="nut-settings__name">{GRAPHICS[key].name}</span>
            <div className="nut-settings__choices">
              {Object.keys(GRAPHICS[key].levels).map((level) => (
                <button key={level} type="button" data-key={key} data-level={level}>
                  {LEVEL_NAMES[level]}
                </button>
              ))}
            </div>
          </div>
        ))}
        <div className="nut-settings__row">
          <span className="nut-settings__name">Camera</span>
          <div className="nut-settings__choices">
            <button type="button" data-key="camera" data-level="third">
              Third person
            </button>
            <button type="button" data-key="camera" data-level="first">
              First person
            </button>
          </div>
        </div>
        <div className="nut-settings__row">
          <span className="nut-settings__name">FPS counter</span>
          <div className="nut-settings__choices">
            <button type="button" data-key="fps" data-level="off">
              Off
            </button>
            <button type="button" data-key="fps" data-level="on">
              On
            </button>
          </div>
        </div>
        <p className="nut-settings__note">Ultra draw distance and foliage are for powerful computers</p>
        <button type="button" className="nut-settings__done">
          Done
        </button>
      </div>
      <button ref={shopRef} className="nut-sound nut-shop" aria-label="Nut store">
        <svg viewBox="0 0 10 10" width="15" height="15" shapeRendering="crispEdges" aria-hidden="true">
          <path d={pixelPath(SHOP_ICON)} />
        </svg>
      </button>
      <button ref={bagRef} className="nut-sound nut-bag" aria-label="Power-ups">
        <svg viewBox="0 0 10 10" width="15" height="15" shapeRendering="crispEdges" aria-hidden="true">
          <path d={pixelPath(BAG_ICON)} />
        </svg>
      </button>
      <div ref={storePanelRef} className="squirrel-prompt centered nut-settings nut-store" data-page="list" data-tab="char" role="dialog" aria-label="Nut store">
        <div className="nut-store__page nut-store__page--list">
          <p className="squirrel-prompt__title">Nut store</p>
          <p className="nut-store__bank">0 nuts to spend</p>
          <div className="nut-store__tabs" role="tablist">
            <button type="button" role="tab" data-tab="char">
              Character
            </button>
            <button type="button" role="tab" data-tab="skin">
              Skins
            </button>
            <button type="button" role="tab" data-tab="power">
              Power-ups
            </button>
          </div>
          <div className="nut-store__grid nut-store__section" data-section="char">
            {CHARACTERS.map((ch) => (
              <button key={ch.id} type="button" className="nut-store__item" data-char={ch.id}>
                <canvas className="nut-store__thumb" data-thumb={`char:${ch.id}`} aria-hidden="true" />
                <span className="nut-store__name">{ch.name}</span>
                <span className="nut-store__price"></span>
              </button>
            ))}
          </div>
          <div className="nut-store__grid nut-store__section" data-section="skin">
            {SKINS.map((skin) => (
              <button key={skin.id} type="button" className="nut-store__item" data-skin={skin.id}>
                <canvas className="nut-store__thumb" data-thumb={`skin:${skin.id}`} aria-hidden="true" />
                <span className="nut-store__name">{skin.name}</span>
                <span className="nut-store__price"></span>
              </button>
            ))}
          </div>
          <div className="nut-store__grid nut-store__section" data-section="power">
            {SHOP_ITEMS.map((item) => (
              <button key={item.id} type="button" className="nut-store__item" data-item={item.id}>
                <canvas className="nut-store__thumb" data-thumb={`item:${item.id}`} aria-hidden="true" />
                <span className="nut-store__name">{item.name}</span>
                <span className="nut-store__price"></span>
              </button>
            ))}
          </div>
          <p className="nut-settings__note">
            <span className="for-keys">Hover over anything to see it up close; click to buy or wear it. </span>
            <span className="for-touch">Tap anything to see it up close. </span>
            Eat nuts in nut mode to earn more. Skins fit whichever character you play as. Power-ups: one of each at a time, and super nuts can't be used in boss fights
          </p>
          <button type="button" className="nut-settings__done">
            Done
          </button>
        </div>
        <div className="nut-store__page nut-store__page--detail">
          <button type="button" className="nut-store__back">
            &lt; Store
          </button>
          <canvas className="nut-store__view" aria-label="Drag to turn it" />
          <p className="nut-store__title"></p>
          <p className="nut-store__desc"></p>
          <button type="button" className="nut-store__act"></button>
        </div>
      </div>
      <div ref={storePopRef} className="nut-store__pop" aria-hidden="true">
        <canvas className="nut-store__view" />
        <p className="nut-store__title"></p>
        <p className="nut-store__desc"></p>
        <p className="nut-store__status"></p>
      </div>
      <div ref={bagPanelRef} className="squirrel-prompt centered nut-settings nut-store nut-bag-panel" role="dialog" aria-label="Power-ups">
        <p className="squirrel-prompt__title">Power-ups</p>
        <div className="nut-store__grid">
          {SHOP_ITEMS.map((item) => (
            <button key={item.id} type="button" className="nut-store__item" data-use={item.id}>
              <canvas className="nut-store__thumb" data-thumb={`item:${item.id}`} aria-hidden="true" />
              <span className="nut-store__name">
                {item.name}
                <span className="for-keys"> ({item.key})</span>
              </span>
              <span className="nut-store__price"></span>
            </button>
          ))}
        </div>
        <button type="button" className="nut-store__open">
          Nut store
        </button>
        <button type="button" className="nut-settings__done">
          Done
        </button>
      </div>
      <button ref={devRef} className="nut-sound nut-dev" aria-label="Dev menu">
        <svg viewBox="0 0 10 10" width="15" height="15" shapeRendering="crispEdges" aria-hidden="true">
          <path d={pixelPath(DEV_ICON)} />
        </svg>
      </button>
      <div ref={devPanelRef} className="squirrel-prompt centered nut-settings nut-dev-panel" role="dialog" aria-label="Dev menu">
        <p className="squirrel-prompt__title">Dev</p>
        <div className="nut-settings__row">
          <span className="nut-settings__name">Biome</span>
          <div className="nut-settings__choices nut-dev__wrap">
            {BIOME_KINDS.map((kind) => (
              <button key={kind} type="button" data-dev="biome" data-value={kind}>
                {BIOMES[kind].name}
              </button>
            ))}
          </div>
        </div>
        <div className="nut-settings__row">
          <span className="nut-settings__name">Weather</span>
          <div className="nut-settings__choices nut-dev__wrap">
            {['clear', 'rain', 'storm', 'blizzard'].map((w) => (
              <button key={w} type="button" data-dev="weather" data-value={w}>
                {w}
              </button>
            ))}
          </div>
        </div>
        <div className="nut-settings__row">
          <span className="nut-settings__name">Season</span>
          <div className="nut-settings__choices nut-dev__wrap">
            {['autumn', 'winter', 'spring', 'summer'].map((w) => (
              <button key={w} type="button" data-dev="season" data-value={w}>
                {w}
              </button>
            ))}
          </div>
        </div>
        <div className="nut-settings__row">
          <span className="nut-settings__name">Time of day</span>
          <div className="nut-settings__choices nut-dev__wrap">
            <input type="range" min="0" max="1" step="0.005" data-dev="time" aria-label="Time of day" />
            <span className="nut-dev__clock">06:00</span>
            <button type="button" data-dev="freeze">
              Freeze
            </button>
          </div>
        </div>
        <div className="nut-settings__row">
          <span className="nut-settings__name">Store nuts</span>
          <div className="nut-settings__choices nut-dev__wrap">
            <input type="number" min="0" data-dev="bank" aria-label="Store nuts" />
            <button type="button" data-dev="setBank">
              Set
            </button>
            <button type="button" data-dev="addBank">
              +100
            </button>
          </div>
        </div>
        <div className="nut-settings__row">
          <span className="nut-settings__name">Score</span>
          <div className="nut-settings__choices nut-dev__wrap">
            <input type="number" min="0" data-dev="score" aria-label="Score" />
            <button type="button" data-dev="setScore">
              Set
            </button>
          </div>
        </div>
        <div className="nut-settings__row">
          <span className="nut-settings__name">Cheats</span>
          <div className="nut-settings__choices nut-dev__wrap">
            <button type="button" data-dev="god">
              God mode
            </button>
            <button type="button" data-dev="hearts">
              Full hearts
            </button>
            <button type="button" data-dev="boss">
              Boss fight
            </button>
          </div>
        </div>
        <div className="nut-settings__row">
          <span className="nut-settings__name">Power-ups</span>
          <div className="nut-settings__choices nut-dev__wrap">
            <button type="button" data-dev="power" data-value="magnet">
              Golden nut
            </button>
            <button type="button" data-dev="power" data-value="pepper">
              Pepper
            </button>
            <button type="button" data-dev="power" data-value="feather">
              Feather
            </button>
            <button type="button" data-dev="power" data-value="shield">
              Shield
            </button>
            <button type="button" data-dev="super">
              Super nut
            </button>
          </div>
        </div>
        <div className="nut-settings__row">
          <span className="nut-settings__name">Store</span>
          <div className="nut-settings__choices nut-dev__wrap">
            <button type="button" data-dev="unlock">
              Unlock everything
            </button>
            <button type="button" data-dev="resetStore">
              Reset store
            </button>
          </div>
        </div>
        <p className="nut-settings__note">Type !dev again to hide this menu</p>
        <button type="button" className="nut-settings__done">
          Done
        </button>
      </div>
      <button ref={infoRef} className="nut-sound nut-info" aria-label="How to play">
        <svg viewBox="0 0 10 10" width="15" height="15" shapeRendering="crispEdges" aria-hidden="true">
          <path fillRule="evenodd" d="M0 0h10v10H0zM1 1v8h8V1z" />
          <path d="M4 2h2v2H4zM4 5h2v3H4z" />
        </svg>
      </button>
      <button ref={pauseRef} className="nut-sound nut-pause" aria-label="Pause">
        <svg viewBox="0 0 10 10" width="15" height="15" shapeRendering="crispEdges" aria-hidden="true">
          <path className="nut-pause__bars" d="M2 1h2v8H2zM6 1h2v8H6z" />
          <path className="nut-pause__play" d="M2 1h1v8H2zM3 2h1v6H3zM4 3h1v4H4zM5 4h1v2H5z" />
        </svg>
      </button>
      <button ref={soundRef} className="nut-sound" aria-label="Sound" aria-pressed="false">
        <svg viewBox="0 0 12 10" width="18" height="15" shapeRendering="crispEdges" aria-hidden="true">
          <path className="nut-sound__speaker" d="M0 3h2l3-3h1v10H5L2 7H0z" />
          <path className="nut-sound__waves" d="M8 3h1v4H8zM10 1h1v8h-1z" />
          <path className="nut-sound__off" d="M8 3h1v1H8zM11 3h1v1h-1zM9 4h2v2H9zM8 6h1v1H8zM11 6h1v1h-1z" />
        </svg>
      </button>
      <div ref={promptRef} className="squirrel-prompt" role="status">
        <p className="squirrel-prompt__title">Nut mode</p>
        <p className="for-keys">
          <b>WASD</b> to move, hold <b>Shift</b> to run
        </p>
        <p className="for-keys">
          <b>Space</b> to jump, even onto low treetops; hold it to glide
        </p>
        <p className="for-keys">
          <b>Click</b> or <b>F</b> to pounce on creatures, mushrooms and trees
        </p>
        <p className="for-touch">
          <b>Left stick</b> to move, push it all the way to run
        </p>
        <p className="for-touch">
          <b>Arrow</b> to jump, even onto low treetops; hold it to glide
        </p>
        <p className="for-touch">
          <b>Sword</b> to pounce on creatures, mushrooms and trees
        </p>
        <p>
          Run into a tree to <b>climb</b> it
        </p>
        <p>
          Avoid <b>red mushrooms</b> and the woods' creatures, and look out for rainbow nuts
        </p>
        <p className="for-keys">
          <b>P</b> to pause, <b>R</b> to reset, <b>M</b> for sound, <b>L</b> for fullscreen, <b>C</b> for first person, <b>G</b> for graphics, <b>O</b> for FPS, <b>1</b> and <b>2</b> for bought power-ups. Difficulty and the nut store are top right
        </p>
        <p className="for-touch">Pause, sound, graphics, first person, the nut store and your power-ups are top right; difficulty is on the pause card</p>
        <p className="squirrel-prompt__dismiss for-touch">Tap to hide</p>
        <p className="squirrel-prompt__resume for-keys">Click anywhere or press Space or P to resume</p>
        <p className="squirrel-prompt__resume for-touch">Tap anywhere to resume</p>
      </div>
      <div ref={popupsRef} className="nut-pops" aria-hidden="true"></div>
      <div ref={overlayRef} className="squirrel-prompt nut-overlay" role="status">
        <p className="squirrel-prompt__title nut-overlay__title"></p>
        <p className="nut-overlay__text"></p>
        <div className="nut-overlay__levels">
          {DIFFICULTY_LEVELS.map((d) => (
            <button key={d} type="button" data-level={d}>
              {DIFFICULTIES[d].name}
            </button>
          ))}
        </div>
      </div>
      <div ref={toastRef} className="squirrel-prompt squirrel-toast" role="alert"></div>
    </>
  )
}

export default Squirrel
